import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect, memo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Lottie from 'lottie-react';
import jsmediatags from 'jsmediatags/dist/jsmediatags.min.js';
import StoryGraphViewer from './StoryGraphViewer';
import InstaApp from './InstaApp';
import { resolveLanguage, createT } from '../translations';
import { API_BASE_URL } from '../../utils/api';
import { meetsTier } from '../utils/premiumTier';
import { getCurrentTier, checkSession, logOut as authLogOut } from '../utils/authClient';
import { openExternalUrl } from '../utils/openExternal';
import { assetUrl } from '../utils/assetUrl';
import { isStandalone } from '../utils/dataProvider';
import { listSaves, putSave, deleteSavesByType } from '../utils/localSaves';
import OnboardingQuiz from '../OnboardingQuiz';
import { simulatePlaythrough } from '../utils/fakePlaythrough';

/* ── Animated emoji map (loaded once) ──────────────────────────── */

let _emojiMapCache = null;
let _emojiMapPromise = null;
const _lottieCache = {};

function loadEmojiMap() {
  if (_emojiMapCache) return Promise.resolve(_emojiMapCache);
  if (!_emojiMapPromise) {
    _emojiMapPromise = fetch(assetUrl('/lottie-emoji/_map.json'))
      .then((r) => r.json())
      .then((d) => { _emojiMapCache = d; return d; })
      .catch(() => ({}));
  }
  return _emojiMapPromise;
}

function useEmojiMap() {
  const [map, setMap] = useState(_emojiMapCache);
  useEffect(() => {
    if (_emojiMapCache) { setMap(_emojiMapCache); return; }
    loadEmojiMap().then(setMap);
  }, []);
  return map;
}

function isEmojiOnly(text) {
  if (!text) return false;
  const stripped = text.replace(/\s/g, '');
  // Match emoji sequences (including ZWJ, skin tones, variation selectors)
  const emojiRegex = /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(?:\u200D(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*$/u;
  return emojiRegex.test(stripped);
}

// Preload all lottie emoji JSONs once the map is loaded
let _lottiePreloadStarted = false;
function preloadAllLottie(emojiMap) {
  if (_lottiePreloadStarted || !emojiMap) return;
  _lottiePreloadStarted = true;
  for (const [emoji, file] of Object.entries(emojiMap)) {
    if (_lottieCache[emoji]) continue;
    fetch(assetUrl(`/lottie-emoji/${file}`))
      .then((r) => r.json())
      .then((d) => { _lottieCache[emoji] = d; })
      .catch(() => {});
  }
}

function useLottieData(emoji, emojiMap) {
  const [data, setData] = useState(_lottieCache[emoji] || null);
  useEffect(() => {
    if (!emoji || !emojiMap || !emojiMap[emoji]) return;
    if (_lottieCache[emoji]) { setData(_lottieCache[emoji]); return; }
    // Preload all on first use
    preloadAllLottie(emojiMap);
    // Wait for this specific emoji
    const check = setInterval(() => {
      if (_lottieCache[emoji]) { setData(_lottieCache[emoji]); clearInterval(check); }
    }, 50);
    return () => clearInterval(check);
  }, [emoji, emojiMap]);
  return data;
}

/**
 * Chat-Based Visual Novel — fake Android phone simulator.
 * Boot animation → Home screen → Messages / other apps.
 */

/* ── helpers ─────────────────────────────────────────────────── */

function avatarInitials(name) {
  return (name || '?').slice(0, 1).toUpperCase();
}

function avatarColor(name) {
  const colors = ['#9b6dba', '#d4678e', '#c47a5a', '#6ba088', '#7a82b5', '#c9637a', '#8e6fab', '#d4a054'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

/* ── Game time helpers ─────────────────────────────────────── */

// Shared ref so formatGameTime can be called from non-component code
const gameTimeRef = { current: { hours: 14, minutes: 0, day: 0 } };

function formatGameTimeStr(gt) {
  const h = String(gt.hours).padStart(2, '0');
  const m = String(gt.minutes).padStart(2, '0');
  return `${h}:${m}`;
}

function applyTimeskip(gt, mode, hours, minutes) {
  let newH = gt.hours;
  let newM = gt.minutes;
  let newDay = gt.day;

  if (mode === 'set') {
    const targetTotal = hours * 60 + minutes;
    const currentTotal = gt.hours * 60 + gt.minutes;
    if (targetTotal <= currentTotal) newDay += 1; // next day
    newH = hours;
    newM = minutes;
  } else {
    // skip mode — add duration
    newM += minutes;
    newH += hours;
    newH += Math.floor(newM / 60);
    newM = newM % 60;
    if (newH >= 24) {
      newDay += Math.floor(newH / 24);
      newH = newH % 24;
    }
  }

  const deltaMinutes = ((newDay - gt.day) * 24 + newH - gt.hours) * 60 + (newM - gt.minutes);

  return { hours: newH, minutes: newM, day: newDay, deltaMinutes: Math.max(0, deltaMinutes) };
}

function getTimePeriod(hour) {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

function formatTimeskipLabel(totalMinutes, toHour, t) {
  if (totalMinutes <= 0) return null;
  if (totalMinutes <= 2) return t ? t('momentLater') : 'A moment later';
  if (totalMinutes <= 4) return t ? t('fewMinLater') : 'A few minutes later';
  if (totalMinutes === 5) return t ? t('min5Later') : '5 minutes later';
  if (totalMinutes <= 9) return t ? t('fewMinLater') : 'A few minutes later';
  if (totalMinutes === 10) return t ? t('min10Later') : '10 minutes later';
  if (totalMinutes <= 14) return t ? t('aboutMin10Later') : 'About 10 minutes later';
  if (totalMinutes === 15) return t ? t('min15Later') : '15 minutes later';
  if (totalMinutes <= 19) return t ? t('aboutMin15Later') : 'About 15 minutes later';
  if (totalMinutes === 20) return t ? t('min20Later') : '20 minutes later';
  if (totalMinutes <= 24) return t ? t('aboutMin20Later') : 'About 20 minutes later';
  if (totalMinutes <= 35) return t ? t('halfHourLater') : 'Half an hour later';
  if (totalMinutes <= 50) return t ? t('aboutHourLater') : 'About an hour later';
  if (totalMinutes <= 90) return t ? t('hourLater') : 'An hour later';
  if (totalMinutes <= 150) return t ? t('coupleHoursLater') : 'A couple of hours later';
  if (totalMinutes <= 300) return t ? t('severalHoursLater') : 'Several hours later';

  // Large skips — use time-of-day label
  const period = getTimePeriod(toHour);
  if (period === 'morning') return t ? t('nextMorning') : 'Next morning';
  if (period === 'afternoon') return t ? t('thatAfternoon') : 'That afternoon';
  if (period === 'evening') return t ? t('thatEvening') : 'That evening';
  return t ? t('laterThatNight') : 'Later that night';
}

function getWeatherTemp(hour) {
  // Smooth curve: ~22 at night, peaks ~30 around 13-14h
  return Math.round(22 + 8 * Math.max(0, Math.sin((hour - 5) * Math.PI / 14)));
}

function getWeatherIcon(hour) {
  return (hour >= 6 && hour < 20) ? 'sun' : 'moon';
}

/* ── Haptic feedback ────────────────────────────────────────── */

const vibrationRef = { current: true };

function vibrateMessage() {
  if (vibrationRef.current) navigator.vibrate?.(30);
}

function vibrateTap() {
  if (vibrationRef.current) navigator.vibrate?.(14);
}

/* ── Game sounds ───────────────────────────────────────────── */

const gameSoundsRef = { current: null };
// Mirrored from the user's Sound toggle in Settings. When false, game SFX
// (chat beeps, notifications, overlay stings, etc.) are silenced. The Sexify
// music player is independent and not gated by this flag.
const soundEnabledRef = { current: true };

function playGameSound(key, { volumeScale = 1 } = {}) {
  if (!soundEnabledRef.current) return;
  const cfg = gameSoundsRef.current;
  if (!cfg) return;
  const sound = cfg[key];
  if (!sound?.url) return;
  const a = new Audio(sound.url);
  a.volume = Math.min(1, ((cfg.masterVolume ?? 100) / 100) * volumeScale);
  a.play().catch(() => {});
}

/* ── Sexify music player (persistent singleton) ───────────── */

const sexifyPlayer = {
  audio: null,
  tracks: [],
  currentIndex: -1,
  volume: 0.475,
  shuffle: false,
  repeat: 'off', // 'off' | 'all' | 'one'
  shuffleOrder: [],
  shufflePos: -1,
  _listeners: new Set(),
  _metadataCache: {},
  _metadataPending: new Set(),

  subscribe(fn) { this._listeners.add(fn); return () => this._listeners.delete(fn); },
  _notify() { this._listeners.forEach((fn) => fn(this.getState())); },

  getState() {
    return {
      playing: !!(this.audio && !this.audio.paused),
      currentIndex: this.currentIndex,
      currentTrack: this.tracks[this.currentIndex] || null,
      volume: this.volume,
      shuffle: this.shuffle,
      repeat: this.repeat,
      duration: this.audio?.duration || 0,
      currentTime: this.audio?.currentTime || 0,
      tracks: this.tracks,
      metadata: this._metadataCache,
    };
  },

  fetchMetadata(track) {
    if (!track?.url) return;
    if (this._metadataCache[track.url] || this._metadataPending.has(track.url)) return;

    // Use pre-extracted metadata from bundle if available (standalone builds)
    if (track.artist != null || track.album != null || track.coverUrl) {
      this._metadataCache[track.url] = {
        artist: track.artist || null,
        album: track.album || null,
        coverUrl: track.coverUrl || null,
      };
      this._notify();
      return;
    }

    // Fall back to jsmediatags for online mode
    this._metadataPending.add(track.url);
    try {
      jsmediatags.read(track.url, {
        onSuccess: (tag) => {
          const { artist, album, picture } = tag.tags;
          let coverUrl = null;
          if (picture) {
            const bytes = new Uint8Array(picture.data);
            const blob = new Blob([bytes], { type: picture.format });
            coverUrl = URL.createObjectURL(blob);
          }
          this._metadataCache[track.url] = { artist: artist || null, album: album || null, coverUrl };
          this._metadataPending.delete(track.url);
          this._notify();
        },
        onError: () => {
          this._metadataCache[track.url] = { artist: null, album: null, coverUrl: null };
          this._metadataPending.delete(track.url);
          this._notify();
        },
      });
    } catch (_) {
      this._metadataCache[track.url] = { artist: null, album: null, coverUrl: null };
      this._metadataPending.delete(track.url);
    }
  },

  setTracks(tracks) {
    this.tracks = tracks || [];
    this._rebuildShuffle();
    // Prefetch metadata for every track so cover/info are ready regardless of play order.
    // Standalone builds ship pre-extracted metadata (synchronous), so this is cheap.
    for (const track of this.tracks) {
      this.fetchMetadata(track);
    }
  },

  _rebuildShuffle() {
    const arr = this.tracks.map((_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    this.shuffleOrder = arr;
    this.shufflePos = this.currentIndex >= 0
      ? arr.indexOf(this.currentIndex)
      : -1;
  },

  _setupAudio() {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.addEventListener('ended', () => this._onEnded());
      this.audio.addEventListener('timeupdate', () => this._notify());
      this.audio.addEventListener('loadedmetadata', () => this._notify());
    }
  },

  play(index) {
    if (index < 0 || index >= this.tracks.length) return;
    this._setupAudio();
    this.currentIndex = index;
    this.audio.src = this.tracks[index].url;
    this.audio.volume = this.volume;
    this.audio.play().catch(() => {});
    // Ensure metadata for the now-playing track is loaded (safety net for online mode
    // where a track might be added after setTracks was called).
    this.fetchMetadata(this.tracks[index]);
    if (this.shuffle) {
      this.shufflePos = this.shuffleOrder.indexOf(index);
    }
    this._notify();
  },

  toggle() {
    if (!this.audio) return;
    if (this.audio.paused) {
      if (!this.audio.src && this.tracks.length > 0) { this.play(0); return; }
      this.audio.play().catch(() => {});
    } else {
      this.audio.pause();
    }
    this._notify();
  },

  next() {
    if (this.tracks.length === 0) return;
    // If nothing has been played yet, start from the first track
    if (this.currentIndex < 0) { this.play(0); return; }
    let nextIdx;
    if (this.shuffle) {
      this.shufflePos = (this.shufflePos + 1) % this.shuffleOrder.length;
      nextIdx = this.shuffleOrder[this.shufflePos];
    } else {
      nextIdx = (this.currentIndex + 1) % this.tracks.length;
    }
    this.play(nextIdx);
  },

  prev() {
    if (this.tracks.length === 0) return;
    // If nothing has been played yet, start from the first track
    if (this.currentIndex < 0) { this.play(0); return; }
    if (this.audio && this.audio.currentTime > 3) {
      this.audio.currentTime = 0;
      this._notify();
      return;
    }
    let prevIdx;
    if (this.shuffle) {
      this.shufflePos = (this.shufflePos - 1 + this.shuffleOrder.length) % this.shuffleOrder.length;
      prevIdx = this.shuffleOrder[this.shufflePos];
    } else {
      prevIdx = (this.currentIndex - 1 + this.tracks.length) % this.tracks.length;
    }
    this.play(prevIdx);
  },

  seek(time) {
    if (this.audio) { this.audio.currentTime = time; this._notify(); }
  },

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.audio) this.audio.volume = this.volume;
    this._notify();
  },

  toggleShuffle() {
    this.shuffle = !this.shuffle;
    if (this.shuffle) this._rebuildShuffle();
    this._notify();
  },

  cycleRepeat() {
    const modes = ['off', 'all', 'one'];
    this.repeat = modes[(modes.indexOf(this.repeat) + 1) % modes.length];
    this._notify();
  },

  _onEnded() {
    if (this.repeat === 'one') {
      this.audio.currentTime = 0;
      this.audio.play().catch(() => {});
      this._notify();
      return;
    }
    const isLast = this.shuffle
      ? this.shufflePos >= this.shuffleOrder.length - 1
      : this.currentIndex >= this.tracks.length - 1;
    if (isLast && this.repeat === 'off') {
      this._notify();
      return;
    }
    this.next();
  },
};

function useSexifyPlayer() {
  const [state, setState] = useState(sexifyPlayer.getState());
  useEffect(() => sexifyPlayer.subscribe(setState), []);
  return state;
}

/* ── Test contacts (shown when no characters from storyflow) ── */

const TEST_CONTACTS = [
  { id: '_test_alex',  name: 'Alex',  avatar: null, description: '' },
  { id: '_test_sarah', name: 'Sarah', avatar: null, description: '' },
  { id: '_test_jake',  name: 'Jake',  avatar: null, description: '' },
];

const TEST_CHAT_HISTORIES = {
  _test_alex: [
    { text: 'Hey! Are you coming to the party tonight?', isUser: false, time: '12:30' },
    { text: 'I was thinking about it', isUser: true, time: '12:31' },
    { text: 'You should! Everyone will be there', isUser: false, time: '12:31' },
    { text: 'We can grab a coffee beforehand if you want', isUser: false, time: '12:32' },
    { text: 'Sounds good, I\'ll be there around 8', isUser: true, time: '12:33' },
    { text: '🎉', isUser: false, time: '12:33' },
    { timeskipSeparator: true, label: 'An hour later', totalMinutes: 60 },
    { text: 'Oh by the way, did you finish that project?', isUser: false, time: '13:30' },
    { text: 'Almost, just a few things left', isUser: true, time: '13:31' },
    { text: '🔥', isUser: false, time: '13:31' },
    { text: 'Also, Jake said he might bring his guitar', isUser: false, time: '13:32' },
    { text: 'Oh that\'s awesome, haven\'t heard him play in a while', isUser: true, time: '13:33' },
    { text: 'Right?? It\'s gonna be great', isUser: false, time: '13:33' },
    { text: 'Here, let me share his contact', isUser: false, time: '13:34' },
    { introduce: true, introduceId: '_test_jake', introduceName: 'Jake', introduceAvatar: null, isUser: false },
    { text: 'Check out where we\'re going btw', isUser: false, time: '13:35' },
    { image: assetUrl('/game-icons/background.png'), isUser: false },
    { image: assetUrl('/game-icons/background.png'), isUser: true },
    { text: '😍', isUser: true, time: '13:36' },
    { timeskipSeparator: true, label: 'Half an hour later', totalMinutes: 30 },
    { text: 'Anyway, have you talked to Sarah recently? She\'s been acting kind of strange lately...', isUser: false, time: '14:00' },
  ],
  _test_sarah: [
    { text: 'Hi', isUser: false, time: '13:00' },
    { text: 'Hey', isUser: true, time: '13:01' },
    { text: 'Can we talk?', isUser: false, time: '13:01' },
    { text: 'Sure, what\'s up?', isUser: true, time: '13:02' },
    { text: 'I don\'t really know how to say this', isUser: false, time: '13:02' },
    { text: '😢', isUser: false, time: '13:03' },
    { text: 'You\'re scaring me a little', isUser: true, time: '13:04' },
    { timeskipSeparator: true, label: 'A few minutes later', totalMinutes: 7 },
    { text: 'Sorry, I don\'t mean to', isUser: false, time: '13:11' },
    { text: 'It\'s just... things have been complicated', isUser: false, time: '13:12' },
    { text: '😔', isUser: false, time: '13:12' },
    { text: 'I need to tell you something important. But not here, not like this.', isUser: false, time: '13:13' },
  ],
  _test_jake: [],
};

const TEST_CHOICES = {
  _test_alex: [
    'What do you mean, strange?',
    'I haven\'t noticed anything unusual. Maybe she\'s just been busy with work or something like that',
    '🤔',
  ],
  _test_sarah: [
    'Okay, where do you want to meet?',
    'You\'re scaring me. Just tell me what\'s going on',
  ],
};

const TEST_NOTIFICATION_MESSAGES = [
  { contactId: '_test_sarah', text: 'Are you there?' },
  { contactId: '_test_alex', text: 'Dude check this out' },
  { type: 'status', contactId: '_test_alex', emoji: '🤠' },
  // 5 minute skip — Sarah will show "5 minutes later", Alex accumulates 5
  { type: 'timeskip', timeskipMode: 'skip', hours: 0, minutes: 5 },
  { contactId: '_test_sarah', text: 'I really need to talk to you...' },
  { type: 'introduce', contactId: '_test_alex', introduceId: '_test_jake', introduceName: 'Jake' },
  { contactId: '_test_jake', text: 'Hey! Alex gave me your number' },
  { type: 'status', contactId: '_test_sarah', emoji: '😢' },
  // 10 minute skip — Alex accumulates 5+10=15, Sarah 10, Jake 10
  { type: 'timeskip', timeskipMode: 'skip', hours: 0, minutes: 10 },
  { contactId: '_test_alex', text: 'The party is getting crazy!' },
  { contactId: '_test_sarah', text: 'Please don\'t ignore me' },
  { type: 'status', contactId: '_test_alex', emoji: '🎸' },
  { contactId: '_test_jake', text: 'Bringing the guitar tonight 🎶' },
  // Big jump to evening — set 20:00
  { type: 'timeskip', timeskipMode: 'set', hours: 20, minutes: 0 },
  { contactId: '_test_alex', text: 'Sarah is acting weird again' },
  { type: 'status', contactId: '_test_sarah', emoji: '😔' },
  { contactId: '_test_sarah', text: 'I saw something I shouldn\'t have' },
  { contactId: '_test_alex', text: 'You won\'t believe what just happened' },
  // 30 minute skip in the evening
  { type: 'timeskip', timeskipMode: 'skip', hours: 0, minutes: 30 },
  { contactId: '_test_sarah', text: 'We need to meet. Tonight.' },
  { type: 'status', contactId: '_test_jake', emoji: '🎵' },
  { contactId: '_test_alex', text: 'Bro where are you??' },
];

/* ── Test video storyflow (video node sequence for test mode) ── */

const TEST_VIDEO_URL = 'https://r2.nikhub.io/storage/team-1775507029533-qq1t26vw/Video/1767977497310__unity_replicate-prediction-c7d39kvq01rmw0cvk8yb8c6tng2.mp4';

const TEST_VIDEO_STORYFLOW = {
  graph: {
    nodes: [
      {
        id: 'tv1', type: 'sfVideo', data: {
          videoUrl: TEST_VIDEO_URL,
          text: 'Hey, look at this place...\nIsn\'t it incredible?',
          variants: ['You look amazing!', 'Where is this?', 'Show me more'],
          relationships: [2, 1, 3],
        }
      },
      {
        id: 'tv2', type: 'sfVideo', data: {
          videoUrl: TEST_VIDEO_URL,
          text: 'I discovered this spot yesterday\nThought you\'d want to see it',
        }
      },
      {
        id: 'tv3', type: 'sfVideo', data: {
          videoUrl: TEST_VIDEO_URL,
          variants: ['That\'s beautiful', 'Can we go there together?'],
          relationships: [1, 2],
        }
      },
      {
        id: 'tv4', type: 'sfVideo', data: {
          videoUrl: TEST_VIDEO_URL,
        }
      },
      {
        id: 'tv_end', type: 'sfDialogue', data: {
          speakerName: 'Sarah',
          speakerId: '_test_sarah',
          text: 'That was fun, right?\nWe should do this again sometime',
        }
      },
    ],
    edges: [
      { id: 'te1-2a', source: 'tv1', target: 'tv2', sourceHandle: 'choice-0' },
      { id: 'te1-2b', source: 'tv1', target: 'tv2', sourceHandle: 'choice-1' },
      { id: 'te1-2c', source: 'tv1', target: 'tv2', sourceHandle: 'choice-2' },
      { id: 'te2-3', source: 'tv2', target: 'tv3', sourceHandle: 'flow-out' },
      { id: 'te3-4a', source: 'tv3', target: 'tv4', sourceHandle: 'choice-0' },
      { id: 'te3-4b', source: 'tv3', target: 'tv4', sourceHandle: 'choice-1' },
      { id: 'te4-end', source: 'tv4', target: 'tv_end', sourceHandle: 'flow-out' },
    ],
  }
};

/* ── SVG Icons (inline) ─────────────────────────────────────── */

const Icons = {
  back: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path d="M15 19l-7-7 7-7" />
    </svg>
  ),
  signal: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3">
      <rect x="1" y="14" width="4" height="8" rx="1" />
      <rect x="7" y="10" width="4" height="12" rx="1" />
      <rect x="13" y="6" width="4" height="16" rx="1" />
      <rect x="19" y="2" width="4" height="20" rx="1" />
    </svg>
  ),
  wifi: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3 w-3">
      <path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0" />
      <circle cx="12" cy="20" r="1" fill="currentColor" />
    </svg>
  ),
  battery: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
      <rect x="2" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <rect x="4" y="8" width="14" height="8" rx="1" />
      <rect x="20" y="9" width="2" height="6" rx="0.5" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5 text-yellow-400">
      <circle cx="12" cy="12" r="5" fill="currentColor" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  moon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5 text-blue-300">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="currentColor" />
    </svg>
  ),
  messages: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  gallery: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  sexify: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
      <path d="M2 12h2l2-5 3 10 3-7 2 4h2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 8v8M21 10v4" strokeLinecap="round" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  exit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
  gitBranch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 01-9 9" />
    </svg>
  ),
};

/* ── Boot animation ─────────────────────────────────────────── */

function PhoneBootScreen({ onComplete }) {
  const [bgReady, setBgReady] = useState(false);
  const animDone = useRef(false);

  // Preload background image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgReady(true);
    img.onerror = () => setBgReady(true); // proceed even if fails
    img.src = assetUrl('/game-icons/background.png');
  }, []);

  const handleAnimDone = useCallback(() => {
    animDone.current = true;
    if (bgReady) onComplete();
  }, [bgReady, onComplete]);

  // If bg loaded after anim finished, complete now
  useEffect(() => {
    if (bgReady && animDone.current) onComplete();
  }, [bgReady, onComplete]);

  return (
    <div className="absolute inset-0 z-40" style={{ backgroundColor: '#0e0a12' }}>
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-8">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          className="text-center"
        >
          <img
            src={assetUrl('/game-icons/game_logo_800x500.webp')}
            alt="Sexting Department"
            className="w-64 h-auto"
          />
        </motion.div>

        {/* Spinner */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="relative"
        >
          <motion.div
            className="h-6 w-6 rounded-full border-2 border-white/10 border-t-white/50"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
      </div>

      {/* Fade-out overlay */}
      <motion.div
        className="absolute inset-0 z-20"
        style={{ backgroundColor: '#0e0a12' }}
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.5, delay: 1.4, ease: 'easeOut' }}
        onAnimationComplete={handleAnimDone}
      />
    </div>
  );
}

/* ── Phone status bar (FIXED, always on top) ─────────────────── */

function PhoneStatusBar({ gameTime }) {
  const timeStr = gameTime ? formatGameTimeStr(gameTime) : '14:00';

  return (
    <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-1 text-[11px] font-medium text-white/90 bg-black/30 backdrop-blur-sm">
      <span>{timeStr}</span>
      <div className="flex items-center gap-1.5">
        {Icons.signal}
        {Icons.wifi}
        {Icons.battery}
      </div>
    </div>
  );
}

/* ── Home screen app icon (PNG-based) ────────────────────────── */

function HomeAppIcon({ src, label, color, scale = 1, onClick, index = 0 }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-0.5"
      initial={{ opacity: 0, scale: 0.6, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      whileTap={{ scale: 0.9 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div
        className="flex h-14 w-14 items-center justify-center"
        style={{ filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.5))' }}
      >
        {color ? (
          <div
            style={{
              width: `${scale * 100}%`,
              height: `${scale * 100}%`,
              backgroundColor: color,
              WebkitMaskImage: `url(${src})`,
              maskImage: `url(${src})`,
              WebkitMaskSize: 'cover',
              maskSize: 'cover',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
        ) : (
          <img
            src={src}
            alt={label}
            className="object-contain"
            style={{
              width: `${scale * 100}%`,
              height: `${scale * 100}%`,
            }}
          />
        )}
      </div>
      <span className="text-[10px] text-white font-medium drop-shadow-sm">{label}</span>
    </motion.button>
  );
}

/* ── Dock icon (bottom bar) ──────────────────────────────────── */

function DockIcon({ icon, label, color, badge, onClick, index = 0 }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-0.5"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.9 }}
      transition={{ duration: 0.35, delay: 0.15 + index * 0.07, ease: [0.34, 1.56, 0.64, 1] }}
    >
      <div
        className="relative flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-lg [&>svg]:h-7 [&>svg]:w-7"
        style={{ backgroundColor: color }}
      >
        {icon}
        {badge && <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 border-2" style={{ borderColor: color }} />}
      </div>
      <span className="text-[10px] text-white/70 font-medium">{label}</span>
    </motion.button>
  );
}

/* ── Home screen ────────────────────────────────────────────── */

function AndroidHomeScreen({ onNavigate, onExit, hasUnread, musicPlaying, gameTime, showClock, t, wallpaper, instaAppName }) {
  const timeStr = gameTime ? formatGameTimeStr(gameTime) : '14:00';
  const weatherIcon = gameTime ? getWeatherIcon(gameTime.hours) : 'sun';
  const weatherTemp = gameTime ? getWeatherTemp(gameTime.hours) : 30;

  const apps = [
    { id: 'patreon',    src: assetUrl('/game-icons/Patreon.png'),              label: 'Patreon',    color: '#020202', scale: 0.75 },
    { id: 'itch',       src: assetUrl('/game-icons/Itch.png'),                 label: 'Itch',       color: '#f15c3e', scale: 0.75 },
    { id: 'episodes',   src: assetUrl('/game-icons/Book Filled.png'),          label: t('episodes'),   color: null,      scale: 0.92 },
    { id: 'saves',      src: assetUrl('/game-icons/Folder Filled.png'),        label: t('saves'),      color: null,      scale: 0.92 },
    ...(instaAppName ? [{ id: 'insta', src: assetUrl('/game-icons/social-media.png'), label: instaAppName, color: null, scale: 0.86 }] : []),
    { id: 'statistics', src: assetUrl('/game-icons/Pie Chart (Filled).png'),   label: t('statistics'), color: null,      scale: 0.92 },
    { id: 'settings',   src: assetUrl('/game-icons/Settings Filled.png'),      label: t('settings'),   color: null,      scale: 0.92 },
  ];

  return (
    <div className="relative flex h-full flex-col">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={wallpaper?.imageUrl || assetUrl('/game-icons/background.png')}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Content (below fixed status bar) */}
      <div className="relative z-10 flex flex-col h-full pt-6">

        {/* Main area: left (clock + weather), right (app grid) */}
        <div className="flex-1 flex px-4 pt-4">
          {/* Left column: clock + weather */}
          {showClock !== false && (
            <div className="flex-1 flex flex-col items-start gap-3 pt-2 pr-3">
              <div className="text-4xl font-extralight text-white/90 tabular-nums leading-none tracking-wider"
                style={{ textShadow: '0 2px 16px rgba(255,255,255,0.15), 0 0 40px rgba(139,92,246,0.2)' }}>
                {timeStr}
              </div>
              <div className="flex items-center gap-1.5">
                {weatherIcon === 'moon' ? Icons.moon : Icons.sun}
                <span className="text-sm text-white/90 font-medium drop-shadow">{weatherTemp}°C</span>
              </div>
            </div>
          )}
          {showClock === false && <div className="flex-1" />}

          {/* Right column: 2-per-row app grid, fixed width anchored to right */}
          <div className="w-[52%] shrink-0 grid grid-cols-2 gap-x-3 gap-y-4 justify-items-center content-start pt-1">
            {apps.map((app, i) => {
              const isLast = i === apps.length - 1;
              const needSpacer = isLast && apps.length % 2 === 1;
              return (
                <React.Fragment key={app.id}>
                  {needSpacer && <div />}
                  <HomeAppIcon
                    src={app.src}
                    label={app.label}
                    color={app.color}
                    scale={app.scale}
                    onClick={() => onNavigate(app.id)}
                    index={i}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Bottom dock */}
        <div className="shrink-0 pb-4 px-6">
          <div className="flex items-center justify-around bg-black/25 backdrop-blur-sm rounded-2xl pt-2.5 pb-1.5 px-3">
            <DockIcon icon={Icons.messages} label={t('messages')} color="#8b5099" badge={hasUnread} onClick={() => onNavigate('messages')} index={0} />
            <DockIcon icon={Icons.gallery}  label={t('gallery')}  color="#c4567a" onClick={() => onNavigate('gallery')} index={1} />
            <DockIcon icon={Icons.sexify}   label="Sexify"   color="#1DB954" badge={musicPlaying} onClick={() => onNavigate('sexify')} index={2} />
            <DockIcon icon={Icons.exit}     label={t('exit')}     color="#ef4444" onClick={onExit} index={3} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Placeholder app screen ─────────────────────────────────── */

function PlaceholderScreen({ appId, onBack, t }) {
  const appInfo = {
    patreon:    { name: 'Patreon',    src: assetUrl('/game-icons/Patreon.png'),            iconColor: '#3161ff', header: '#1c2340', bg: '#151a30' },
    itch:       { name: 'Itch',       src: assetUrl('/game-icons/Itch.png'),               iconColor: '#f15c3e', header: '#3a2220', bg: '#261917' },
    episodes:   { name: 'Episodes',   src: assetUrl('/game-icons/Book Filled.png'),        iconColor: null,      header: '#2d2440', bg: '#1c1630' },
    saves:      { name: 'Saves',      src: assetUrl('/game-icons/Folder Filled.png'),      iconColor: null,      header: '#382a1e', bg: '#28201a' },
    statistics: { name: 'Statistics', src: assetUrl('/game-icons/Pie Chart (Filled).png'), iconColor: null,      header: '#1f2d45', bg: '#162038' },
    settings:   { name: 'Settings',   src: assetUrl('/game-icons/Settings Filled.png'),    iconColor: null,      header: '#2a3035', bg: '#1c2126' },
    gallery:    { name: 'Gallery',    src: null,                                  iconColor: null,      header: '#3a1840', bg: '#1e0e28' },
  };

  const info = appInfo[appId] || { name: appId, src: null, iconColor: null, header: null, bg: null };
  const headerBg = info.header || '#282c38';
  const screenBg = info.bg || '#1a1d26';

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: `linear-gradient(180deg, ${headerBg} 0%, ${screenBg} 40%, ${screenBg} 100%)` }}>
      <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: headerBg }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">
          {Icons.back}
        </button>
        <span className="text-sm font-medium tracking-wide text-white/90">{info.name}</span>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        {info.src ? (
          <div className="h-20 w-20 rounded-3xl overflow-hidden">
            {info.iconColor ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: info.iconColor,
                  WebkitMaskImage: `url(${info.src})`,
                  maskImage: `url(${info.src})`,
                  WebkitMaskSize: 'cover',
                  maskSize: 'cover',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
              />
            ) : (
              <img src={info.src} alt={info.name} className="h-full w-full object-cover" />
            )}
          </div>
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/5 text-white/30">
            {Icons.gallery}
          </div>
        )}
        <span className="text-sm text-white/40">{t('comingSoonTitle')}</span>
      </div>
    </div>
  );
}

/* ── Shared UI: toggle row, dropdown, modal ──────────────────── */

const SettingsIcons = {
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  ),
  volume: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
    </svg>
  ),
  vibrate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <rect x="8" y="4" width="8" height="16" rx="2" />
      <path d="M4 10v4M20 10v4M1 11v2M23 11v2" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <path d="M12 2a10 10 0 00-6.88 17.23l.9-.92A8.5 8.5 0 1120.5 12H22A10 10 0 0012 2z" />
      <path d="M12 12l4.5-4.5" strokeLinecap="round" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  gitBranch: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 01-9 9" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-[18px] w-[18px] shrink-0">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
};

function SettingsRow({ label, icon, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/60 flex items-center gap-2">
        {icon && <span className="text-white/40">{icon}</span>}
        {label}
      </span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function ToggleChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${active ? 'bg-white/90 text-[#1c2126]' : 'bg-white/[0.08] text-white/50 hover:bg-white/[0.12]'}`}
    >
      {label}
    </button>
  );
}

function ToggleSwitch({ on, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="relative rounded-full transition-colors"
      style={{ width: 44, height: 24, backgroundColor: on ? 'rgba(139,205,160,0.8)' : 'rgba(255,255,255,0.15)' }}
    >
      <div
        className="absolute rounded-full bg-white shadow transition-all"
        style={{ width: 18, height: 18, top: 3, left: on ? 23 : 3 }}
      />
    </button>
  );
}

function Dropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const label = options.find((o) => o.value === value)?.label || value;

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1.5 bg-white/[0.08] hover:bg-white/[0.12] rounded-lg px-3 py-1.5 text-xs text-white/80 transition-colors">
        {label}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 rounded-lg overflow-hidden shadow-xl border border-white/10" style={{ backgroundColor: '#2a2832' }}>
          {options.map((o) => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
              className={`block w-full text-left px-4 py-2 text-xs transition-colors ${value === o.value ? 'text-white bg-white/10' : 'text-white/60 hover:bg-white/5'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GameModal({ title, onClose, children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl p-5 shadow-2xl" style={{ backgroundColor: '#2a2832' }}>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-white">{title}</span>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

/* ── Settings screen ─────────────────────────────────────────── */

function SettingsScreen({ onBack, sound, onSoundChange, vibration, onVibrationChange, showClock, onShowClockChange, language, onLanguageChange, speed, onSpeedChange, t }) {
  const langs = [{ value: 'auto', label: t('langAuto') }, { value: 'en', label: t('langEn') }, { value: 'ru', label: t('langRu') }];

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #252330 0%, #1a1c22 50%, #1c1e24 100%)' }}>
      <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#2a2832' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
        <span className="text-sm font-medium tracking-wide text-white/90">{t('settings')}</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-none px-4 pt-4 space-y-3">
        {/* Language */}
        <div className="rounded-xl bg-white/[0.03] px-4 py-3.5">
          <SettingsRow label={t('language')} icon={SettingsIcons.globe}><Dropdown value={language} options={langs} onChange={onLanguageChange} /></SettingsRow>
        </div>
        {/* Audio */}
        <div className="rounded-xl bg-white/[0.03] px-4 divide-y divide-white/5">
          <div className="py-3.5"><SettingsRow label={t('sound')} icon={SettingsIcons.volume}><ToggleSwitch on={sound} onToggle={() => onSoundChange((s) => !s)} /></SettingsRow></div>
          <div className="py-3.5"><SettingsRow label={t('vibration')} icon={SettingsIcons.vibrate}><ToggleSwitch on={vibration} onToggle={() => { vibrateTap(); onVibrationChange((v) => !v); }} /></SettingsRow></div>
        </div>
        {/* Display */}
        <div className="rounded-xl bg-white/[0.03] px-4 py-3.5">
          <SettingsRow label={t('showClock')} icon={SettingsIcons.clock}><ToggleSwitch on={showClock} onToggle={() => onShowClockChange((v) => !v)} /></SettingsRow>
        </div>
        {/* Speed */}
        <div className="rounded-xl bg-white/[0.03] px-4 py-3.5">
          <SettingsRow label={t('messageSpeed')} icon={SettingsIcons.gauge}>
            <div className="flex gap-1.5">
              {['1x', '2x', '3x'].map((v) => <ToggleChip key={v} label={v} active={speed === v} onClick={() => onSpeedChange(v)} />)}
            </div>
          </SettingsRow>
        </div>
        {/* Version */}
        <div className="rounded-xl bg-white/[0.03] px-4 py-3.5">
          <SettingsRow label={t('version')} icon={SettingsIcons.gitBranch}>
            <span className="text-xs text-white/25">{process.env.REACT_APP_GAME_VERSION || '0.0.0'}</span>
          </SettingsRow>
        </div>
      </div>
    </div>
  );
}

/* ── Saves screen ────────────────────────────────────────────── */

function formatPlayTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function SavesScreen({ onBack, teamId, gameState, onLoad, onReset, onRestoreProgress, canRestoreProgress, playTimeSeconds, gameTimeStr, currentChapter, currentChapterNumber, t }) {
  const TOTAL_SLOTS = 20;
  const [saves, setSaves] = useState([]); // saves from API
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // slot number being saved
  const [renameSlot, setRenameSlot] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmLoad, setConfirmLoad] = useState(null); // save object to confirm loading

  const token = sessionStorage.getItem('flowcore_session_token') || '';

  // Fetch saves on mount
  useEffect(() => {
    if (!teamId) { setLoading(false); return; }
    (async () => {
      try {
        if (isStandalone()) {
          setSaves(listSaves(teamId));
        } else {
          const res = await fetch(`${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setSaves(data.saves || []);
          }
        }
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [teamId, token]);

  // Build slot map: slot number → save data
  const slotMap = useMemo(() => {
    const map = {};
    saves.filter((s) => s.saveType === 'save_slot').forEach((s) => { map[s.slot] = s; });
    return map;
  }, [saves]);

  const currentProgress = useMemo(() => saves.find((s) => s.saveType === 'current_progress'), [saves]);

  const handleSave = useCallback(async (slotNum) => {
    if (!teamId || !gameState || saving) return;
    setSaving(slotNum);
    try {
      const saveData = {
        teamId,
        slot: slotNum,
        saveType: 'save_slot',
        label: slotMap[slotNum]?.label || `Save ${slotNum}`,
        gameTemplate: 'ChatVisualNovel',
        state: gameState,
        currentStoryflowId: gameState?.currentStoryflowId || '',
        currentNodeId: gameState?.currentNodeId || '',
        currentChapter: currentChapter || '',
        currentChapterNumber: currentChapterNumber ?? 0,
        playTime: playTimeSeconds || 0,
        gameTime: gameTimeStr || ''
      };
      if (isStandalone()) {
        const saved = putSave(teamId, saveData);
        setSaves((prev) => {
          const without = prev.filter((s) => !(s.saveType === 'save_slot' && s.slot === slotNum));
          return [...without, { ...saved, saveType: 'save_slot' }];
        });
      } else {
        const res = await fetch(`${API_BASE_URL}/api/game-saves`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(saveData)
        });
        if (res.ok) {
          const saved = await res.json();
          setSaves((prev) => {
            const without = prev.filter((s) => !(s.saveType === 'save_slot' && s.slot === slotNum));
            return [...without, { ...saved, saveType: 'save_slot' }];
          });
        }
      }
    } catch (e) { /* ignore */ }
    setSaving(null);
  }, [teamId, gameState, token, slotMap, saving, currentChapter, currentChapterNumber, playTimeSeconds, gameTimeStr]);

  const handleRename = useCallback(async (slotNum, newLabel) => {
    const existing = slotMap[slotNum];
    if (!existing || !teamId) return;
    try {
      if (isStandalone()) {
        putSave(teamId, { ...existing, label: newLabel });
        setSaves((prev) => prev.map((s) => s.saveType === 'save_slot' && s.slot === slotNum ? { ...s, label: newLabel } : s));
      } else {
        const res = await fetch(`${API_BASE_URL}/api/game-saves`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            teamId, slot: slotNum, saveType: 'save_slot', label: newLabel,
            gameTemplate: 'ChatVisualNovel', state: existing.state,
            currentChapter: existing.currentChapter, playTime: existing.playTime, gameTime: existing.gameTime
          })
        });
        if (res.ok) {
          setSaves((prev) => prev.map((s) => s.saveType === 'save_slot' && s.slot === slotNum ? { ...s, label: newLabel } : s));
        }
      }
    } catch (e) { /* ignore */ }
  }, [teamId, token, slotMap]);

  const handleDeleteAll = useCallback(async () => {
    if (!teamId) return;
    try {
      if (isStandalone()) {
        deleteSavesByType(teamId, 'current_progress');
        setSaves((prev) => prev.filter((s) => s.saveType !== 'current_progress'));
        onReset?.();
      } else {
        // Only delete current_progress, not save slots
        const res = await fetch(`${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}&saveType=current_progress`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setSaves((prev) => prev.filter((s) => s.saveType !== 'current_progress'));
          onReset?.();
        }
      }
    } catch (e) { /* ignore */ }
    setConfirmReset(false);
  }, [teamId, token, onReset]);

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #302218 0%, #28201a 40%, #221c16 100%)' }}>
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#382a1e' }}>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
          <span className="text-sm font-medium tracking-wide text-white/90">{t ? t('saves') : 'Saves'}</span>
        </div>
        <button type="button" onClick={() => setConfirmReset(true)}
          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-500/10 text-[11px] text-red-400/80 hover:bg-red-500/20 hover:text-red-400 transition-colors">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3 w-3"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
          {t ? t('reset') : 'Reset'}
        </button>
      </div>

      {/* Restore progress (fake-playthrough quiz) */}
      {canRestoreProgress && onRestoreProgress ? (
        <button
          type="button"
          onClick={onRestoreProgress}
          className="mx-4 mt-3 mb-1 rounded-xl px-4 py-3 flex items-center gap-3 bg-gradient-to-r from-violet-500/20 to-violet-400/10 hover:from-violet-500/30 hover:to-violet-400/15 ring-1 ring-violet-400/30 text-left transition-colors"
        >
          <div className="h-8 w-8 rounded-lg bg-violet-500/30 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4 text-violet-200">
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-violet-100">{t('savesRestore')}</div>
            <div className="text-[10px] text-violet-200/70 mt-0.5 leading-tight">{t('savesRestoreHint')}</div>
          </div>
          <svg viewBox="0 0 24 24" className="shrink-0 w-4 h-4 text-violet-200/60" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      ) : null}

      {/* Current progress */}
      <div className="mx-4 mt-3 mb-2 rounded-xl bg-white/[0.03] px-4 py-3">
        <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1.5">{t('currentProgress')}</div>
        {currentProgress ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/70">{currentProgress.currentChapter || t('inProgress')}</span>
            <span className="text-[10px] text-white/30">{formatPlayTime(currentProgress.playTime || 0)}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/70">{currentChapter || t('playing')}</span>
            <span className="text-[10px] text-white/30">{formatPlayTime(playTimeSeconds || 0)}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-xs text-white/30">{t ? t('loadingSaves') : 'Loading saves...'}</span>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto scrollbar-none">
          {Array.from({ length: TOTAL_SLOTS }, (_, i) => i + 1).map((slotNum) => {
            const save = slotMap[slotNum];
            return (
              <div key={slotNum} className="flex items-center px-4 py-2.5 border-b border-white/5">
                <span className="text-[10px] text-white/20 w-5 shrink-0 text-right mr-3">{slotNum}</span>
                {save ? (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/80 truncate">{save.label || `Save ${slotNum}`}</span>
                        <button type="button" onClick={() => { setRenameSlot(slotNum); setRenameValue(save.label || ''); }}
                          className="text-white/15 hover:text-white/40 transition-colors shrink-0">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3 w-3"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                      </div>
                      <div className="text-[10px] text-white/25 mt-0.5">{fmtDate(save.updatedAt)} · {formatPlayTime(save.playTime || 0)}</div>
                    </div>
                    <div className="flex gap-1.5 ml-2 shrink-0">
                      <button type="button" onClick={() => setConfirmLoad(save)}
                        className="px-3 py-1.5 rounded-md bg-white/[0.06] text-[11px] text-white/50 hover:bg-white/10 hover:text-white/70 transition-colors">{t ? t('load') : 'Load'}</button>
                      <button type="button" onClick={() => handleSave(slotNum)} disabled={saving === slotNum}
                        className="px-3 py-1.5 rounded-md bg-white/[0.06] text-[11px] text-white/50 hover:bg-white/10 hover:text-white/70 transition-colors disabled:opacity-30">
                        {saving === slotNum ? '...' : (t ? t('save') : 'Save')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-white/12 italic flex-1">{t('empty')}</span>
                    <button type="button" onClick={() => handleSave(slotNum)} disabled={saving === slotNum}
                      className="px-3 py-1.5 rounded-md bg-white/[0.06] text-[11px] text-white/50 hover:bg-white/10 hover:text-white/70 transition-colors ml-2 shrink-0 disabled:opacity-30">
                      {saving === slotNum ? '...' : (t ? t('save') : 'Save')}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {renameSlot !== null && (
          <GameModal title={t ? t('renameSave') : 'Rename Save'} onClose={() => setRenameSlot(null)}>
            <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25 mb-3" />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setRenameSlot(null)} className="px-4 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/70">{t ? t('cancel') : 'Cancel'}</button>
              <button type="button" onClick={() => { handleRename(renameSlot, renameValue); setRenameSlot(null); }}
                className="px-4 py-1.5 rounded-lg text-xs bg-white/10 text-white hover:bg-white/15">{t ? t('save') : 'Save'}</button>
            </div>
          </GameModal>
        )}
        {confirmLoad && (
          <GameModal title={t ? t('loadSave') : 'Load Save'} onClose={() => setConfirmLoad(null)}>
            <p className="text-xs text-white/50 mb-4">{t ? t('loadConfirm', { name: confirmLoad.label }) : `Load "${confirmLoad.label}"? Current unsaved progress will be lost.`}</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmLoad(null)} className="px-4 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/70">{t ? t('cancel') : 'Cancel'}</button>
              <button type="button" onClick={() => { onLoad?.(confirmLoad.state); setConfirmLoad(null); }}
                className="px-4 py-1.5 rounded-lg text-xs bg-white/10 text-white hover:bg-white/15">{t ? t('load') : 'Load'}</button>
            </div>
          </GameModal>
        )}
        {confirmReset && (
          <GameModal title={t ? t('resetProgress') : 'Reset Progress'} onClose={() => setConfirmReset(false)}>
            <p className="text-xs text-white/50 mb-4">{t ? t('resetConfirm') : 'Are you sure you want to reset all progress? This action cannot be undone.'}</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmReset(false)} className="px-4 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/70">{t ? t('cancel') : 'Cancel'}</button>
              <button type="button" onClick={handleDeleteAll} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3 w-3"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                {t ? t('resetAll') : 'Reset All'}
              </button>
            </div>
          </GameModal>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Statistics screen ───────────────────────────────────────── */

function StatBar({ label, value, min = -50, max = 50 }) {
  const isPositive = value >= 0;
  return (
    <div className="rounded-xl bg-white/[0.03] px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-white/80">{label}</span>
        <span className={`text-[11px] font-medium ${isPositive ? 'text-emerald-300' : 'text-red-400'}`}>{value > 0 ? '+' : ''}{value}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.08] relative overflow-hidden">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/15" />
        <motion.div
          className="absolute top-0 bottom-0 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: isPositive ? `${(value / max) * 50}%` : `${(Math.abs(value) / Math.abs(min)) * 50}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          style={{
            ...(isPositive ? { left: '50%' } : { right: '50%' }),
            background: isPositive
              ? 'linear-gradient(90deg, #34d399 0%, #6ee7b7 50%, #a7f3d0 100%)'
              : 'linear-gradient(270deg, #f87171 0%, #fb923c 100%)',
            boxShadow: isPositive
              ? '0 0 8px rgba(52, 211, 153, 0.4), 0 0 2px rgba(52, 211, 153, 0.2)'
              : '0 0 8px rgba(248, 113, 113, 0.4), 0 0 2px rgba(248, 113, 113, 0.2)',
          }}
        />
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-xs text-white/60">{label}</span>
      <span className="text-xs text-white/80 tabular-nums">{value}</span>
    </div>
  );
}

function StatisticsScreen({ onBack, t, playTimeSeconds, galleryUnlocks, totalPhotos, totalVideos, relationships, characters, unlockedContacts }) {
  const charMap = useMemo(() => {
    const m = {};
    for (const c of (characters || [])) m[c.characterId || c.id] = c.name;
    return m;
  }, [characters]);

  const unlockedPhotos = (galleryUnlocks || []).filter((g) => !g.isVideo).length;
  const unlockedVideos = (galleryUnlocks || []).filter((g) => g.isVideo).length;
  // Show all unlocked contacts with their relationship value (0 if not set)
  const relEntries = Object.keys(unlockedContacts || {}).map((charId) => [charId, (relationships || {})[charId] || 0]);

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #1e2a4a 0%, #162038 40%, #121c2e 100%)' }}>
      <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#1f2d45' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
        <span className="text-sm font-medium tracking-wide text-white/90">{t('statistics')}</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-none px-4 pt-4 pb-6 space-y-3">
        {/* Playtime */}
        <div className="rounded-xl bg-white/[0.03] px-4">
          <div className="text-[10px] text-white/30 uppercase tracking-wider pt-3 pb-1">{t('playtime')}</div>
          <div className="divide-y divide-white/5">
            <StatRow label={t('currentSave')} value={formatPlayTime(playTimeSeconds || 0)} />
          </div>
        </div>

        {/* Media */}
        <div className="rounded-xl bg-white/[0.03] px-4">
          <div className="text-[10px] text-white/30 uppercase tracking-wider pt-3 pb-1">{t('mediaUnlocked')}</div>
          <div className="divide-y divide-white/5">
            <StatRow label={t('photos')} value={`${unlockedPhotos} / ${totalPhotos || '?'}`} />
            <StatRow label={t('videos')} value={`${unlockedVideos} / ${totalVideos || '?'}`} />
          </div>
        </div>

        {/* Relationships */}
        <div>
          <div className="text-[10px] text-white/30 uppercase tracking-wider px-1 mb-2">{t('relationship')}</div>
          <div className="space-y-2">
            {relEntries.length > 0 ? relEntries.map(([charId, value]) => (
              <StatBar key={charId} label={charMap[charId] || charId} value={value} />
            )) : (
              <div className="text-xs text-white/20 px-1">—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sexify music player screen ────────────────────────────── */

const SexifyIcons = {
  play: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7"><path d="M8 5v14l11-7z" /></svg>
  ),
  pause: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
  ),
  next: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z" /></svg>
  ),
  prev: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" /></svg>
  ),
  shuffle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
      <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
    </svg>
  ),
  repeat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
      <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />
    </svg>
  ),
  repeatOne: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
      <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />
      <text x="10" y="15" fill="currentColor" stroke="none" fontSize="8" fontWeight="bold">1</text>
    </svg>
  ),
  volume: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
      <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" />
    </svg>
  ),
  musicNote: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-10 w-10 text-white/20">
      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
    </svg>
  ),
  list: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
};

function formatMusicTime(seconds) {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function trackDisplayName(track) {
  if (!track?.name) return '';
  // Strip extension
  return track.name.replace(/\.[^.]+$/, '');
}

function SexifyScreen({ onBack, t, coversUnlocked }) {
  const ps = useSexifyPlayer();
  const [showQueue, setShowQueue] = useState(false);
  const [seekDragging, setSeeking] = useState(false);
  const [seekVal, setSeekVal] = useState(0);
  const progressRef = useRef(null);

  const currentTime = seekDragging ? seekVal : ps.currentTime;
  const progress = ps.duration > 0 ? (currentTime / ps.duration) * 100 : 0;

  const handleProgressDown = useCallback((e) => {
    if (!progressRef.current || ps.duration <= 0) return;
    const el = progressRef.current;
    const rect = el.getBoundingClientRect();
    const computePct = (clientX) => Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    setSeekVal(computePct(e.clientX) * ps.duration);
    setSeeking(true);
    try { el.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }

    const handleMove = (ev) => {
      setSeekVal(computePct(ev.clientX) * ps.duration);
    };
    const handleUp = (ev) => {
      sexifyPlayer.seek(computePct(ev.clientX) * ps.duration);
      setSeeking(false);
      try { el.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
      el.removeEventListener('pointermove', handleMove);
      el.removeEventListener('pointerup', handleUp);
      el.removeEventListener('pointercancel', handleUp);
    };
    el.addEventListener('pointermove', handleMove);
    el.addEventListener('pointerup', handleUp);
    el.addEventListener('pointercancel', handleUp);
  }, [ps.duration]);

  const GREEN = '#1DB954';
  const track = ps.currentTrack || ps.tracks[0] || null;
  const displayName = trackDisplayName(track);
  const meta = (track?.url && ps.metadata?.[track.url]) || null;
  const trackUrl = track?.url;

  useEffect(() => { if (trackUrl) sexifyPlayer.fetchMetadata({ url: trackUrl }); }, [trackUrl]);

  if (ps.tracks.length === 0) {
    return (
      <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #121212 40%, #0a0a0a 100%)' }}>
        <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#1a1a1a' }}>
          <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
          <span className="text-sm font-semibold tracking-wide" style={{ color: GREEN }}>Sexify</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6">
          {SexifyIcons.musicNote}
          <span className="text-sm text-white/30 text-center">{t('noMusicYet')}</span>
        </div>
      </div>
    );
  }

  // Queue / track list view
  if (showQueue) {
    return (
      <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #121212 40%, #0a0a0a 100%)' }}>
        <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#1a1a1a' }}>
          <button type="button" onClick={() => setShowQueue(false)} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
          <span className="text-sm font-semibold tracking-wide" style={{ color: GREEN }}>{t('queue')}</span>
          <span className="text-[10px] text-white/30 ml-auto">{ps.tracks.length} {t('tracks')}</span>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-none">
          {ps.tracks.map((t, i) => {
            const isCurrent = i === ps.currentIndex;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { sexifyPlayer.play(i); setShowQueue(false); }}
                className="flex items-center w-full px-4 py-2.5 gap-3 border-b border-white/5 text-left transition-colors hover:bg-white/5"
              >
                <span className="w-6 text-right text-[11px] shrink-0" style={{ color: isCurrent ? GREEN : 'rgba(255,255,255,0.25)' }}>
                  {isCurrent && ps.playing ? (
                    <span className="inline-flex gap-[2px] items-end h-3">
                      <span className="w-[3px] bg-current rounded-sm animate-pulse" style={{ height: '60%', backgroundColor: GREEN }} />
                      <span className="w-[3px] bg-current rounded-sm animate-pulse" style={{ height: '100%', backgroundColor: GREEN, animationDelay: '0.15s' }} />
                      <span className="w-[3px] bg-current rounded-sm animate-pulse" style={{ height: '40%', backgroundColor: GREEN, animationDelay: '0.3s' }} />
                    </span>
                  ) : (i + 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs truncate" style={{ color: isCurrent ? GREEN : 'rgba(255,255,255,0.8)' }}>{trackDisplayName(t)}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Main player view
  return (
    <div className="flex h-full flex-col pt-6 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #2a2a2a 0%, #121212 50%, #0a0a0a 100%)' }}>
      {/* Blurred album art background */}
      {meta?.coverUrl && coversUnlocked && (
        <div className="absolute inset-0 z-0">
          <img src={meta.coverUrl} alt="" className="w-full h-full object-cover" style={{ filter: 'blur(60px) saturate(1.4) brightness(0.3)', transform: 'scale(1.3)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.85) 100%)' }} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 shrink-0 relative z-10">
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-white/50">{t('nowPlaying')}</span>
        <button type="button" onClick={() => setShowQueue(true)} className="p-1 text-white/70 hover:text-white transition-colors">{SexifyIcons.list}</button>
      </div>

      {/* Album art area */}
      <div className="flex-1 flex items-center justify-center px-8 relative z-10">
        <motion.div
          className="relative w-full aspect-square max-w-[260px] rounded-2xl shadow-2xl flex items-center justify-center overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${GREEN}22 0%, ${GREEN}08 100%)`, border: `1px solid ${GREEN}15` }}
          animate={ps.playing ? { scale: [1, 1.02, 1] } : { scale: 1 }}
          transition={ps.playing ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
        >
          {meta?.coverUrl ? (
            coversUnlocked ? (
              <img src={meta.coverUrl} alt="Album art" className="w-full h-full object-cover" />
            ) : (
              <>
                <img src={meta.coverUrl} alt="Album art" className="w-full h-full object-cover" style={{ filter: 'blur(6px)', transform: 'scale(1.1)' }} />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5" style={{ background: `linear-gradient(180deg, ${GREEN}25 0%, rgba(0,0,0,0.40) 50%, ${GREEN}18 100%)` }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-11 w-11 text-white/75">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <span className="text-[11px] text-white/50 text-center leading-tight px-4">{t('unlockCovers')}</span>
                </div>
              </>
            )
          ) : (
            <div className="flex flex-col items-center gap-2">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-16 w-16" style={{ color: `${GREEN}40` }}>
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
            </div>
          )}
        </motion.div>
      </div>

      {/* Track info + controls */}
      <div className="shrink-0 px-6 pb-10 relative z-10">
        {/* Track name */}
        <div className="mb-4">
          <div className="text-base font-bold text-white truncate">{displayName || '\u00A0'}</div>
          <div className="text-xs text-white/40">{[meta?.artist, meta?.album].filter(Boolean).join(' \u2014 ') || '\u00A0'}</div>
        </div>

        {/* Fake waveform visualizer */}
        <div className="flex items-end justify-center gap-[2px] h-6 mb-3" style={{ opacity: ps.playing ? 1 : 0 }}>
          {Array.from({ length: 20 }, (_, i) => (
            <motion.div
              key={i}
              className="rounded-full"
              style={{ width: 2.5, backgroundColor: GREEN, opacity: 0.6 }}
              animate={ps.playing ? { height: [4, 8 + Math.random() * 14, 4] } : { height: 4 }}
              transition={{ duration: 0.4 + Math.random() * 0.4, repeat: ps.playing ? Infinity : 0, delay: i * 0.05, ease: 'easeInOut' }}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="mb-2">
          <div
            ref={progressRef}
            className="relative h-1 rounded-full cursor-pointer group"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', touchAction: 'none' }}
            onPointerDown={handleProgressDown}
          >
            <div className="absolute left-0 top-0 h-full rounded-full transition-[width] duration-75"
              style={{ width: `${progress}%`, backgroundColor: GREEN }} />
            <div className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)`, backgroundColor: '#fff' }} />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-white/30 tabular-nums">{formatMusicTime(currentTime)}</span>
            <span className="text-[10px] text-white/30 tabular-nums">{formatMusicTime(ps.duration)}</span>
          </div>
        </div>

        {/* Main controls */}
        <div className="flex items-center justify-between">
          {/* Shuffle */}
          <button type="button" onClick={() => sexifyPlayer.toggleShuffle()}
            className="p-2 transition-colors" style={{ color: ps.shuffle ? GREEN : 'rgba(255,255,255,0.4)' }}>
            {SexifyIcons.shuffle}
          </button>
          {/* Prev */}
          <button type="button" onClick={() => sexifyPlayer.prev()}
            className="p-2 text-white/80 hover:text-white transition-colors">
            {SexifyIcons.prev}
          </button>
          {/* Play/Pause */}
          <button type="button" onClick={() => {
            if (ps.currentIndex < 0 && ps.tracks.length > 0) sexifyPlayer.play(0);
            else sexifyPlayer.toggle();
          }}
            className="flex items-center justify-center h-14 w-14 rounded-full transition-transform active:scale-90"
            style={{ backgroundColor: GREEN }}>
            {ps.playing ? SexifyIcons.pause : SexifyIcons.play}
          </button>
          {/* Next */}
          <button type="button" onClick={() => sexifyPlayer.next()}
            className="p-2 text-white/80 hover:text-white transition-colors">
            {SexifyIcons.next}
          </button>
          {/* Repeat */}
          <button type="button" onClick={() => sexifyPlayer.cycleRepeat()}
            className="p-2 transition-colors" style={{ color: ps.repeat !== 'off' ? GREEN : 'rgba(255,255,255,0.4)' }}>
            {ps.repeat === 'one' ? SexifyIcons.repeatOne : SexifyIcons.repeat}
          </button>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-white/30">{SexifyIcons.volume}</span>
          <div className="flex-1 relative h-1 rounded-full cursor-pointer group"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              sexifyPlayer.setVolume(pct);
            }}>
            <div className="absolute left-0 top-0 h-full rounded-full"
              style={{ width: `${ps.volume * 100}%`, backgroundColor: GREEN }} />
            <div className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full shadow-md"
              style={{ left: `calc(${ps.volume * 100}% - 6px)`, backgroundColor: '#fff' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Episodes screen ─────────────────────────────────────────── */

function EpisodesScreen({ onBack, allStoryflows, unlockedChapters, t }) {
  const chapters = useMemo(() => {
    return (allStoryflows || [])
      .filter((s) => s.number !== 0 && s.number !== null && s.number !== undefined)
      .map((s) => ({
        id: s.id,
        number: s.number ?? null,
        name: s.name,
        cover: s.cover?.url128 || s.cover?.url64 || null,
        unlocked: s.number === 1 || (unlockedChapters || []).includes(s.id),
      }));
  }, [allStoryflows, unlockedChapters]);

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #261e3e 0%, #1c1630 40%, #14102a 100%)' }}>
      <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#2d2440' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
        <span className="text-sm font-medium tracking-wide text-white/90">{t ? t('episodes') : 'Episodes'}</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-none">
        {chapters.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-white/30 text-sm">{t('noChaptersYet')}</div>
        ) : (
          chapters.map((ch) => (
            <div key={ch.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
              {ch.unlocked ? (
                <div className="h-12 w-12 rounded-lg overflow-hidden shrink-0 bg-white/5">
                  {ch.cover ? (
                    <img src={ch.cover} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-white/10" />
                  )}
                </div>
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/5 shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5 text-white/20">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-white/30">{ch.number != null ? `Chapter ${ch.number}` : 'Chapter'}</div>
                <div className={`text-sm ${ch.unlocked ? 'text-white/80' : 'text-white/25'}`}>{ch.name}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Patreon benefit icon (animated lottie with loop) ────────── */

function PatreonBenefitIcon({ emoji, fallback, emojiMap }) {
  const lottieData = useLottieData(emojiMap && emojiMap[emoji] ? emoji : null, emojiMap);
  if (lottieData) {
    return <Lottie animationData={lottieData} loop style={{ width: 18, height: 18 }} />;
  }
  return <span className="text-sm">{fallback}</span>;
}

/* ── Patreon screen ──────────────────────────────────────────── */

function PatreonScreen({ onBack, patreonUrl, authEndpoint, patreonTiers, playerTier, onLinkedTierChange, t }) {
  const emojiMap = useEmojiMap();
  const [linking, setLinking] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null); // 'opened' | 'linked' | 'error'
  // linkedTier mirrors the authoritative playerTier from the parent. Because
  // logOut() keeps the stale tier in localStorage (Unity parity), we MUST
  // NOT read it back from getCurrentTier() on mount — the parent's playerTier
  // is the source of truth for the current session.
  const [linkedTier, setLinkedTier] = useState(playerTier || '');
  useEffect(() => { setLinkedTier(playerTier || ''); }, [playerTier]);

  const isLinked = !!linkedTier;

  const doCheckSession = useCallback(async () => {
    if (linking || !authEndpoint) {
      if (!authEndpoint) setLinkStatus('error');
      return;
    }
    setLinking(true);
    try {
      const tierOrder = Array.isArray(patreonTiers) ? patreonTiers : [];
      // First click (not linked): if OAuth needed, checkSession opens the
      // Patreon auth URL in a new tab. Once the user finishes in browser and
      // taps again, the same call returns authenticated=true and the tier is
      // saved to localStorage.
      const result = await checkSession(authEndpoint, { openUrl: true, tierOrder });
      if (result.authenticated) {
        const nextTier = result.tier || getCurrentTier(tierOrder);
        setLinkedTier(nextTier);
        setLinkStatus('linked');
        if (onLinkedTierChange) onLinkedTierChange(nextTier);
      } else {
        setLinkStatus('opened');
      }
    } catch (_) {
      setLinkStatus('error');
    } finally {
      setLinking(false);
    }
  }, [authEndpoint, patreonTiers, linking, onLinkedTierChange]);

  const doLogout = useCallback((e) => {
    e.stopPropagation();
    authLogOut();
    setLinkedTier('');
    setLinkStatus(null);
    if (onLinkedTierChange) onLinkedTierChange('');
  }, [onLinkedTierChange]);

  const benefits = [
    { emoji: '🚀', fallback: '⚡', text: t('benefitEarlyAccess') },
    { emoji: '✨', fallback: '✦', text: t('benefitExclusive') },
    { emoji: '💎', fallback: '🔓', text: t('benefitGallery') },
  ];

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #36204a 0%, #2a1832 40%, #1e1228 100%)' }}>
      <div className="flex items-center gap-3 border-b border-white/8 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#3a2444' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors">{Icons.back}</button>
        <span className="text-sm font-medium tracking-wide text-white/90">Patreon</span>
      </div>
      <div className="flex-1 flex flex-col px-5 pt-6 overflow-y-auto scrollbar-none">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="text-center mb-5"
        >
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl mb-4" style={{ backgroundColor: '#4a2d56' }}>
            <img src={assetUrl('/game-icons/Patreon.png')} alt="" className="h-9 w-9" style={{ filter: 'brightness(0) saturate(100%) invert(55%) sepia(90%) saturate(1500%) hue-rotate(290deg) brightness(1.1)' }} />
          </div>
          <div className="text-lg font-light tracking-wide text-white/85 mb-2">{t('supportProject')}</div>
          <div className="text-[11px] text-white/30 leading-relaxed">{t('supportDesc')}</div>
        </motion.div>

        {/* Benefits */}
        <div className="space-y-1.5 mb-5">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.3 + i * 0.12 }}
              className="flex items-center gap-3 rounded-xl px-4 py-2.5"
              style={{ backgroundColor: '#33203c' }}
            >
              <PatreonBenefitIcon emoji={b.emoji} fallback={b.fallback} emojiMap={emojiMap} />
              <span className="text-xs text-white/60">{b.text}</span>
            </motion.div>
          ))}
        </div>

        {/* Membership */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.7 }}
          className="rounded-xl px-4 py-3 mb-6"
          style={{ backgroundColor: '#33203c' }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/40">{t('currentMembership')}</span>
            <span className="text-xs font-medium text-white/60 bg-white/5 px-2.5 py-0.5 rounded-full">{t('freeTier')}</span>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="space-y-3 mb-4">
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.85 }}
            type="button"
            onClick={() => openExternalUrl(patreonUrl || 'https://www.patreon.com')}
            className="flex items-center gap-3 w-full py-3 px-4 rounded-xl text-left hover:brightness-110 transition-all"
            style={{ backgroundColor: '#4a2d56' }}
          >
            <span className="flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-bold shrink-0" style={{ backgroundColor: '#9b3a8e' }}>1</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-white/85">{t('getMembership')}</div>
              <div className="text-[10px] text-white/30 mt-0.5">{t('subscribePatreon')}</div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/25 shrink-0"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 1.0 }}
            className="relative w-full"
          >
            <button
              type="button"
              onClick={doCheckSession}
              disabled={linking || !authEndpoint}
              className="flex items-center gap-3 w-full py-3 px-4 rounded-xl text-left hover:brightness-110 transition-all shadow-lg shadow-pink-500/10 disabled:opacity-60"
              style={{ backgroundColor: '#9b3a8e' }}
            >
              <span className="flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-bold shrink-0 bg-white/15">2</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-white/90 truncate">
                  {linking
                    ? t('checking')
                    : isLinked
                      ? `${t('linked')}${linkedTier ? ' · ' + linkedTier : ''}`
                      : linkStatus === 'opened'
                        ? t('authOpened')
                        : t('linkPatreon')}
                </div>
                <div className="text-[10px] text-white/40 mt-0.5 truncate">
                  {linkStatus === 'error' && !authEndpoint
                    ? 'Auth endpoint not configured'
                    : linkStatus === 'error'
                      ? 'Something went wrong'
                      : isLinked
                        ? t('tapToRefresh')
                        : t('connectAccount')}
                </div>
              </div>
              <img src={assetUrl('/game-icons/Patreon.png')} alt="" className="brightness-0 invert shrink-0" style={{ height: 14, width: 14, opacity: 0.6 }} />
            </button>
            {isLinked && (
              <button
                type="button"
                onClick={doLogout}
                title={t('logout') || 'Log out'}
                aria-label={t('logout') || 'Log out'}
                className="absolute top-1/2 right-10 -translate-y-1/2 flex items-center justify-center h-6 w-6 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-white/80" aria-hidden>
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── Photo viewer (fullscreen) ──────────────────────────────── */

function PhotoViewer({ photos, initialIndex, onClose, wallpaper, onSetWallpaper, t }) {
  const [index, setIndex] = useState(initialIndex);
  const indexRef = useRef(initialIndex);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [swipeX, setSwipeX] = useState(0);
  const [swipeY, setSwipeY] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const [showWallpaper, setShowWallpaper] = useState(false);
  const idleTimer = useRef(null);
  const zoomed = scale > 1.05;
  const lastPinchDist = useRef(null);
  const pinchBaseScale = useRef(1);
  // React attaches onWheel/onTouchMove as passive listeners — preventDefault()
  // on them no-ops with a console warning. For wheel-zoom and pinch-zoom we
  // need to call preventDefault to stop page scroll / browser pinch, so we
  // attach these two handlers natively via addEventListener with passive:false.
  const rootRef = useRef(null);

  // Keep indexRef in sync
  useEffect(() => { indexRef.current = index; }, [index]);

  // Show "Set as Wallpaper" after idle
  const resetIdle = useCallback(() => {
    setShowWallpaper(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setShowWallpaper(true), 1000);
  }, []);

  useEffect(() => {
    resetIdle();
    return () => clearTimeout(idleTimer.current);
  }, [index, resetIdle]);

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    resetIdle();
    setScale((s) => {
      const next = Math.max(1, Math.min(3, s - e.deltaY * 0.002));
      if (next <= 1.05) setPan({ x: 0, y: 0 });
      return next;
    });
  }, [resetIdle]);

  // Pointer events for pan (zoomed) or swipe (not zoomed)
  const isPinching = useRef(false);
  const activeTouches = useRef(0);

  const handlePointerDown = useCallback((e) => {
    if (isPinching.current) return;
    resetIdle();
    if (zoomed) {
      setDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setPanStart({ ...pan });
    } else {
      setSwipeStartX(e.clientX);
      setSwipeStartY(e.clientY);
      setSwipeX(0);
      setSwipeY(0);
    }
  }, [zoomed, pan, resetIdle]);

  const handlePointerMove = useCallback((e) => {
    if (isPinching.current) return;
    if (zoomed && dragging) {
      setPan({
        x: panStart.x + (e.clientX - dragStart.x),
        y: panStart.y + (e.clientY - dragStart.y),
      });
    } else if (swipeStartX !== null) {
      setSwipeX(e.clientX - swipeStartX);
      setSwipeY(e.clientY - swipeStartY);
    }
  }, [zoomed, dragging, dragStart, panStart, swipeStartX, swipeStartY]);

  const handlePointerUp = useCallback(() => {
    if (isPinching.current) return;
    if (zoomed) {
      setDragging(false);
    } else {
      const idx = indexRef.current;
      if (Math.abs(swipeX) > 60 && Math.abs(swipeX) > Math.abs(swipeY)) {
        if (swipeX < 0 && idx < photos.length - 1) {
          setIndex(idx + 1);
          setScale(1);
          setPan({ x: 0, y: 0 });
        } else if (swipeX > 0 && idx > 0) {
          setIndex(idx - 1);
          setScale(1);
          setPan({ x: 0, y: 0 });
        }
      }
      if (swipeY > 80 && Math.abs(swipeY) > Math.abs(swipeX)) {
        onClose();
      }
      setSwipeStartX(null);
      setSwipeStartY(null);
      setSwipeX(0);
      setSwipeY(0);
    }
  }, [zoomed, swipeX, swipeY, photos.length, onClose]);

  // Touch events for pinch-to-zoom + pan when zoomed
  const handleTouchStart = useCallback((e) => {
    activeTouches.current = e.touches.length;
    if (e.touches.length === 2) {
      // Cancel any in-progress swipe
      isPinching.current = true;
      setSwipeStartX(null);
      setSwipeX(0);
      setSwipeY(0);
      setDragging(false);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
      pinchBaseScale.current = scale;
    }
  }, [scale]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && lastPinchDist.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / lastPinchDist.current;
      const next = Math.max(1, Math.min(4, pinchBaseScale.current * ratio));
      setScale(next);
      if (next <= 1.05) setPan({ x: 0, y: 0 });
      resetIdle();
    }
  }, [resetIdle]);

  // Attach wheel + touchmove natively as non-passive so preventDefault works.
  const handleWheelRef = useRef(handleWheel);
  const handleTouchMoveRef = useRef(handleTouchMove);
  useEffect(() => { handleWheelRef.current = handleWheel; }, [handleWheel]);
  useEffect(() => { handleTouchMoveRef.current = handleTouchMove; }, [handleTouchMove]);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onWheelNative = (e) => handleWheelRef.current(e);
    const onTouchMoveNative = (e) => handleTouchMoveRef.current(e);
    el.addEventListener('wheel', onWheelNative, { passive: false });
    el.addEventListener('touchmove', onTouchMoveNative, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheelNative);
      el.removeEventListener('touchmove', onTouchMoveNative);
    };
  }, []);

  // Manual double-tap tracking — onDoubleClick / dblclick fires unreliably on
  // Android WebView when touch-action:none is set, so we detect taps ourselves.
  const lastTapRef = useRef({ t: 0, x: 0, y: 0 });

  const toggleZoom = useCallback(() => {
    setScale((s) => (s > 1.05 ? 1 : 2));
    setPan({ x: 0, y: 0 });
    resetIdle();
  }, [resetIdle]);

  const handleTouchEnd = useCallback((e) => {
    activeTouches.current = e.touches.length;
    if (e.touches.length === 0) {
      const wasPinching = isPinching.current;
      if (wasPinching) {
        setTimeout(() => { isPinching.current = false; }, 100);
        // If the user nearly pinched back to normal, snap to 1 so swipe resumes
        // immediately without requiring an extra gesture.
        setScale((s) => (s < 1.15 ? 1 : s));
      } else if (e.changedTouches && e.changedTouches.length === 1) {
        // Single-touch tap end — check for double-tap.
        const touch = e.changedTouches[0];
        const now = Date.now();
        const last = lastTapRef.current;
        const dt = now - last.t;
        const dx = touch.clientX - last.x;
        const dy = touch.clientY - last.y;
        if (dt > 0 && dt < 300 && Math.hypot(dx, dy) < 30) {
          toggleZoom();
          lastTapRef.current = { t: 0, x: 0, y: 0 };
        } else {
          lastTapRef.current = { t: now, x: touch.clientX, y: touch.clientY };
        }
      }
      lastPinchDist.current = null;
    }
  }, [toggleZoom]);

  // Mouse double-click still works on desktop.
  const handleDoubleClick = useCallback(() => {
    toggleZoom();
  }, [toggleZoom]);

  // Keyboard navigation: ←/→ change photo, ↑/↓ zoom, Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setScale(1); setPan({ x: 0, y: 0 });
        setIndex((i) => (i > 0 ? i - 1 : i));
        resetIdle();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setScale(1); setPan({ x: 0, y: 0 });
        setIndex((i) => (i < photos.length - 1 ? i + 1 : i));
        resetIdle();
      } else if (e.key === 'ArrowUp' || e.key === '+' || e.key === '=') {
        e.preventDefault();
        setScale((s) => Math.min(4, s + 0.5));
        resetIdle();
      } else if (e.key === 'ArrowDown' || e.key === '-') {
        e.preventDefault();
        setScale((s) => {
          const next = Math.max(1, s - 0.5);
          if (next <= 1.05) setPan({ x: 0, y: 0 });
          return next;
        });
        resetIdle();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose, resetIdle]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      ref={rootRef}
      className="absolute inset-0 z-50 bg-black flex items-center justify-center select-none"
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <motion.button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white/80 hover:text-white"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15, duration: 0.2 }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </motion.button>

      {/* Image — entry animation lives on the outer motion.div, while the
          inner div owns the user-controlled zoom/pan transform. They must stay
          separate: motion takes ownership of `transform` on any element with
          animate={{ scale }}, which would otherwise overwrite our zoom. */}
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full h-full flex items-center justify-center"
      >
        <div
          style={{
            transform: `scale(${scale}) translate(${pan.x / scale}px, ${pan.y / scale}px) translateX(${!zoomed ? swipeX * 0.3 : 0}px)`,
            transition: dragging || swipeStartX !== null ? 'none' : 'transform 0.2s ease-out',
          }}
          className="w-full h-full flex items-center justify-center"
        >
          <img
            src={photos[index]}
            alt=""
            className="max-w-full max-h-full object-contain pointer-events-none"
            draggable={false}
          />
        </div>
      </motion.div>

      {/* Counter */}
      <div className="absolute top-3 left-0 right-0 text-center text-xs text-white/40">
        {index + 1} / {photos.length}
      </div>

      {/* Set as Wallpaper */}
      <AnimatePresence>
        {showWallpaper && !zoomed && onSetWallpaper && !(wallpaper?.imageUrl === photos[index]) && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25 }}
            type="button"
            onClick={(e) => { e.stopPropagation(); onSetWallpaper(index); }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/15 backdrop-blur-sm text-white text-xs font-medium px-5 py-2.5 rounded-full hover:bg-white/25 transition-colors"
          >
            {t('setAsWallpaper')}
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Gallery screen ─────────────────────────────────────────── */

function GalleryScreen({ onBack, onNavigatePatreon, onPhotoViewerToggle, galleryUnlocks, allStoryflows, teamId, playerTier, fullGalleryTier, patreonTiers, wallpaper, onSetWallpaper, t }) {
  const [tab, setTab] = useState('gallery'); // 'gallery' | 'crosssave' | 'full'
  const [viewerIndex, setViewerIndex] = useState(null);
  const [crossSaveUnlocks, setCrossSaveUnlocks] = useState(null);

  useEffect(() => {
    onPhotoViewerToggle?.(viewerIndex !== null);
  }, [viewerIndex, onPhotoViewerToggle]);

  // Load cross-save unlocks when tab switches
  useEffect(() => {
    if (tab !== 'crosssave' || crossSaveUnlocks || !teamId) return;
    (async () => {
      try {
        let allSaves = [];
        if (isStandalone()) {
          allSaves = listSaves(teamId);
        } else {
          const token = sessionStorage.getItem('flowcore_session_token') || '';
          const res = await fetch(`${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            allSaves = data.saves || [];
          }
        }
        {
          const merged = {};
          for (const save of allSaves) {
            const unlocks = save.state?.galleryUnlocks || [];
            for (const u of unlocks) {
              const key = `${u.storyflowId}:${u.nodeId}`;
              if (!merged[key] || u.unlockedAt > merged[key].unlockedAt) {
                merged[key] = u;
              }
            }
          }
          // Also merge current progress unlocks
          for (const u of (galleryUnlocks || [])) {
            const key = `${u.storyflowId}:${u.nodeId}`;
            if (!merged[key] || u.unlockedAt > merged[key].unlockedAt) {
              merged[key] = u;
            }
          }
          setCrossSaveUnlocks(validateGalleryUnlocks(Object.values(merged), allStoryflows));
        }
      } catch {}
    })();
  }, [tab, crossSaveUnlocks, teamId, galleryUnlocks, allStoryflows]);

  // Current progress photos
  const currentPhotos = useMemo(() => {
    const valid = validateGalleryUnlocks(galleryUnlocks || [], allStoryflows || []);
    return valid.sort((a, b) => (b.unlockedAt || 0) - (a.unlockedAt || 0));
  }, [galleryUnlocks, allStoryflows]);

  // Full gallery photos (all image nodes)
  const fullPhotos = useMemo(() => collectAllImages(allStoryflows || []), [allStoryflows]);

  const canAccessFullGallery = !fullGalleryTier || meetsTier(playerTier, fullGalleryTier, patreonTiers || []);

  const photos = tab === 'gallery' ? currentPhotos
    : tab === 'crosssave' ? (crossSaveUnlocks || currentPhotos)
    : fullPhotos;

  const tabTitle = { gallery: t('gallery'), crosssave: t('crossSave'), full: t('fullGallery') };
  const showGrid = tab === 'gallery' || tab === 'crosssave' || (tab === 'full' && canAccessFullGallery);
  const count = photos.length;
  const totalCount = fullPhotos.length || count;

  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #2a1438 0%, #1e0e28 40%, #160a22 100%)' }}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#3a1840' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/70 hover:text-white transition-colors shrink-0">{Icons.back}</button>
        <span className="text-sm font-medium tracking-wide text-white/90 flex-1 truncate">{tabTitle[tab]}</span>
        {showGrid && <span className="shrink-0 text-[11px] text-white/35 tabular-nums mr-1">{count}/{totalCount}</span>}
        <div className="flex gap-0.5 shrink-0">
          {/* Gallery */}
          <button type="button" onClick={() => setTab('gallery')}
            className={`p-1.5 rounded-lg transition-colors ${tab === 'gallery' ? 'bg-white/15 text-white' : 'text-white/25 hover:text-white/50'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
          </button>
          {/* Cross-save */}
          <button type="button" onClick={() => setTab('crosssave')}
            className={`p-1.5 rounded-lg transition-colors ${tab === 'crosssave' ? 'bg-white/15 text-white' : 'text-white/25 hover:text-white/50'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
          </button>
          {/* Full (premium trophy) */}
          <button type="button" onClick={() => setTab('full')}
            className={`p-1.5 rounded-lg transition-colors ${tab === 'full' ? 'bg-white/15 text-white' : 'text-white/25 hover:text-white/50'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5"><path d="M6 9H4a1 1 0 01-1-1V4a1 1 0 011-1h16a1 1 0 011 1v4a1 1 0 01-1 1h-2" /><path d="M6 3v6a6 6 0 0012 0V3" /><path d="M12 15v4M8 19h8" /></svg>
          </button>
        </div>
      </div>

      {showGrid ? (
        <div className="flex-1 overflow-y-auto scrollbar-none">
          {photos.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-white/30 text-sm">{t('noPhotosYet')}</div>
          ) : (
            <div className="grid grid-cols-3 gap-[2px]">
              {photos.map((photo, i) => (
                <button key={`${photo.storyflowId || ''}-${photo.nodeId || i}`} type="button" onClick={() => setViewerIndex(i)} className="aspect-square overflow-hidden">
                  <img src={photo.imagePreview512 || photo.imageUrl} alt="" className="h-full w-full object-cover hover:opacity-80 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} className="h-16 w-16 text-white/10">
            <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <div className="text-center">
            <div className="text-sm text-white/40 mb-1.5">{t('premiumContent')}{fullGalleryTier ? ` — ${fullGalleryTier} ${t('andAbove')}` : ''}</div>
            <div className="text-[11px] text-white/20 leading-relaxed">{t('linkPatreonGallery')}</div>
          </div>
          <button type="button" onClick={onNavigatePatreon} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-medium text-white hover:brightness-110 transition-all" style={{ backgroundColor: '#9b3a8e' }}>
            <img src={assetUrl('/game-icons/Patreon.png')} alt="" className="h-4 w-4 brightness-0 invert" />
            {t('goToPatreon')}
          </button>
        </div>
      )}

      <AnimatePresence>
        {viewerIndex !== null && (
          <PhotoViewer
            photos={photos.map((p) => p.imageUrl || p.imagePreview512)}
            initialIndex={viewerIndex}
            onClose={() => setViewerIndex(null)}
            wallpaper={wallpaper}
            onSetWallpaper={(idx) => {
              const photo = photos[idx];
              if (photo && onSetWallpaper) {
                onSetWallpaper({ storyflowId: photo.storyflowId, nodeId: photo.nodeId, imageUrl: photo.imageUrl || photo.imagePreview512 });
              }
            }}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Typing indicator ────────────────────────────────────────── */

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-white/50"
          animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

/* ── Animated "typing" / "typing." / "typing.." / "typing..." ── */

function TypingText({ label }) {
  return <>{label || 'typing...'}</>;
}

/* ── Inline status emoji (small animated) ──────────────────── */

function StatusEmojiInline({ emoji, emojiMap, size = 16 }) {
  const lottieData = useLottieData(emoji && emojiMap?.[emoji] ? emoji : null, emojiMap);
  if (!emoji) return null;
  if (lottieData) {
    return <Lottie animationData={lottieData} loop style={{ width: size, height: size, display: 'inline-block', flexShrink: 0 }} />;
  }
  return <span style={{ fontSize: size * 0.75, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>;
}

/* ── Introduce (contact card) bubble ────────────────────────── */

function IntroduceBubble({ message, onClick }) {
  return (
    <ChatBubbleShell isUser={false}>
      <button
        type="button"
        onClick={() => onClick?.(message.introduceId)}
        className="max-w-[80%] rounded-2xl px-3 py-2.5 shadow-sm cursor-pointer hover:brightness-110 active:scale-[0.98] transition-all flex items-center gap-3"
        style={{
          backgroundColor: '#3a3342',
          borderBottomLeftRadius: '4px',
          border: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {message.introduceAvatar ? (
          <img src={message.introduceAvatar} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
            style={{ backgroundColor: avatarColor(message.introduceName) }}
          >
            {avatarInitials(message.introduceName)}
          </div>
        )}
        <div className="min-w-0 flex-1 text-left">
          <div className="text-sm font-semibold text-white/90">{message.introduceName}</div>
          <div className="text-[11px] text-white/40">Contact</div>
        </div>
        <svg className="h-5 w-5 text-white/30 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </ChatBubbleShell>
  );
}

/* ── Timeskip separator (centered line in chat) ─────────────── */

function TimeskipSeparator({ label }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-3 px-6 py-1.5"
    >
      <div className="flex-1 h-px bg-white/10" />
      <span className="text-[10px] text-white/30 font-medium tracking-wide whitespace-nowrap">{label}</span>
      <div className="flex-1 h-px bg-white/10" />
    </motion.div>
  );
}

/* ── Chat bubble ─────────────────────────────────────────────── */

/**
 * Bubble entry shell. Incoming messages grow a height: 0 → auto placeholder
 * so the chat content gains space gradually instead of jerking when a new
 * bubble lands; combined with the ResizeObserver in ChatPane the scroll
 * position rides the growth so the previous bottom row stays put. User
 * messages keep a simpler opacity/y entry — the disappearing choice panel
 * underneath already absorbs their height.
 */
function ChatBubbleShell({ isUser, children }) {
  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="flex justify-end px-3"
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      transition={{
        height: { duration: 0.22, ease: [0.16, 1, 0.3, 1] },
        opacity: { duration: 0.22, delay: 0.05, ease: 'easeOut' },
      }}
      style={{ overflow: 'hidden' }}
    >
      <div className="flex justify-start px-3">{children}</div>
    </motion.div>
  );
}

/* ── Voice message bubble (telegram-style) ───────────────────── */

const VOICE_WAVEFORM_BARS = [4, 8, 6, 11, 5, 13, 4, 10, 7, 12, 5, 9, 6, 11, 4, 8, 12, 5, 10, 6, 9, 13, 5, 8, 11, 4, 9, 7, 6, 10];

function VoiceMessageBubble({ audioUrl, subtitle, isUser }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [subtitleOpen, setSubtitleOpen] = useState(false);
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const stopTrack = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (a && a.duration) setProgress(a.currentTime / a.duration);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const toggle = useCallback(() => {
    if (playing) {
      // Stop + rewind to 0 so next press replays from the start.
      stopTrack();
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (_) { /* ignore */ }
        audioRef.current = null;
      }
      setPlaying(false);
      setProgress(0);
      return;
    }
    if (!audioUrl) return;
    const next = new Audio(audioUrl);
    next.addEventListener('ended', () => {
      stopTrack();
      setPlaying(false);
      setProgress(0);
      audioRef.current = null;
    });
    next.play().catch(() => {});
    audioRef.current = next;
    setPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [playing, audioUrl, stopTrack, tick]);

  useEffect(() => () => {
    stopTrack();
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
    }
  }, [stopTrack]);

  const bgColor = isUser ? '#5c3a8e' : '#3a3342';
  const barColor = '#e7d9ff';

  return (
    <ChatBubbleShell isUser={isUser}>
      <div
        className="relative max-w-[85%] rounded-2xl shadow-sm text-white overflow-hidden"
        style={{
          backgroundColor: bgColor,
          borderBottomRightRadius: isUser ? '4px' : undefined,
          borderBottomLeftRadius: !isUser ? '4px' : undefined,
        }}
      >
        <div className="flex items-center gap-2.5 px-3 py-2">
          <button
            type="button"
            onClick={toggle}
            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:brightness-110 active:scale-95 transition"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            aria-label={playing ? 'Stop' : 'Play'}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <div className="flex items-center gap-[2px] flex-1 h-5 min-w-[90px]">
            {VOICE_WAVEFORM_BARS.map((h, i) => {
              const played = (i / VOICE_WAVEFORM_BARS.length) < progress;
              return (
                <div
                  key={i}
                  style={{
                    width: 2,
                    minWidth: 2,
                    height: h + 4,
                    borderRadius: 2,
                    backgroundColor: barColor,
                    opacity: played ? 1 : 0.35,
                    transition: 'opacity 0.1s'
                  }}
                />
              );
            })}
          </div>
          {subtitle ? (
            <button
              type="button"
              onClick={() => setSubtitleOpen((v) => !v)}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:brightness-125 transition font-bold"
              style={{
                backgroundColor: subtitleOpen ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)',
                fontSize: '0.78rem',
                letterSpacing: 0
              }}
              aria-label="Toggle subtitle"
              aria-pressed={subtitleOpen}
            >
              T
            </button>
          ) : null}
        </div>
        {subtitle && subtitleOpen ? (
          <div
            className="px-3.5 pb-2 pt-1.5 text-sm leading-relaxed"
            style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}
          >
            <span className="whitespace-pre-wrap break-words">{subtitle}</span>
          </div>
        ) : null}
      </div>
    </ChatBubbleShell>
  );
}

const ChatBubble = memo(function ChatBubble({ message, isUser, onImageClick, emojiMap, onIntroduceClick }) {
  const emojiOnly = !message.introduce && isEmojiOnly(message.text);
  const emoji = emojiOnly ? message.text.replace(/\s/g, '') : null;
  const hasAnimation = emoji && emojiMap && emojiMap[emoji];
  const lottieData = useLottieData(hasAnimation ? emoji : null, emojiMap);

  if (message.introduce) {
    return <IntroduceBubble message={message} onClick={onIntroduceClick} />;
  }

  if (message.voice) {
    return (
      <VoiceMessageBubble
        audioUrl={message.voice.audioUrl}
        subtitle={message.voice.subtitle}
        isUser={isUser}
      />
    );
  }

  if (message.image) {
    return (
      <ChatBubbleShell isUser={isUser}>
        <button
          type="button"
          onClick={() => onImageClick?.(message.image)}
          className="max-w-[50%] rounded-2xl overflow-hidden shadow-sm cursor-pointer hover:opacity-85 hover:scale-[1.02] active:scale-[0.98] transition-all"
          style={{
            borderBottomRightRadius: isUser ? '4px' : undefined,
            borderBottomLeftRadius: !isUser ? '4px' : undefined,
          }}
        >
          <img src={message.image} alt="" className="w-full h-auto object-cover" />
        </button>
      </ChatBubbleShell>
    );
  }

  // Animated emoji message — no bubble, just the animation (or placeholder while loading)
  if (hasAnimation) {
    return (
      <ChatBubbleShell isUser={isUser}>
        <div style={{ width: 64, height: 64 }}>
          {lottieData
            ? <Lottie animationData={lottieData} loop={false} style={{ width: 64, height: 64 }} />
            : null}
        </div>
      </ChatBubbleShell>
    );
  }

  // Emoji-only but no animation available — show large emoji text without bubble
  if (emojiOnly) {
    return (
      <ChatBubbleShell isUser={isUser}>
        <span className="text-5xl">{message.text}</span>
      </ChatBubbleShell>
    );
  }

  return (
    <ChatBubbleShell isUser={isUser}>
      <div
        className="relative max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm text-white"
        style={{
          backgroundColor: isUser ? '#5c3a8e' : '#3a3342',
          borderBottomRightRadius: isUser ? '4px' : undefined,
          borderBottomLeftRadius: !isUser ? '4px' : undefined,
        }}
      >
        <span className="whitespace-pre-wrap break-words">{message.text}</span>
      </div>
    </ChatBubbleShell>
  );
});

/* ── Choice buttons (bottom of chat) ─────────────────────────── */

function ChoiceButtons({ choices, onChoose }) {
  return (
    <div className="shrink-0 px-3 py-3" style={{ backgroundColor: '#272331' }}>
      <div className="flex flex-col gap-2 items-end">
        {choices.map((choice, i) => {
          const emojiOnly = isEmojiOnly(choice);
          return (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.15, delay: i * 0.06 }}
              type="button"
              onPointerDown={() => playGameSound('buttonPress')}
              onClick={() => { playGameSound('buttonRelease'); onChoose(i); }}
              className={`
                max-w-[80%] rounded-2xl px-3.5 py-2 font-medium text-white
                hover:brightness-125
                transition-colors text-left leading-relaxed
                ${emojiOnly ? 'text-xl' : 'text-sm'}
              `}
              style={{
                border: '2px dashed #5c3a8e',
                backgroundColor: 'rgba(92, 58, 142, 0.15)',
                borderBottomRightRadius: '4px',
              }}
            >
              {choice}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Video overlay (fullscreen camera mode) ────────────────── */

function VideoOverlay({ startNodeId, storyflowData, onExit, lang }) {
  const [currentNodeId, setCurrentNodeId] = useState(startNodeId);
  const [phase, setPhase] = useState('fadein'); // fadein | playing | replicas | waiting | choices
  const [cycleComplete, setCycleComplete] = useState(false);
  const [visibleReplicas, setVisibleReplicas] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const videoRef = useRef(null);
  const loopingRef = useRef(false);
  const prevTimeRef = useRef(0);

  const currentNode = useMemo(() => {
    return storyflowData?.graph?.nodes?.find((n) => n.id === currentNodeId);
  }, [currentNodeId, storyflowData]);

  const videoUrl = currentNode?.data?.videoUrl || '';
  const rawText = currentNode ? getLocalizedText(currentNode, lang) : '';
  const messages = useMemo(() => rawText ? rawText.split('\n').filter(Boolean) : [], [rawText]);
  const variants = useMemo(() => (currentNode ? getLocalizedVariants(currentNode, lang) : []).filter(Boolean), [currentNode, lang]);
  const hasReplicas = messages.length > 0;
  const hasChoices = variants.length > 0;

  // Fade-in complete → start playing
  useEffect(() => {
    if (phase === 'fadein') {
      const t = setTimeout(() => setPhase('playing'), 900);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // Detect first loop cycle via timeupdate (native loop attr handles smooth repeat)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    prevTimeRef.current = 0;
    const handleTimeUpdate = () => {
      if (prevTimeRef.current > 0.5 && video.currentTime < prevTimeRef.current - 0.5 && !loopingRef.current) {
        loopingRef.current = true;
        setCycleComplete(true);
      }
      prevTimeRef.current = video.currentTime;
    };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [currentNodeId]);

  useEffect(() => {
    if (!cycleComplete || phase !== 'playing') return;
    if (hasReplicas) {
      setPhase('replicas');
      setVisibleReplicas(0);
    } else {
      setPhase('waiting');
    }
  }, [cycleComplete, phase, hasReplicas]);

  // Show replicas one by one with 750ms interval
  useEffect(() => {
    if (phase !== 'replicas') return;
    if (visibleReplicas < messages.length) {
      const t = setTimeout(() => setVisibleReplicas((v) => v + 1), 750);
      return () => clearTimeout(t);
    }
    // All replicas visible → waiting
    const t2 = setTimeout(() => setPhase('waiting'), 400);
    return () => clearTimeout(t2);
  }, [phase, visibleReplicas, messages.length]);

  // Follow edge to next node
  const goToNext = useCallback((handleId = 'flow-out') => {
    const edge = storyflowData?.graph?.edges?.find(
      (e) => e.source === currentNodeId && e.sourceHandle === handleId
    );
    if (!edge) { onExit(null); return; }
    const nextNode = storyflowData?.graph?.nodes?.find((n) => n.id === edge.target);
    if (nextNode?.type === 'sfVideo') {
      // Stay in video mode — transition
      setTransitioning(true);
      setTimeout(() => {
        setCycleComplete(false);
        loopingRef.current = false;
        setPhase('playing');
        setVisibleReplicas(0);
        setCurrentNodeId(edge.target);
        setTransitioning(false);
      }, 500);
    } else {
      onExit(edge.target);
    }
  }, [currentNodeId, storyflowData, onExit]);

  // Handle click anywhere on screen
  const handleScreenClick = useCallback(() => {
    if (phase === 'waiting') {
      if (hasChoices) {
        setPhase('choices');
      } else {
        goToNext('flow-out');
      }
    }
  }, [phase, hasChoices, goToNext]);

  const handleChoiceSelect = useCallback((index) => {
    vibrateTap();
    goToNext(`choice-${index}`);
  }, [goToNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
      transition={{ duration: 0.6 }}
      className="absolute inset-0 z-[80] flex flex-col"
      style={{ backgroundColor: '#000' }}
      onClick={handleScreenClick}
    >
      {/* Video */}
      <motion.div
        className="absolute inset-0"
        initial={{ filter: 'blur(20px)', opacity: 0 }}
        animate={{
          filter: phase === 'fadein' ? 'blur(20px)' : 'blur(0px)',
          opacity: phase === 'fadein' ? 0 : (transitioning ? 0 : 1),
        }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <video
          ref={videoRef}
          src={videoUrl}
          muted
          loop
          playsInline
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </motion.div>

      {/* REC indicator — top right */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
        <motion.div
          animate={{ opacity: [1, 0.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }}
        />
        <img src={assetUrl('/game-icons/live.png')} alt="REC" style={{ height: 16, objectFit: 'contain' }} />
      </div>

      {/* Replicas — left side, 150px from bottom */}
      <AnimatePresence>
        {(phase === 'replicas' || phase === 'waiting') && hasReplicas && phase !== 'choices' && (
          <motion.div
            key="replicas"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
            className="absolute left-3 right-16 z-10 flex flex-col gap-1.5"
            style={{ bottom: 150 }}
          >
            {messages.slice(0, visibleReplicas).map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className="self-start"
              >
                <div
                  className="rounded-2xl rounded-bl-md px-3.5 py-2 text-sm leading-relaxed shadow-lg max-w-[85%]"
                  style={{ backgroundColor: '#fff', color: '#111' }}
                >
                  <span className="whitespace-pre-wrap break-words">{msg}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Choices — right side, 75px from bottom */}
      <AnimatePresence>
        {phase === 'choices' && hasChoices && (
          <div
            className="absolute right-3 left-10 z-10 flex flex-col gap-2 items-end"
            style={{ bottom: 75 }}
            onClick={(e) => e.stopPropagation()}
          >
            {variants.map((choice, i) => (
              <motion.button
                key={i}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2, delay: i * 0.1 }}
                type="button"
                onPointerDown={() => playGameSound('buttonPress')}
                onClick={() => { playGameSound('buttonRelease'); handleChoiceSelect(i); }}
                className="max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2 text-sm font-medium leading-relaxed text-left shadow-lg hover:brightness-110"
                style={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#111', border: '1px solid rgba(255,255,255,0.3)' }}
              >
                {choice}
              </motion.button>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* "Continue" button when no choices, phase=waiting */}
      <AnimatePresence>
        {phase === 'waiting' && !hasChoices && cycleComplete && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute right-3 z-10"
            style={{ bottom: 75 }}
          >
            <div className="rounded-2xl px-5 py-2 text-sm font-semibold shadow-lg" style={{ backgroundColor: '#fff', color: '#111' }}>
              Continue
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Notification toast ─────────────────────────────────────── */

function NotificationToast({ notification, contact, onClick, statusEmoji, emojiMap }) {
  const displayAvatar = notification.avatarOverride || contact?.avatar;
  const displayName = notification.nameOverride || contact?.name;
  return (
    <motion.div
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      exit={{ y: -80, opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } }}
      transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      className="absolute top-7 left-0 right-0 z-[55] flex justify-center pointer-events-none"
    >
      <div className="w-[280px] max-w-[calc(100%-24px)] pointer-events-auto cursor-pointer" onClick={onClick}>
      <div className="flex items-center gap-3 rounded-2xl p-3 shadow-2xl border border-white/10"
           style={{ backgroundColor: 'rgba(24, 22, 30, 0.95)', backdropFilter: 'blur(16px)' }}>
        {displayAvatar ? (
          <img src={displayAvatar} alt="" className="h-10 w-10 shrink-0 rounded-xl object-cover" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white"
               style={{ backgroundColor: avatarColor(displayName) }}>
            {avatarInitials(displayName)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-white/90 flex items-center gap-1">
            {displayName}
            {!notification.avatarOverride && statusEmoji && <StatusEmojiInline emoji={statusEmoji} emojiMap={emojiMap} size={13} />}
          </div>
          <div className="text-[11px] text-white/50 truncate">{notification.text}</div>
        </div>
      </div>
      </div>
    </motion.div>
  );
}

/* ── New Chapter overlay ────────────────────────────────────── */

function NewChapterOverlay({ onContinue, t, chapterNumber, chapterName, chapterCover }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 z-[70] flex items-center justify-center"
    >
      <motion.div
        initial={{ backdropFilter: 'blur(0px)' }}
        animate={{ backdropFilter: 'blur(16px)' }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0 bg-black/75"
      />
      <div className="relative z-10 flex flex-col items-center gap-3 px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
        >
          {chapterCover ? (
            <img src={chapterCover} alt="" className="h-20 w-20 rounded-2xl object-cover shadow-lg" />
          ) : (
            <img src={assetUrl('/game-icons/Book Filled.png')} alt="" className="h-16 w-16" />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: 'easeOut' }}
          className="text-white/40 text-[11px] tracking-[0.3em] uppercase"
        >
          {t('chapter')} {chapterNumber ?? ''}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.0, ease: 'easeOut' }}
          className="text-white text-[22px] font-light tracking-wide text-center -mt-1"
        >
          {chapterName || ''}
        </motion.div>
        <motion.button
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.4, ease: 'easeOut' }}
          type="button"
          onClick={onContinue}
          className="mt-8 px-10 py-2 rounded-2xl bg-purple-500/25 text-white text-sm font-medium hover:bg-purple-500/35 active:scale-95 transition-all"
        >
          {t('continueBtn')}
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── Results overlay (stats phase of end screen) ────────────── */

function ResultsOverlay({ onNext, t, stats, relationships }) {
  const [trophyData, setTrophyData] = useState(null);
  useEffect(() => {
    if (_lottieCache['🏆']) { setTrophyData(_lottieCache['🏆']); return; }
    fetch(assetUrl('/lottie-emoji/trophy.json'))
      .then((r) => r.json())
      .then((d) => { _lottieCache['🏆'] = d; setTrophyData(d); })
      .catch(() => {});
  }, []);

  const statIcons = {
    'Photos Unlocked': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-3.5 w-3.5 text-purple-300/60"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
    'Time Played': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-3.5 w-3.5 text-purple-300/60"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  };
  const statRows = stats && stats.length ? stats : [];
  const relRows = relationships && relationships.length ? relationships : [];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 z-[70] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <div className="relative z-10 flex flex-col items-center w-full px-6 max-w-xs">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-2"
        >
          {trophyData ? (
            <Lottie animationData={trophyData} loop={false} style={{ width: 64, height: 64 }} />
          ) : (
            <div style={{ width: 64, height: 64 }} />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-white text-lg font-light tracking-[0.2em] uppercase mb-6"
        >
          {t('results')}
        </motion.div>
        {statRows.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.5 + i * 0.12 }}
            className="flex items-center justify-between w-full py-2.5 border-b border-white/5"
          >
            <span className="text-xs text-purple-300/80 flex items-center gap-1.5">{statIcons[s.label]}{s.label}</span>
            <span className="text-xs text-white/80 tabular-nums">{s.value}</span>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 1.1 }}
          className="w-full mt-5 mb-2"
        >
          <div className="text-[10px] text-white/30 uppercase tracking-wider text-center">{t('relationship')}</div>
        </motion.div>
        {relRows.map((r, i) => (
          <motion.div
            key={r.name}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 1.3 + i * 0.15 }}
            className="w-full mb-2"
          >
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[11px] text-white/80">{r.name}</span>
              <span className={`text-[10px] font-medium ${r.value >= 0 ? 'text-emerald-400/80' : 'text-red-400/60'}`}>
                {r.value > 0 ? '+' : ''}{r.value}
              </span>
            </div>
            <div className="h-1 rounded-full bg-white/[0.08] relative overflow-hidden">
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/15" />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(Math.abs(r.value) / 50) * 50}%` }}
                transition={{ duration: 0.6, delay: 1.5 + i * 0.15 }}
                className="absolute top-0 bottom-0 rounded-full"
                style={r.value >= 0
                  ? { left: '50%', background: 'linear-gradient(90deg, #34d399, #6ee7b7)' }
                  : { right: '50%', background: 'linear-gradient(270deg, #f87171, #fb923c)' }
                }
              />
            </div>
          </motion.div>
        ))}
        <motion.button
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 2.0 }}
          type="button"
          onClick={onNext}
          className="mt-4 px-10 py-2 rounded-2xl bg-purple-500/25 text-white text-sm font-medium hover:bg-purple-500/35 active:scale-95 transition-all"
        >
          Next
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── Coming Soon overlay ────────────────────────────────────── */

function ComingSoonOverlay({ onPatreon, onSaves, onNewGame, t }) {
  const [glassesData, setGlassesData] = useState(null);
  useEffect(() => {
    if (_lottieCache['🥂']) { setGlassesData(_lottieCache['🥂']); return; }
    fetch(assetUrl('/lottie-emoji/clinkingGlasses.json'))
      .then((r) => r.json())
      .then((d) => { _lottieCache['🥂'] = d; setGlassesData(d); })
      .catch(() => {});
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 z-[70] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <div className="relative z-10 flex flex-col items-center w-full px-6 max-w-xs">
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-4"
        >
          {glassesData ? (
            <Lottie animationData={glassesData} loop={false} style={{ width: 64, height: 64 }} />
          ) : (
            <div style={{ width: 64, height: 64 }} />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-white text-xl font-light tracking-wide mb-2 text-center"
        >
          {t('comingSoonTitle')}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-white/55 text-xs text-center leading-relaxed mb-6 px-2"
        >
          {t('comingSoonBody')}
        </motion.div>
        <div className="w-full max-w-[240px] mx-auto space-y-2.5">
          <motion.button
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.1 }}
            type="button"
            onClick={onPatreon}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl text-sm font-medium text-white hover:brightness-110 active:scale-[0.97] transition-all shadow-lg shadow-pink-500/10"
            style={{ backgroundColor: '#9b3a8e' }}
          >
            <img src={assetUrl('/game-icons/Patreon.png')} alt="" className="h-4 w-4 brightness-0 invert" />
            Patreon
          </motion.button>
          <motion.button
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.3 }}
            type="button"
            onClick={onSaves}
            className="w-full py-2.5 rounded-2xl bg-purple-500/20 text-sm font-medium text-white/90 hover:bg-purple-500/30 active:scale-[0.97] transition-all"
          >
            {t('saves')}
          </motion.button>
          <motion.button
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.5 }}
            type="button"
            onClick={onNewGame}
            className="w-full py-2.5 rounded-2xl bg-purple-500/20 text-sm font-medium text-white/90 hover:bg-purple-500/30 active:scale-[0.97] transition-all"
          >
            {t('newGame')}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Exit confirmation modal ────────────────────────────────── */

function ExitConfirmModal({ onConfirm, onCancel, emojiMap, t }) {
  const lottieData = useLottieData(emojiMap && emojiMap['👋'] ? '👋' : null, emojiMap);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="absolute inset-0 z-[80] flex items-center justify-center"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 10 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="relative z-10 flex flex-col items-center w-full max-w-[260px] mx-4 rounded-2xl px-6 pt-6 pb-5"
        style={{ backgroundColor: '#2a2035' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated emoji */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1, type: 'spring', stiffness: 200 }}
          className="mb-3"
        >
          {lottieData ? (
            <Lottie animationData={lottieData} loop style={{ width: 52, height: 52 }} />
          ) : (
            <motion.span
              animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
              className="inline-block text-4xl"
              style={{ transformOrigin: '70% 80%' }}
            >👋</motion.span>
          )}
        </motion.div>

        <div className="text-sm font-medium text-white/90 mb-1">{t ? t('leaveGame') : 'Leave the game?'}</div>
        <div className="text-[11px] text-white/35 text-center mb-5 leading-relaxed">{t ? t('progressSaved') : 'Your progress has been saved'}</div>

        <div className="flex w-full gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 rounded-xl text-xs font-medium text-white/70 bg-white/8 hover:bg-white/12 active:scale-[0.97] transition-all"
          >
            {t ? t('stay') : 'Stay'}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-2 rounded-xl text-xs font-medium text-white hover:brightness-110 active:scale-[0.97] transition-all"
            style={{ backgroundColor: '#ef4444' }}
          >
            {t ? t('exit') : 'Exit'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Contact list item ───────────────────────────────────────── */

function ContactItem({ contact, lastMessage, hasChoices, active, onClick, statusEmoji, emojiMap, isTyping }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${active ? 'bg-white/5' : 'hover:bg-white/5'}`}
    >
      {contact.avatar ? (
        <img src={contact.avatar} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: avatarColor(contact.name) }}>
          {avatarInitials(contact.name)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <span className="truncate text-sm font-semibold text-white flex items-center gap-1">
          {contact.name}
          {statusEmoji && <StatusEmojiInline emoji={statusEmoji} emojiMap={emojiMap} />}
        </span>
        <span className="truncate text-xs text-white/50 block mt-0.5">
          {isTyping
            ? 'Typing...'
            : lastMessage?.voice
              ? '🎙 Voice message'
              : lastMessage?.text || lastMessage?.image
                ? (lastMessage.introduce
                    ? `📇 ${lastMessage.introduceName}`
                    : lastMessage.image
                      ? 'Photo'
                      : lastMessage.text)
                : 'No messages yet'}
        </span>
      </div>
      {hasChoices && (
        <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-rose-400/80" />
      )}
    </button>
  );
}

/* ── Story Graph gate modal ────────────────────────────────── */

function StoryGraphGateModal({ onContinue, onCancel, t, tierName }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.15 }}
        className="w-72 rounded-2xl border border-white/10 p-5"
        style={{ backgroundColor: '#1e1b25' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="rgb(167,139,250)" strokeWidth={2} className="h-5 w-5">
            <line x1="6" y1="3" x2="6" y2="15" />
            <circle cx="18" cy="6" r="3" />
            <circle cx="6" cy="18" r="3" />
            <path d="M18 9a9 9 0 01-9 9" />
          </svg>
          <span className="text-sm font-semibold text-white/90">Story Graph</span>
        </div>
        <p className="text-xs text-white/60 mb-2 leading-relaxed">
          {t('storyGraphLogOnly')}
        </p>
        <p className="text-[11px] text-white/40 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('storyGraphSpoilers', { tier: `<span class="text-amber-400/80 font-medium">${tierName || 'Premium'}</span>` }) }} />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl py-2 text-xs font-medium text-white/60 border border-white/10 hover:bg-white/5 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="flex-1 rounded-xl py-2 text-xs font-semibold text-white transition-colors"
            style={{ backgroundColor: 'rgb(99,102,241)' }}
          >
            {t('viewLog')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Contacts screen ─────────────────────────────────────────── */

function ContactsPane({ contacts, chatHistories, activeContactId, onSelect, onBack, choicesForContact, contactStatuses, emojiMap, onStoryGraph, typingContactId, t }) {
  return (
    <div className="flex h-full flex-col pt-6" style={{ background: 'linear-gradient(180deg, #282430 0%, #211e28 30%, #1c1a22 100%)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-white/5 px-3 py-3 shrink-0" style={{ backgroundColor: '#282430' }}>
        <button type="button" onClick={onBack} className="p-1 text-white/60 hover:text-white transition-colors">
          {Icons.back}
        </button>
        <span className="text-sm font-medium tracking-wide text-white/90 flex-1">{t('contacts')}</span>
        <button type="button" onClick={onStoryGraph} className="p-1.5 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors">
          {Icons.gitBranch}
        </button>
      </div>

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto scrollbar-none">
        {contacts.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <span className="text-xs text-white/30">No contacts yet</span>
          </div>
        ) : (
          contacts.map((c) => {
            const msgs = chatHistories[c.id] || [];
            const lastMsg = msgs[msgs.length - 1] || null;
            return (
              <ContactItem
                key={c.id}
                contact={c}
                lastMessage={lastMsg}
                hasChoices={!!choicesForContact?.(c.id)}
                active={c.id === activeContactId}
                onClick={() => onSelect(c.id)}
                statusEmoji={contactStatuses?.[c.id]}
                emojiMap={emojiMap}
                isTyping={typingContactId === c.id}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

/* ── Relationship floater ────────────────────────────────────── */

function RelationshipFloater({ value, onComplete }) {
  const isPositive = value > 0;
  const text = `Relationship ${isPositive ? '+' : ''}${value}`;
  const color = isPositive ? '#4ade80' : '#f87171';

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{
        opacity: [0, 1, 1, 0],
        y: [60, -10, -20, -140],
      }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 1.5,
        times: [0, 0.2, 0.53, 1],
        ease: ['easeOut', 'easeInOut', 'easeIn'],
      }}
      onAnimationComplete={onComplete}
      className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-50"
      style={{ bottom: '45%' }}
    >
      <div
        className="rounded-xl px-4 py-2 text-sm font-bold whitespace-nowrap"
        style={{ backgroundColor: 'rgba(0,0,0,0.85)', color }}
      >
        {text}
      </div>
    </motion.div>
  );
}

/* ── Chat screen ─────────────────────────────────────────────── */

function ChatPane({ contact, messages, choices, relationships, onChoose, typing, typingLabel, onBack, onPhotoViewerToggle, emojiMap, onIntroduceClick, statusEmoji, onStoryGraph, showCameraButton, onOpenCamera, wallpaper, speed, onCycleSpeed, t }) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const [viewerImage, setViewerImage] = useState(null);
  const [relFloater, setRelFloater] = useState(null);

  // Status: "last seen recently" by default, "online" after incoming message
  const [status, setStatus] = useState('last seen recently');
  useEffect(() => { setStatus('last seen recently'); }, [contact?.id]);
  useEffect(() => {
    if (typing) return; // typing indicator shown separately, keep current status
    if (messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last && !last.isUser && !last.timeskipSeparator) setStatus('online');
    }
  }, [typing, messages]);

  const handleChooseWithFloater = useCallback((i) => {
    const val = relationships?.[i] || 0;
    if (val !== 0) {
      setRelFloater({ value: val, key: Date.now() });
      playGameSound(val > 0 ? 'relationPositive' : 'relationNegative');
    }
    onChoose(i);
  }, [onChoose, relationships]);

  useEffect(() => {
    onPhotoViewerToggle?.(!!viewerImage);
  }, [viewerImage, onPhotoViewerToggle]);

  // Initial / contact-switch snap. Always pin to bottom and reset the
  // sticky flag — entering a chat means the user wants to see the latest.
  // The layout often isn't fully settled on the first commit (framer-motion
  // measuring `height: auto` on incoming bubbles, image decode, screen
  // transition reflow), so we re-pin in a couple of trailing RAFs to catch
  // the final scrollHeight before the user notices.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTop = el.scrollHeight;
    let raf1 = 0;
    let raf2 = 0;
    let raf3 = 0;
    raf1 = requestAnimationFrame(() => {
      const r = scrollRef.current;
      if (r) r.scrollTop = r.scrollHeight;
      raf2 = requestAnimationFrame(() => {
        const r2 = scrollRef.current;
        if (r2) r2.scrollTop = r2.scrollHeight;
        raf3 = requestAnimationFrame(() => {
          const r3 = scrollRef.current;
          if (r3) r3.scrollTop = r3.scrollHeight;
        });
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (raf3) cancelAnimationFrame(raf3);
    };
  }, [contact?.id]);

  // Track whether the user is at the bottom; once they scroll up to read
  // history we stop auto-pinning so they don't get yanked back down. Resumes
  // automatically when they scroll back to the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = dist < 24;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Pin scroll to bottom continuously while content height changes — this
  // covers the bubble's height: 0 → auto reveal animation, the typing
  // indicator showing/hiding, and the choices panel mounting/unmounting.
  // We observe both the inner content (bubble growth) and the scroll
  // viewport itself (its clientHeight shrinks when the bottom choice panel
  // mounts, which doesn't otherwise notify us).
  useEffect(() => {
    const sc = scrollRef.current;
    const co = contentRef.current;
    if (!sc || !co || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) sc.scrollTop = sc.scrollHeight;
    });
    ro.observe(co);
    ro.observe(sc);
    return () => ro.disconnect();
  }, []);

  // When the answer panel appears, force a scroll-to-bottom and re-arm the
  // sticky flag. The panel mount shrinks the messages viewport without
  // touching contentRef, and depending on the user's prior scroll position
  // the autoscroll occasionally detaches. Choices appearing means it's the
  // player's turn — always pull them to the bottom so they see both the
  // last message and the new options.
  const hadChoicesRef = useRef(false);
  useLayoutEffect(() => {
    const hasChoices = !!(choices && choices.length > 0);
    if (hasChoices && !hadChoicesRef.current) {
      const sc = scrollRef.current;
      if (sc) {
        stickToBottomRef.current = true;
        sc.scrollTop = sc.scrollHeight;
      }
    }
    hadChoicesRef.current = hasChoices;
  }, [choices]);

  if (!contact) {
    return (
      <div className="flex h-full items-center justify-center" style={{ backgroundColor: '#211e28' }}>
        <p className="text-sm text-white/30">Select a chat to start</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col pt-6 relative" style={{ background: 'linear-gradient(180deg, #262230 0%, #211e28 20%, #1c1a22 100%)' }}>
      {/* Chat header */}
      <div className="flex items-center gap-3 border-b border-white/5 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#282430' }}>
        <button type="button" onClick={onBack} className="flex items-center justify-center rounded p-1 text-white/60 hover:text-white transition-colors mr-1">{Icons.back}</button>
        {contact.avatar ? (
          <img src={contact.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white" style={{ backgroundColor: avatarColor(contact.name) }}>
            {avatarInitials(contact.name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white/90 truncate flex items-center gap-1">
            {contact.name}
            {statusEmoji && <StatusEmojiInline emoji={statusEmoji} emojiMap={emojiMap} />}
          </div>
          <div className="text-[11px] text-white/40">{typing ? <TypingText label={typingLabel} /> : status}</div>
        </div>
        <button
          type="button"
          onClick={onCycleSpeed}
          aria-label="Dialogue speed"
          className="min-w-[2rem] px-1.5 py-1 rounded-lg text-[11px] font-semibold tabular-nums text-white/55 hover:text-white/85 hover:bg-white/5 transition-colors"
        >
          {speed || '1x'}
        </button>
        <button type="button" onClick={onStoryGraph} className="p-1.5 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors">
          {Icons.gitBranch}
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-none">
        <div ref={contentRef} className="py-3 space-y-2">
          {messages.length === 0 && !typing && (
            <div className="flex flex-col items-center justify-center h-full gap-2 px-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.05]">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-6 w-6 text-white/20">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </svg>
              </div>
              <p className="text-xs text-white/25 text-center">No messages yet</p>
            </div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              msg.timeskipSeparator
                ? <TimeskipSeparator key={`ts-${msg._key}`} label={msg.label} />
                : <ChatBubble key={msg._key} message={msg} isUser={msg.isUser} onImageClick={(src) => setViewerImage(src)} emojiMap={emojiMap} onIntroduceClick={onIntroduceClick} />
            ))}
          </AnimatePresence>
          <div className="flex justify-start px-3" style={{ visibility: typing ? 'visible' : 'hidden' }}>
            <div className="rounded-2xl rounded-bl-md px-3.5 py-2.5" style={{ backgroundColor: '#3a3342' }}><TypingDots /></div>
          </div>
        </div>
      </div>

      {showCameraButton && !typing ? (
        <div className="shrink-0 px-3 py-3 flex justify-end" style={{ backgroundColor: '#272331' }}>
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            type="button"
            onClick={onOpenCamera}
            className="flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-semibold text-white hover:brightness-125 active:scale-[0.96] transition-all shadow-lg"
            style={{ background: 'linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)', borderBottomRightRadius: '4px' }}
          >
            <motion.span
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff', display: 'inline-block', flexShrink: 0 }}
            />
            Open Camera
          </motion.button>
        </div>
      ) : choices && choices.length > 0 && !typing ? (
        <ChoiceButtons choices={choices} onChoose={handleChooseWithFloater} />
      ) : (
        <div className="shrink-0 px-3 py-3" style={{ backgroundColor: '#211e28' }} />
      )}

      {/* Relationship floater */}
      <AnimatePresence>
        {relFloater && (
          <RelationshipFloater
            key={relFloater.key}
            value={relFloater.value}
            onComplete={() => setRelFloater(null)}
          />
        )}
      </AnimatePresence>

      {/* Single-image viewer */}
      <AnimatePresence>
        {viewerImage && (
          <PhotoViewer photos={[viewerImage]} initialIndex={0} onClose={() => setViewerImage(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Screen transition variants ──────────────────────────────── */

const screenVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
  }),
  center: {
    x: 0,
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
  }),
};

// Hint every screen wrapper into its own composite layer — fixes Samsung
// WebView (S21+) stutter where full-viewport absolute screens caused a full
// repaint per animation frame instead of a GPU composite. `will-change`
// forces layer promotion; `contain: layout paint` isolates the subtree so
// re-renders inside one screen don't invalidate the rest. `backfaceVisibility`
// is a legacy but still effective additional nudge for Android WebView.
const SCREEN_LAYER_STYLE = {
  willChange: 'transform, opacity',
  contain: 'layout paint',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden'
};

/* ── Backstory processor (chapter 0) ─────────────────────────── */

/**
 * Synchronously walk chapter 0 graph to extract initial game state.
 * No animation delays — just extracts data.
 */
function getLocalizedText(node, lang) {
  if (lang === 'en' && node.data?.text_en) return node.data.text_en;
  return node.data?.text || '';
}

function getLocalizedVariants(node, lang) {
  const ru = node.data?.variants || [];
  const en = node.data?.variants_en || [];
  if (lang === 'en' && en.length) {
    // Per-variant fallback: use EN if non-empty, otherwise fall back to RU
    return en.map((v, i) => v || ru[i] || '');
  }
  return ru;
}

/* eslint-disable no-loop-func -- all inner callbacks (forEach/find) are
   synchronous within the same loop iteration; they never outlive the
   iteration that spawned them, so capturing loop-local vars is safe. */
function processBackstory(chapter0, allStoryflows, characters, lang) {
  if (!chapter0?.graph?.nodes?.length) {
    return { chatHistories: {}, unlockedContacts: {}, galleryUnlocks: [], contactStatuses: {}, gameTime: { hours: 14, minutes: 0, day: 0 }, decisions: {}, nodeHistory: [], relationships: {}, nextStoryflowId: null, nextNodeId: null };
  }

  const nodes = chapter0.graph.nodes;
  const edges = chapter0.graph.edges || [];
  const chatHistories = {};
  const unlockedContacts = {};
  const galleryUnlocks = [];
  const instaPosts = [];
  const contactStatuses = {};
  const decisions = {};
  const nodeHistory = [];
  const relationships = {};
  let gameTime = { hours: 14, minutes: 0, day: 0 };
  let lastSpeakerId = null;
  let nextStoryflowId = null;
  let nextNodeId = null;

  const findNode = (id) => nodes.find((n) => n.id === id);
  const followFlowOut = (fromId) => {
    const edge = edges.find((e) => e.source === fromId && (e.sourceHandle === 'flow-out' || !e.sourceHandle));
    return edge ? edge.target : null;
  };

  // Find start node
  const startNode = nodes.find((n) => n.type === 'sfStart');
  if (!startNode) return { chatHistories, unlockedContacts, galleryUnlocks, instaPosts, contactStatuses, gameTime, decisions, nodeHistory, relationships, nextStoryflowId, nextNodeId };

  const visited = new Set();
  let currentId = startNode.id;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    nodeHistory.push(currentId);
    const node = findNode(currentId);
    if (!node) break;

    if (node.type === 'sfStart') {
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfDialogue') {
      const speakerId = node.data?.speakerId;
      if (speakerId) {
        lastSpeakerId = speakerId;
        unlockedContacts[speakerId] = true;
      }
      const contactId = speakerId || lastSpeakerId;
      if (contactId) {
        const rawText = getLocalizedText(node, lang);
        const messages = rawText.split('\n').filter(Boolean);
        if (!chatHistories[contactId]) chatHistories[contactId] = [];
        messages.forEach((msg) => {
          chatHistories[contactId].push({
            text: msg, isUser: false, time: formatGameTimeStr(gameTime),
            speakerName: node.data?.speakerName
          });
        });
      }
      const variants = getLocalizedVariants(node, lang).filter(Boolean);
      if (variants.length > 0) {
        // Auto-pick first option for backstory
        if (contactId && chatHistories[contactId]) {
          chatHistories[contactId].push({ text: variants[0], isUser: true, time: formatGameTimeStr(gameTime) });
        }
        const edge = edges.find((e) => e.source === currentId && e.sourceHandle === 'choice-0');
        currentId = edge ? edge.target : followFlowOut(currentId);
      } else {
        currentId = followFlowOut(currentId);
      }
    } else if (node.type === 'sfImage') {
      const contactId = node.data?.characterId || lastSpeakerId;
      if (contactId) {
        if (!chatHistories[contactId]) chatHistories[contactId] = [];
        chatHistories[contactId].push({
          image: node.data?.imageUrl || node.data?.imagePreview512,
          isUser: !!node.data?.playerSends,
          time: formatGameTimeStr(gameTime)
        });
      }
      galleryUnlocks.push({
        storyflowId: chapter0.id,
        nodeId: node.id,
        imageUrl: node.data?.imageUrl || node.data?.imagePreview512,
        imagePreview512: node.data?.imagePreview512,
        playerSends: !!node.data?.playerSends,
        unlockedAt: Date.now() - (10000 - galleryUnlocks.length) // ensure ordering
      });
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfVideo') {
      // Videos don't go to gallery; auto-follow flow-out
      const variants = getLocalizedVariants(node, lang).filter(Boolean);
      if (variants.length > 0) {
        const edge = edges.find((e) => e.source === currentId && e.sourceHandle === 'choice-0');
        currentId = edge ? edge.target : followFlowOut(currentId);
      } else {
        currentId = followFlowOut(currentId);
      }
    } else if (node.type === 'sfDecision') {
      const opts = node.data?.options || [];
      // Find the edge we actually traversed by looking at the previous node
      // in history — edges.find by target alone picks an arbitrary match.
      const prevId = nodeHistory[nodeHistory.length - 2];
      const inEdge = edges.find(
        (e) => e.source === prevId && e.target === currentId &&
               e.targetHandle && e.targetHandle.startsWith('decision-in-')
      );
      const inIdx = inEdge ? parseInt(inEdge.targetHandle.split('-').pop(), 10) || 0 : 0;
      const chosenOption = opts[inIdx] || opts[0] || 'True';
      decisions[node.data?.decisionName || node.id] = chosenOption;
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === `decision-out-${inIdx}`);
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfBranch') {
      const ref = node.data?.decisionRef;
      const stored = ref ? decisions[ref] : null;
      const switchOpts = node.data?.switchOptions || [];
      let matchIdx = stored ? switchOpts.indexOf(stored) : -1;
      // If no decision stored, try "False" output
      if (matchIdx < 0) {
        const falseIdx = switchOpts.indexOf('False');
        matchIdx = falseIdx >= 0 ? falseIdx : 0;
      }
      const handle = `switch-out-${matchIdx}`;
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === handle);
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfTimeskip') {
      const mode = node.data?.timeskipMode || 'skip';
      const h = node.data?.hours || 0;
      const m = node.data?.minutes || 0;
      gameTime = applyTimeskip(gameTime, mode, h, m);
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfIntroduce') {
      const charId = node.data?.characterId;
      if (charId) unlockedContacts[charId] = true;
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfStatus') {
      const charId = node.data?.characterId || lastSpeakerId;
      const emoji = node.data?.statusEmoji || '';
      if (charId) contactStatuses[charId] = emoji;
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfInsta') {
      // During backstory fast-forward, just add the post silently
      const charId = node.data?.characterId;
      const imageUrl = node.data?.imageUrl || node.data?.imagePreview512;
      if (charId && imageUrl) {
        instaPosts.push({
          id: `ipost-node-${currentId}`,
          characterId: charId,
          accountId: '',
          section: node.data?.instaSection || 'public',
          imageUrl,
          imagePreview512: node.data?.imagePreview512 || imageUrl,
          caption: node.data?.caption || '',
          likes: node.data?.likes || 0,
          createdAt: new Date().toISOString()
        });
      }
      currentId = followFlowOut(currentId);
    } else if (node.type === 'sfRpSwitch') {
      // Auto-pick "Enough" path for backstory
      const edge = edges.find((e) => e.source === currentId && e.sourceHandle === 'rp-out-pass');
      currentId = edge ? edge.target : followFlowOut(currentId);
    } else if (node.type === 'sfNextChapter') {
      // Find target chapter
      const targetName = node.data?.targetChapterName || node.data?.chapterName;
      if (targetName && allStoryflows) {
        const target = allStoryflows.find((s) => s.name === targetName);
        if (target) {
          nextStoryflowId = target.id;
          const startN = (target.graph?.nodes || []).find((n) => n.type === 'sfStart');
          nextNodeId = startN?.id || null;
        }
      }
      currentId = null; // terminal
    } else {
      currentId = followFlowOut(currentId);
    }
  }

  return { chatHistories, unlockedContacts, galleryUnlocks, instaPosts, contactStatuses, gameTime, decisions, nodeHistory, relationships, nextStoryflowId, nextNodeId };
}
/* eslint-enable no-loop-func */

/**
 * Validate gallery unlocks against current storyflow data.
 * If a node's image changed, update URL. If node deleted, remove unlock.
 */
function validateGalleryUnlocks(unlocks, allStoryflows) {
  if (!unlocks?.length || !allStoryflows?.length) return [];
  return unlocks.map((u) => {
    const sf = allStoryflows.find((s) => s.id === u.storyflowId);
    if (!sf) return null;
    const node = (sf.graph?.nodes || []).find((n) => n.id === u.nodeId);
    if (!node) return null; // node deleted
    // Update URLs if changed
    return {
      ...u,
      imageUrl: node.data?.imageUrl || node.data?.imagePreview512 || u.imageUrl,
      imagePreview512: node.data?.imagePreview512 || u.imagePreview512
    };
  }).filter(Boolean);
}

/**
 * Collect all image nodes from all storyflows (for Full Gallery mode).
 */
function collectAllImages(allStoryflows) {
  const images = [];
  for (const sf of (allStoryflows || [])) {
    for (const node of (sf.graph?.nodes || [])) {
      if (node.type === 'sfImage' && (node.data?.imageUrl || node.data?.imagePreview512)) {
        images.push({
          storyflowId: sf.id,
          nodeId: node.id,
          imageUrl: node.data?.imageUrl || node.data?.imagePreview512,
          imagePreview512: node.data?.imagePreview512,
          playerSends: !!node.data?.playerSends,
          chapterName: sf.name,
          chapterNumber: sf.number
        });
      }
    }
  }
  return images;
}

/* ── Main component ──────────────────────────────────────────── */

export default function ChatVisualNovel({
  teamId,
  entryStoryflow,
  entryNode,
  allStoryflows,
  characters,
  sounds,
  musicTracks,
  patreonUrl,
  itchUrl,
  patreonTiers,
  authEndpoint,
  promoEndpoint,
  promoInfoEndpoint,
  fullGalleryTier,
  fullStoryLogTier,
  instaAppName,
  instaPrivateTier,
  instaStoriesMin,
  instaStoriesMax,
  instaAccounts: instaAccountsProp,
  initialDecisions
}) {
  // For backward compat: single storyflowData from old prop
  const storyflowData = useMemo(() => {
    if (entryStoryflow && allStoryflows?.length) {
      return allStoryflows.find((s) => s.id === entryStoryflow) || null;
    }
    return null;
  }, [entryStoryflow, allStoryflows]);
  // Build contacts from characters, fall back to test contacts
  const allContacts = useMemo(() => {
    const fromChars = (characters || []).map((c) => ({
      id: c.id || c.characterId,
      name: c.name,
      avatar: c.avatar?.url64 || c.avatar?.url128 || null,
      description: c.description || ''
    }));
    return fromChars.length > 0 ? fromChars : TEST_CONTACTS;
  }, [characters]);

  const isTestMode = !characters || characters.length === 0;

  const emojiMap = useEmojiMap();

  // User preferences — persisted separately from game saves
  const prefsKey = teamId ? `flowcore_prefs_${teamId}` : null;
  const loadPrefs = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(prefsKey) || '{}'); } catch { return {}; }
  }, [prefsKey]);
  const savePrefs = useCallback((patch) => {
    if (!prefsKey) return;
    const cur = (() => { try { return JSON.parse(localStorage.getItem(prefsKey) || '{}'); } catch { return {}; } })();
    localStorage.setItem(prefsKey, JSON.stringify({ ...cur, ...patch }));
  }, [prefsKey]);

  const [vibration, setVibration] = useState(() => loadPrefs().vibration ?? true);
  const [sound, setSound] = useState(() => loadPrefs().sound ?? true);
  const [speed, setSpeed] = useState(() => loadPrefs().speed || '1x');
  const speedRef = useRef('1x');
  useEffect(() => { speedRef.current = speed; savePrefs({ speed }); }, [speed, savePrefs]);
  const cycleSpeed = useCallback(() => {
    vibrateTap();
    setSpeed((prev) => (prev === '1x' ? '2x' : prev === '2x' ? '3x' : '1x'));
  }, []);
  useEffect(() => { vibrationRef.current = vibration; savePrefs({ vibration }); }, [vibration, savePrefs]);
  useEffect(() => { soundEnabledRef.current = sound; savePrefs({ sound }); }, [sound, savePrefs]);
  useEffect(() => { gameSoundsRef.current = sounds || null; }, [sounds]);

  // ── Language / i18n ──
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem('flowcore_game_lang') || 'auto'; } catch { return 'auto'; }
  });
  useEffect(() => {
    try { localStorage.setItem('flowcore_game_lang', language); } catch {}
  }, [language]);
  const resolvedLang = useMemo(() => resolveLanguage(language), [language]);
  const t = useMemo(() => createT(resolvedLang), [resolvedLang]);
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);
  const resolvedLangRef = useRef(resolvedLang);
  useEffect(() => { resolvedLangRef.current = resolvedLang; }, [resolvedLang]);

  // Initialize Sexify player tracks
  useEffect(() => { sexifyPlayer.setTracks(musicTracks || []); }, [musicTracks]);
  const sexifyState = useSexifyPlayer();

  // ── Storyflow lookups ──
  const storyflowsById = useMemo(() => {
    const map = {};
    for (const sf of (allStoryflows || [])) map[sf.id] = sf;
    return map;
  }, [allStoryflows]);

  const storyflowsByName = useMemo(() => {
    const map = {};
    for (const sf of (allStoryflows || [])) map[sf.name] = sf;
    return map;
  }, [allStoryflows]);

  // ── Backstory initial state ──
  const backstoryState = useMemo(() => {
    if (isTestMode || !allStoryflows?.length) return null;
    const chapter0 = allStoryflows.find((s) => s.number === 0);
    if (!chapter0) return null;
    return processBackstory(chapter0, allStoryflows, characters, resolvedLang);
  }, [isTestMode, allStoryflows, characters, resolvedLang]);

  const [screen, setScreen] = useState('boot');
  const screenRef = useRef('boot');
  const [navDirection, setNavDirection] = useState(1);
  const [activeContactId, setActiveContactId] = useState(null);
  const activeContactIdRef = useRef(null);
  useEffect(() => { screenRef.current = screen; }, [screen]);
  useEffect(() => { activeContactIdRef.current = activeContactId; }, [activeContactId]);
  const [chatHistories, setChatHistories] = useState(() => {
    if (isTestMode) return TEST_CHAT_HISTORIES;
    return {};
  });
  // Ref mirror so effects can read current chatHistories without
  // depending on it (which would cancel/restart running timers).
  const chatHistoriesRef = useRef(chatHistories);
  useEffect(() => { chatHistoriesRef.current = chatHistories; }, [chatHistories]);
  const [currentChoices, setCurrentChoices] = useState(null);
  const [testChoicesShown, setTestChoicesShown] = useState({});
  const [typing, setTyping] = useState(false);
  const [typingLabel, setTypingLabel] = useState(null);
  const [typingContactId, setTypingContactId] = useState(null);
  const [currentNodeId, setCurrentNodeId] = useState(entryNode || '');
  const [currentStoryflowId, setCurrentStoryflowId] = useState(entryStoryflow || '');
  const [placeholderAppId, setPlaceholderAppId] = useState(null);
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [testMsgIndex, setTestMsgIndex] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [activeNotification, setActiveNotification] = useState(null);
  const [contactStatuses, setContactStatuses] = useState({});
  const [unlockedContacts, setUnlockedContacts] = useState(() =>
    isTestMode ? { _test_alex: true, _test_sarah: true } : {}
  );
  const [gameTime, setGameTime] = useState({ hours: 14, minutes: 0, day: 0 });
  useEffect(() => { gameTimeRef.current = gameTime; }, [gameTime]);
  const timeskipAccumRef = useRef({}); // { [contactId]: totalMinutes }
  const [showClock, setShowClock] = useState(() => loadPrefs().showClock ?? true);
  useEffect(() => { savePrefs({ showClock }); }, [showClock, savePrefs]);
  const [coversUnlocked, setCoversUnlocked] = useState(false);

  const [showChapter, setShowChapter] = useState(false);
  const [chapterDismissed, setChapterDismissed] = useState(false);
  // Separate end-of-content flag so the chapter-start overlay's dismiss
  // (chapterDismissed) doesn't accidentally trigger the results flow.
  const [endOfContentPending, setEndOfContentPending] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonDismissed, setComingSoonDismissed] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showStoryGraphGate, setShowStoryGraphGate] = useState(false);
  const [showStoryGraph, setShowStoryGraph] = useState(false);
  const [videoNodePending, setVideoNodePending] = useState(null); // { nodeId, contactId }
  const [videoOverlayNodeId, setVideoOverlayNodeId] = useState(null);
  const videoTestTriggered = useRef(false);
  const notifTimerRef = useRef(null);
  const testTypingTimerRef = useRef(null);
  const playStartRef = useRef(Date.now());
  const autoSaveTimerRef = useRef(null);
  const [currentChapter, setCurrentChapter] = useState('');
  const testMessagesDone = testMsgIndex >= TEST_NOTIFICATION_MESSAGES.length;

  // ── New state for real data ──
  const [decisions, setDecisions] = useState(() => initialDecisions || {});
  const [nodeHistory, setNodeHistory] = useState([]);
  const [galleryUnlocks, setGalleryUnlocks] = useState([]);
  const [relationships, setRelationships] = useState({});
  const [unlockedChapters, setUnlockedChapters] = useState([]);
  const [wallpaper, setWallpaper] = useState(null); // { storyflowId, nodeId, imageUrl } or null
  const [instaPosts, setInstaPosts] = useState([]); // posts added dynamically by sfInsta node
  const [viewedStories, setViewedStories] = useState({}); // { [accountId]: [postId, ...] }
  const lastSpeakerIdRef = useRef(null);
  const [playerTier, setPlayerTier] = useState(() => {
    const urlTier = new URLSearchParams(window.location.search).get('testTier');
    return urlTier || getCurrentTier(patreonTiers || []);
  });
  const [backstoryApplied, setBackstoryApplied] = useState(false);
  const [autoLoadDone, setAutoLoadDone] = useState(false);
  const autoLoadDoneRef = useRef(false);

  // Restore-progress quiz overlay (launched from the Saves screen).
  const [showRestoreQuiz, setShowRestoreQuiz] = useState(false);
  const [restoreBuilding, setRestoreBuilding] = useState(false);

  // ── Auto-load current progress on first launch ──
  useEffect(() => {
    if (isTestMode || autoLoadDone) return;
    if (!teamId || entryNode) { setAutoLoadDone(true); autoLoadDoneRef.current = true; return; }

    const doAutoLoad = async () => {
      let save = null;
      if (isStandalone()) {
        const all = listSaves(teamId);
        save = all.find((s) => s.saveType === 'current_progress');
      } else {
        const token = sessionStorage.getItem('flowcore_session_token') || '';
        const r = await fetch(`${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}&saveType=current_progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          save = (data?.saves || []).find((s) => s.saveType === 'current_progress');
        }
      }
      return save;
    };

    doAutoLoad().then((save) => {
      if (save?.state?.currentNodeId) {
        const s = save.state;
        if (s.chatHistories) setChatHistories(s.chatHistories);
        if (s.currentNodeId) setCurrentNodeId(s.currentNodeId);
        if (s.currentStoryflowId) setCurrentStoryflowId(s.currentStoryflowId);
        if (s.gameTime) { setGameTime(s.gameTime); gameTimeRef.current = s.gameTime; }
        if (s.activeContactId) { setActiveContactId(s.activeContactId); activeContactIdRef.current = s.activeContactId; }
        if (s.unlockedContacts) setUnlockedContacts(s.unlockedContacts);
        if (s.contactStatuses) setContactStatuses(s.contactStatuses);
        if (s.currentChapter) setCurrentChapter(s.currentChapter);
        if (s.decisions) setDecisions(s.decisions);
        if (s.nodeHistory) setNodeHistory(s.nodeHistory);
        if (s.galleryUnlocks) setGalleryUnlocks(validateGalleryUnlocks(s.galleryUnlocks, allStoryflows));
        if (s.relationships) setRelationships(s.relationships);
        if (s.unlockedChapters) setUnlockedChapters(s.unlockedChapters);
        if (s.instaPosts) setInstaPosts(s.instaPosts);
        if (s.viewedStories) setViewedStories(s.viewedStories);
        setWallpaper(s.wallpaper || null);
        if (s.currentChoices) setCurrentChoices(s.currentChoices);
        // Mark the saved node as processed so we don't replay it on boot.
        processedNodesRef.current = new Set([s.currentNodeId]);
        storyflowStarted.current = true;
        setBackstoryApplied(true);
      }
      setAutoLoadDone(true); autoLoadDoneRef.current = true;
    }).catch(() => { setAutoLoadDone(true); autoLoadDoneRef.current = true; });
  }, [isTestMode, entryNode, autoLoadDone, teamId, allStoryflows]);

  // ── Apply backstory on first load, then auto-load overwrites if save exists ──
  useEffect(() => {
    if (isTestMode || entryNode || backstoryApplied || !backstoryState) return;
    setChatHistories(backstoryState.chatHistories);
    setUnlockedContacts(backstoryState.unlockedContacts);
    setGalleryUnlocks(validateGalleryUnlocks(backstoryState.galleryUnlocks, allStoryflows));
    if (backstoryState.instaPosts) setInstaPosts(backstoryState.instaPosts);
    setContactStatuses(backstoryState.contactStatuses);
    setGameTime(backstoryState.gameTime);
    gameTimeRef.current = backstoryState.gameTime;
    setDecisions(backstoryState.decisions);
    setNodeHistory(backstoryState.nodeHistory);
    if (backstoryState.nextStoryflowId) {
      setCurrentStoryflowId(backstoryState.nextStoryflowId);
      if (backstoryState.nextNodeId) setCurrentNodeId(backstoryState.nextNodeId);
      const nextSf = storyflowsById[backstoryState.nextStoryflowId];
      if (nextSf) {
        setCurrentChapter(nextSf.name);
        setUnlockedChapters([backstoryState.nextStoryflowId]);
      }
    }
    setBackstoryApplied(true);
  }, [isTestMode, entryNode, backstoryApplied, backstoryState, allStoryflows, storyflowsById]);

  // Effective storyflow — dynamic based on currentStoryflowId
  const effectiveStoryflowData = useMemo(() => {
    if (currentStoryflowId && storyflowsById[currentStoryflowId]) {
      return storyflowsById[currentStoryflowId];
    }
    return storyflowData || (isTestMode ? TEST_VIDEO_STORYFLOW : null);
  }, [currentStoryflowId, storyflowsById, storyflowData, isTestMode]);

  // Covers unlocked — current progress or any save with chapter > 10
  useEffect(() => {
    if ((effectiveStoryflowData?.number ?? 0) > 10) { setCoversUnlocked(true); return; }
    if (!teamId) return;
    if (isStandalone()) {
      const all = listSaves(teamId);
      for (const save of all) {
        if ((save.currentChapterNumber ?? 0) > 10) { setCoversUnlocked(true); return; }
      }
    } else {
      const token = sessionStorage.getItem('flowcore_session_token') || '';
      fetch(`${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then((r) => r.ok ? r.json() : null).then((data) => {
        for (const save of (data?.saves || [])) {
          if ((save.currentChapterNumber ?? 0) > 10) { setCoversUnlocked(true); return; }
        }
      }).catch(() => {});
    }
  }, [teamId, effectiveStoryflowData?.number]);

  // ── Game state snapshot for saving ──
  const getGameState = useCallback(() => ({
    currentStoryflowId,
    currentNodeId,
    currentChapter,
    nodeHistory,
    decisions,
    chatHistories,
    unlockedContacts,
    contactStatuses,
    relationships,
    galleryUnlocks,
    wallpaper,
    gameTime,
    unlockedChapters,
    activeContactId,
    testMsgIndex,
    instaPosts,
    viewedStories,
    currentChoices
  }), [currentStoryflowId, currentNodeId, currentChapter, nodeHistory, decisions, chatHistories, unlockedContacts, contactStatuses, relationships, galleryUnlocks, wallpaper, gameTime, unlockedChapters, activeContactId, testMsgIndex, instaPosts, viewedStories, currentChoices]);

  const getPlayTimeSeconds = useCallback(() => Math.floor((Date.now() - playStartRef.current) / 1000), []);

  // ── Auto-save current progress ──
  const autoSaveProgress = useCallback(() => {
    if (!teamId) return;
    const state = getGameState();
    const playTime = getPlayTimeSeconds();
    const saveData = {
      teamId,
      slot: 100, // reserved slot for current_progress
      saveType: 'current_progress',
      label: t('currentProgress'),
      gameTemplate: 'ChatVisualNovel',
      state,
      currentStoryflowId: currentStoryflowId || '',
      currentNodeId: currentNodeId || '',
      currentChapter: currentChapter || '',
      currentChapterNumber: effectiveStoryflowData?.number ?? 0,
      playTime,
      gameTime: formatGameTimeStr(gameTime)
    };
    if (isStandalone()) {
      putSave(teamId, saveData);
    } else {
      const token = sessionStorage.getItem('flowcore_session_token') || '';
      fetch(`${API_BASE_URL}/api/game-saves`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(saveData)
      }).catch(() => {});
    }
  }, [teamId, getGameState, getPlayTimeSeconds, currentChapter, currentStoryflowId, currentNodeId, gameTime, effectiveStoryflowData?.number, t]);

  // Auto-save periodically (every 30 seconds)
  useEffect(() => {
    if (!teamId || screen === 'boot' || entryNode) return;
    autoSaveTimerRef.current = setInterval(autoSaveProgress, 30000);
    return () => { if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current); };
  }, [teamId, autoSaveProgress, screen, entryNode]);

  // ── Load game state from a save ──
  const handleLoadSave = useCallback((state) => {
    if (!state) return;
    if (state.chatHistories) setChatHistories(state.chatHistories);
    if (state.currentNodeId) setCurrentNodeId(state.currentNodeId);
    if (state.currentStoryflowId) setCurrentStoryflowId(state.currentStoryflowId);
    if (state.gameTime) { setGameTime(state.gameTime); gameTimeRef.current = state.gameTime; }
    if (state.activeContactId) setActiveContactId(state.activeContactId);
    if (state.unlockedContacts) setUnlockedContacts(state.unlockedContacts);
    if (state.contactStatuses) setContactStatuses(state.contactStatuses);
    if (state.currentChapter) setCurrentChapter(state.currentChapter);
    if (state.decisions) setDecisions(state.decisions);
    if (state.nodeHistory) setNodeHistory(state.nodeHistory);
    if (state.galleryUnlocks) setGalleryUnlocks(validateGalleryUnlocks(state.galleryUnlocks, allStoryflows));
    if (state.relationships) setRelationships(state.relationships);
    if (state.unlockedChapters) setUnlockedChapters(state.unlockedChapters);
    setWallpaper(state.wallpaper || null);
    if (state.instaPosts) setInstaPosts(state.instaPosts);
    if (state.viewedStories) setViewedStories(state.viewedStories);
    // Restore pending choices if the save was taken while the player was
    // looking at answer variants — otherwise the chat appears frozen.
    setCurrentChoices(state.currentChoices || null);
    setTyping(false);
    setTypingContactId(null);
    setComingSoonDismissed(false);
    // Clear any lingering end-of-content state so loading a save mid-game
    // doesn't auto-trigger results/coming-soon a second time.
    setShowChapter(false);
    setChapterDismissed(false);
    setEndOfContentPending(false);
    setShowResults(false);
    setShowComingSoon(false);
    lastSpeakerIdRef.current = null;
    // Restore flag: save was built from the quiz and needs the game to
    // actually process the target chapter's sfStart (so the first
    // dialogue fires) and to show the chapter overlay. For normal
    // saves, we mark the current node as processed to avoid replaying
    // it on top of the loaded history.
    if (state.__restore) {
      processedNodesRef.current = new Set();
      setShowChapter(true);
    } else if (state.currentNodeId) {
      processedNodesRef.current = new Set([state.currentNodeId]);
    }
    storyflowStarted.current = true;
    setNavDirection(-1);
    setScreen('home');
  }, [allStoryflows]);

  // ── Restore-progress quiz completion ──
  const handleRestoreComplete = useCallback(({ targetChapterNumber, decisions: quizAnswers, lang }) => {
    setRestoreBuilding(true);
    const synthState = simulatePlaythrough({
      allStoryflows,
      quizAnswers: quizAnswers || {},
      targetChapterNumber: targetChapterNumber ?? 0,
      lang: lang || resolvedLangRef.current
    });
    // Marker for handleLoadSave: sfStart of the target chapter needs
    // to actually run, and the new-chapter overlay should fire.
    synthState.__restore = true;
    playStartRef.current = Date.now();
    handleLoadSave(synthState);
    setRestoreBuilding(false);
    setShowRestoreQuiz(false);
    // Persist the synthesized state so a refresh keeps the restore.
    setTimeout(autoSaveProgress, 200);
  }, [allStoryflows, autoSaveProgress, handleLoadSave]);

  // ── Reset all game state ──
  const storyflowStarted = useRef(false);
  const processedNodesRef = useRef(new Set());
  const handleResetProgress = useCallback(() => {
    // In Play From Here mode we never apply chapter-0 backstory — New Game
    // should cleanly restart the test from entryNode, not dump the player
    // into a chapter-1 prelude.
    if (!isTestMode && !entryNode && backstoryState) {
      // Re-apply backstory state
      setChatHistories(backstoryState.chatHistories);
      setUnlockedContacts(backstoryState.unlockedContacts);
      setGalleryUnlocks(validateGalleryUnlocks(backstoryState.galleryUnlocks, allStoryflows));
      if (backstoryState.instaPosts) setInstaPosts(backstoryState.instaPosts);
      setViewedStories({});
      setContactStatuses(backstoryState.contactStatuses);
      setGameTime(backstoryState.gameTime);
      gameTimeRef.current = backstoryState.gameTime;
      setDecisions(backstoryState.decisions);
      setNodeHistory(backstoryState.nodeHistory);
      setRelationships({});
      setWallpaper(null);
      if (backstoryState.nextStoryflowId) {
        setCurrentStoryflowId(backstoryState.nextStoryflowId);
        if (backstoryState.nextNodeId) setCurrentNodeId(backstoryState.nextNodeId);
        const nextSf = storyflowsById[backstoryState.nextStoryflowId];
        if (nextSf) {
          setCurrentChapter(nextSf.name);
          setUnlockedChapters([backstoryState.nextStoryflowId]);
        }
      } else {
        setCurrentStoryflowId('');
        setCurrentNodeId('');
        setCurrentChapter('');
        setUnlockedChapters([]);
      }
    } else {
      setChatHistories(isTestMode ? { ...TEST_CHAT_HISTORIES } : {});
      setCurrentNodeId(entryNode || '');
      setCurrentStoryflowId(entryStoryflow || '');
      setGameTime({ hours: 14, minutes: 0, day: 0 });
      gameTimeRef.current = { hours: 14, minutes: 0, day: 0 };
      setUnlockedContacts(isTestMode ? { _test_alex: true, _test_sarah: true } : {});
      setContactStatuses({});
      setDecisions({});
      setNodeHistory([]);
      setGalleryUnlocks([]);
      setRelationships({});
      setUnlockedChapters([]);
      setWallpaper(null);
      setCurrentChapter('');
    }
    setActiveContactId(null);
    setCurrentChoices(null);
    setTyping(false);
    setTypingContactId(null);
    setComingSoonDismissed(false);
    setShowChapter(false);
    setChapterDismissed(false);
    setEndOfContentPending(false);
    setShowResults(false);
    setShowComingSoon(false);
    lastSpeakerIdRef.current = null;
    storyflowStarted.current = false;
    processedNodesRef.current = new Set();
    playStartRef.current = Date.now();
    setNavDirection(-1);
    setScreen('home');
  }, [isTestMode, entryNode, entryStoryflow, backstoryState, allStoryflows, storyflowsById]);

  const handleStoryGraphClick = useCallback(() => {
    vibrateTap();
    setShowStoryGraphGate(true);
  }, []);

  const handleStoryGraphGateContinue = useCallback(() => {
    setShowStoryGraphGate(false);
    setShowStoryGraph(true);
  }, []);

  const handleStoryGraphClose = useCallback(() => {
    setShowStoryGraph(false);
  }, []);

  // Visible contacts — only show unlocked ones
  const contacts = useMemo(() => {
    return allContacts.filter((c) => unlockedContacts[c.id]);
  }, [allContacts, unlockedContacts]);

  const activeContact = useMemo(
    () => contacts.find((c) => c.id === activeContactId) || null,
    [contacts, activeContactId]
  );

  const navigateTo = useCallback((target, direction = 1) => {
    setNavDirection(direction);
    setScreen(target);
  }, []);

  const handleHomeAppClick = useCallback((appId) => {
    vibrateTap();
    const directScreens = ['messages', 'gallery', 'settings', 'saves', 'statistics', 'episodes', 'patreon', 'sexify', 'insta'];
    if (appId === 'itch') {
      openExternalUrl(itchUrl || 'https://sexting-department.itch.io/sexting-department');
      return;
    }
    if (directScreens.includes(appId)) {
      navigateTo(appId, 1);
    } else {
      setPlaceholderAppId(appId);
      navigateTo('placeholder', 1);
    }
  }, [navigateTo, itchUrl]);

  const handleExit = useCallback(() => {
    vibrateTap();
    setShowExitModal(true);
  }, []);

  const confirmExit = useCallback(async () => {
    setShowExitModal(false);
    try {
      // Capacitor (Android/iOS native). Use the global Plugins registry
      // instead of `import('@capacitor/app')` so webpack never tries to
      // resolve the module on web/desktop builds. The plugin is registered
      // on `window.Capacitor.Plugins.App` by the native runtime.
      const cap = typeof window !== 'undefined' ? window.Capacitor : null;
      if (cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) {
        const AppPlugin = cap.Plugins && cap.Plugins.App;
        if (AppPlugin && typeof AppPlugin.exitApp === 'function') {
          await AppPlugin.exitApp();
          return;
        }
      }
      // Tauri (Windows/macOS/Linux desktop) — uses withGlobalTauri + a custom
      // exit_app command registered in src-tauri/src/main.rs.
      const tauri = typeof window !== 'undefined' ? window.__TAURI__ : null;
      if (tauri?.core?.invoke) {
        await tauri.core.invoke('exit_app');
        return;
      }
      // Admin dashboard preview (iframe) — notify the parent so it can close
      // the preview for us.
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'game-exit' }, '*');
        return;
      }
      // Plain web fallback
      window.history.back();
    } catch (err) {
      console.warn('[confirmExit] platform exit failed:', err);
      window.history.back();
    }
  }, []);

  // Generation counter — incremented on rewind to cancel stale timeouts
  const processGenRef = useRef(0);

  // Process the current node in the storyflow graph.
  // `fromHandle` — targetHandle of the edge that led into this node, if known.
  // Used by sfDecision to know which decision-in-X it was actually entered via.
  const processNode = useCallback((nodeId, fromHandle = null) => {
    if (!effectiveStoryflowData?.graph?.nodes) return;
    // Prevent processing the same node multiple times
    if (processedNodesRef.current.has(nodeId)) return;
    processedNodesRef.current.add(nodeId);
    const node = effectiveStoryflowData.graph.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    // Capture generation to detect stale callbacks after rewind
    const gen = processGenRef.current;
    const isStale = () => gen !== processGenRef.current;

    // Track node in history
    setNodeHistory((prev) => [...prev, nodeId]);
    // Reset coming soon on progress
    setComingSoonDismissed(false);

    const followFlowOut = (fromNodeId) => {
      if (!effectiveStoryflowData?.graph?.edges) {
        // No edges at all → end of content: trigger results → coming soon flow
        if (!endOfContentPending) setEndOfContentPending(true);
        return;
      }
      const edge = effectiveStoryflowData.graph.edges.find(
        (e) => e.source === fromNodeId && (e.sourceHandle === 'flow-out' || !e.sourceHandle)
      );
      if (edge) {
        setCurrentNodeId(edge.target);
        setTimeout(() => { if (!isStale()) processNode(edge.target, edge.targetHandle || null); }, 300);
      } else {
        // No outgoing edge → end of content: trigger results → coming soon flow
        if (!endOfContentPending) setEndOfContentPending(true);
      }
    };

    if (node.type === 'sfDialogue') {
      const speakerId = node.data?.speakerId;
      const curLang = resolvedLangRef.current;
      const rawText = getLocalizedText(node, curLang);
      const messages = rawText.split('\n').filter(Boolean);
      const variants = getLocalizedVariants(node, curLang).filter(Boolean);
      const hasVoice = !!(node.data?.audio && node.data?.audioUrl) && messages.length > 0;
      const voiceAudioUrl = node.data?.audioUrl;

      // Character binding
      if (speakerId) lastSpeakerIdRef.current = speakerId;
      const contactId = speakerId || lastSpeakerIdRef.current || activeContactId;

      // Auto-unlock contact if not yet in contacts
      if (contactId) {
        setUnlockedContacts((prev) => prev[contactId] ? prev : { ...prev, [contactId]: true });
      }

      if (contactId && !activeContactIdRef.current) {
        setActiveContactId(contactId);
        activeContactIdRef.current = contactId;
      }

      setTyping(true);
      setTypingContactId(contactId);
      if (hasVoice) setTypingLabel('recording a voice message...');
      const speedMul = speedRef.current === '3x' ? 0.33 : speedRef.current === '2x' ? 0.5 : 1;
      let delay = 0;
      messages.forEach((msg, i) => {
        // Voice preamble takes a touch longer than a regular text beat so
        // the "recording a voice message..." label is actually readable.
        delay += (hasVoice && i === 0
          ? (1500 + Math.random() * 700)
          : (600 + Math.random() * 500)
        ) * speedMul;
        setTimeout(() => {
          if (isStale()) return;
          const isActiveChat = (screenRef.current === 'chat' || screenRef.current === 'messages') && activeContactIdRef.current === contactId;
          vibrateMessage();
          if (isActiveChat) playGameSound('chatIncoming');
          if (!isActiveChat) {
            const notifText = hasVoice && i === 0 ? '🎙 Voice message' : msg;
            setNotifications((prev) => [...prev, { contactId, text: notifText, id: Date.now() + i }]);
          }
          setChatHistories((prev) => {
            const extra = [];
            if (i === 0) {
              const mins = timeskipAccumRef.current[contactId];
              if (mins && mins > 0 && (prev[contactId] || []).length > 0) {
                const label = formatTimeskipLabel(mins, gameTimeRef.current.hours, tRef.current);
                if (label) extra.push({ timeskipSeparator: true, label, totalMinutes: mins });
                timeskipAccumRef.current[contactId] = 0;
              }
            }
            const entry = (hasVoice && i === 0)
              ? {
                  voice: { audioUrl: voiceAudioUrl, subtitle: msg },
                  isUser: false,
                  time: formatGameTimeStr(gameTimeRef.current),
                  speakerName: node.data?.speakerName
                }
              : {
                  text: msg,
                  isUser: false,
                  time: formatGameTimeStr(gameTimeRef.current),
                  speakerName: node.data?.speakerName
                };
            return {
              ...prev,
              [contactId]: [
                ...(prev[contactId] || []),
                ...extra,
                entry
              ]
            };
          });
          // Clear the voice label after the first message lands; if there
          // are more lines, they continue with the default typing indicator.
          if (hasVoice && i === 0) setTypingLabel(null);
          if (i === messages.length - 1) {
            setTyping(false);
            setTypingContactId(null);
            if (variants.length > 0) {
              setTimeout(() => {
                if (isStale()) return;
                setCurrentChoices({ nodeId, variants, contactId, relationships: node.data?.relationships || [], tiers: node.data?.tiers || [] });
              }, 400);
            } else {
              followFlowOut(nodeId);
            }
          }
        }, delay);
      });
      if (messages.length === 0) {
        setTyping(false);
        setTypingContactId(null);
        if (variants.length > 0) {
          setCurrentChoices({ nodeId, variants, contactId, relationships: node.data?.relationships || [], tiers: node.data?.tiers || [] });
        } else {
          followFlowOut(nodeId);
        }
      }
    } else if (node.type === 'sfStart') {
      followFlowOut(nodeId);
    } else if (node.type === 'sfImage') {
      // Character binding: use node's characterId or last speaker
      const contactId = node.data?.characterId || lastSpeakerIdRef.current || activeContactId;
      const imageUrl = node.data?.imageUrl || node.data?.imagePreview512;
      const isPlayerSends = !!node.data?.playerSends;

      // Show "choosing a photo..." while image preloads
      if (contactId && imageUrl) {
        setTyping(true);
        setTypingLabel('choosing a photo...');
        setTypingContactId(contactId);
        const img = new Image();
        const deliver = () => {
          if (isStale()) return;
          setTyping(false);
          setTypingLabel(null);
          setTypingContactId(null);
          setChatHistories((prev) => ({
            ...prev,
            [contactId]: [
              ...(prev[contactId] || []),
              { image: imageUrl, isUser: isPlayerSends, time: formatGameTimeStr(gameTimeRef.current) }
            ]
          }));
          // Unlock in gallery
          setGalleryUnlocks((prev) => {
            if (prev.some((u) => u.storyflowId === currentStoryflowId && u.nodeId === nodeId)) return prev;
            return [...prev, {
              storyflowId: currentStoryflowId,
              nodeId,
              imageUrl,
              imagePreview512: node.data?.imagePreview512,
              playerSends: isPlayerSends,
              unlockedAt: Date.now()
            }];
          });
          followFlowOut(nodeId);
        };
        const sm = speedRef.current === '3x' ? 0.33 : speedRef.current === '2x' ? 0.5 : 1;
        const minDelay = (1200 + Math.random() * 800) * sm;
        const startTime = Date.now();
        const delayedDeliver = () => {
          const elapsed = Date.now() - startTime;
          const remaining = minDelay - elapsed;
          if (remaining > 0) setTimeout(deliver, remaining);
          else deliver();
        };
        img.onload = delayedDeliver;
        img.onerror = delayedDeliver;
        img.src = imageUrl;
      } else {
        followFlowOut(nodeId);
      }
    } else if (node.type === 'sfDecision') {
      // Decision pin index === option index === output pin index (switch-like).
      // Prefer the explicit traversed handle passed in from the caller; only
      // fall back to graph lookup (which picks an arbitrary matching edge) if
      // no hint is available.
      const decisionName = node.data?.decisionName || node.id;
      const opts = node.data?.options || [];
      let inIdx = null;
      if (fromHandle && fromHandle.startsWith('decision-in-')) {
        const parsed = parseInt(fromHandle.split('-').pop(), 10);
        if (!isNaN(parsed)) inIdx = parsed;
      }
      if (inIdx === null) {
        const inEdge = effectiveStoryflowData.graph.edges.find(
          (e) => e.target === nodeId && e.targetHandle && e.targetHandle.startsWith('decision-in-')
        );
        inIdx = inEdge ? parseInt(inEdge.targetHandle.split('-').pop(), 10) || 0 : 0;
      }
      const chosenOption = opts[inIdx] || opts[0] || 'True';
      // Store decision
      setDecisions((prev) => ({ ...prev, [decisionName]: chosenOption }));
      // Follow corresponding output
      const outHandle = `decision-out-${inIdx}`;
      const outEdge = effectiveStoryflowData.graph.edges.find(
        (e) => e.source === nodeId && e.sourceHandle === outHandle
      );
      if (outEdge) {
        setCurrentNodeId(outEdge.target);
        setTimeout(() => { if (!isStale()) processNode(outEdge.target, outEdge.targetHandle || null); }, 100);
      } else {
        followFlowOut(nodeId);
      }
    } else if (node.type === 'sfBranch') {
      // Check stored decision and follow matching output
      const ref = node.data?.decisionRef;
      const switchOpts = node.data?.switchOptions || [];
      setDecisions((prevDecisions) => {
        const stored = ref ? prevDecisions[ref] : null;
        let matchIdx = stored ? switchOpts.indexOf(stored) : -1;

        const tryEdge = (handle) => effectiveStoryflowData.graph.edges.find(
          (e) => e.source === nodeId && e.sourceHandle === handle
        );

        let edge = null;
        if (matchIdx >= 0) {
          edge = tryEdge(`switch-out-${matchIdx}`);
        }

        // Decision not found — try "False" output first, then any available output
        if (!edge) {
          const falseIdx = switchOpts.indexOf('False');
          if (falseIdx >= 0) edge = tryEdge(`switch-out-${falseIdx}`);
        }
        if (!edge) {
          // Try any switch-out
          for (let i = 0; i < switchOpts.length; i++) {
            edge = tryEdge(`switch-out-${i}`);
            if (edge) break;
          }
        }
        if (!edge) {
          edge = tryEdge('flow-out') || effectiveStoryflowData.graph.edges.find(
            (e) => e.source === nodeId && !e.sourceHandle
          );
        }

        if (edge) {
          setCurrentNodeId(edge.target);
          setTimeout(() => { if (!isStale()) processNode(edge.target, edge.targetHandle || null); }, 300);
        } else {
          // No valid output — show error in chat
          const contactId = lastSpeakerIdRef.current || activeContactId;
          if (contactId) {
            setChatHistories((prev) => ({
              ...prev,
              [contactId]: [
                ...(prev[contactId] || []),
                { text: `[Switch "${ref || '?'}": no decision was made and no output found]`, isUser: false, isSystem: true, time: formatGameTimeStr(gameTimeRef.current) }
              ]
            }));
          }
          console.error(`sfBranch "${ref}": decision not found, no valid output edge`);
        }

        return prevDecisions;
      });
    } else if (node.type === 'sfIntroduce') {
      const charId = node.data?.characterId || lastSpeakerIdRef.current;
      const char = (characters || []).find((c) => (c.id || c.characterId) === charId);
      if (charId) {
        setUnlockedContacts((prev) => ({ ...prev, [charId]: true }));
        const contactId = lastSpeakerIdRef.current || activeContactId || Object.keys(chatHistoriesRef.current || {})[0];
        if (contactId) {
          setChatHistories((prev) => ({
            ...prev,
            [contactId]: [
              ...(prev[contactId] || []),
              {
                introduce: true,
                introduceId: charId,
                introduceName: char?.name || node.data?.characterName || t('contact'),
                introduceAvatar: char?.avatar?.url64 || char?.avatar?.url128 || null,
                isUser: false,
                time: formatGameTimeStr(gameTimeRef.current)
              }
            ]
          }));
        }
      }
      followFlowOut(nodeId);
    } else if (node.type === 'sfStatus') {
      const charId = node.data?.characterId || lastSpeakerIdRef.current;
      const emoji = node.data?.statusEmoji || '';
      if (charId) {
        setContactStatuses((prev) => ({ ...prev, [charId]: emoji }));
      }
      followFlowOut(nodeId);
    } else if (node.type === 'sfInsta') {
      const charId = node.data?.characterId;
      const imageUrl = node.data?.imageUrl || node.data?.imagePreview512;
      if (charId && imageUrl) {
        // Find the insta account for this character
        const account = (instaAccountsProp || []).find((a) => a.characterId === charId);
        setInstaPosts((prev) => [
          ...prev,
          {
            id: `ipost-node-${nodeId}`,
            characterId: charId,
            accountId: account?.id || '',
            section: node.data?.instaSection || 'public',
            imageUrl,
            imagePreview512: node.data?.imagePreview512 || imageUrl,
            caption: node.data?.caption || '',
            likes: node.data?.likes || 0,
            createdAt: new Date().toISOString()
          }
        ]);
        // Send notification with insta account avatar
        const notifAvatar = account?.avatar?.url64 || account?.avatar?.url128 || null;
        const notifName = account ? `@${account.username}` : (characters.find((c) => c.id === charId)?.name || 'Someone');
        setNotifications((prev) => [
          ...prev,
          { contactId: charId, text: `${instaAppName || 'Instagram'}: New post`, id: `insta-${nodeId}`, avatarOverride: notifAvatar, nameOverride: notifName }
        ]);
      }
      followFlowOut(nodeId);
    } else if (node.type === 'sfTimeskip') {
      const mode = node.data?.timeskipMode || 'skip';
      const h = node.data?.hours || 0;
      const m = node.data?.minutes || 0;
      setGameTime((prev) => {
        const result = applyTimeskip(prev, mode, h, m);
        gameTimeRef.current = result;
        if (result.deltaMinutes > 0) {
          const acc = timeskipAccumRef.current;
          const allIds = Object.keys(chatHistoriesRef.current || {});
          allIds.forEach((cId) => { acc[cId] = (acc[cId] || 0) + result.deltaMinutes; });
        }
        return result;
      });
      followFlowOut(nodeId);
    } else if (node.type === 'sfRpSwitch') {
      const charId = node.data?.characterId || lastSpeakerIdRef.current;
      const threshold = node.data?.relationshipLevel || 0;
      setRelationships((prevRel) => {
        const charVal = prevRel[charId] || 0;
        const pass = charVal >= threshold;
        const handle = pass ? 'rp-out-pass' : 'rp-out-fail';
        const edge = effectiveStoryflowData.graph.edges.find(
          (e) => e.source === nodeId && e.sourceHandle === handle
        );
        if (edge) {
          setCurrentNodeId(edge.target);
          setTimeout(() => { if (!isStale()) processNode(edge.target, edge.targetHandle || null); }, 300);
        }
        return prevRel;
      });
    } else if (node.type === 'sfVideo') {
      const contactId = lastSpeakerIdRef.current || activeContactId || Object.keys(chatHistoriesRef.current || {})[0];
      setVideoNodePending({ nodeId, contactId });
    } else if (node.type === 'sfNextChapter' || node.type === 'sfNextFlow') {
      const targetName = node.data?.targetChapterName || node.data?.chapterName;
      if (targetName) {
        const targetSf = storyflowsByName[targetName];
        if (targetSf) {
          processedNodesRef.current = new Set();
          setCurrentStoryflowId(targetSf.id);
          setCurrentChapter(targetSf.name);
          setUnlockedChapters((prev) => prev.includes(targetSf.id) ? prev : [...prev, targetSf.id]);
          // Show new chapter overlay (for chapters > 0)
          if (targetSf.number > 0) {
            playGameSound('newChapter');
            setShowChapter(true);
          }
          // Find start node in target chapter
          const startN = (targetSf.graph?.nodes || []).find((n) => n.type === 'sfStart');
          if (startN) {
            setCurrentNodeId(startN.id);
            // Process start node after chapter overlay is dismissed (or immediately if chapter 0)
            if (targetSf.number === 0 || targetSf.number === undefined) {
              setTimeout(() => { if (!isStale()) processNode(startN.id); }, 300);
            }
            // Otherwise, processNode will be called from handleChapterContinue
          }
          autoSaveProgress();
        }
      } else {
        autoSaveProgress();
      }
    } else {
      followFlowOut(nodeId);
    }
  }, [effectiveStoryflowData, activeContactId, autoSaveProgress, currentStoryflowId, characters, storyflowsByName, endOfContentPending, instaAccountsProp, instaAppName, t]);

  // Rewind to a previous node — undo all effects between target and current
  const handleRewind = useCallback((targetNodeId) => {
    if (!effectiveStoryflowData?.graph?.nodes) return;
    const graph = effectiveStoryflowData.graph;

    const targetIdx = nodeHistory.indexOf(targetNodeId);
    if (targetIdx < 0) return;

    const undoNodeIds = nodeHistory.slice(targetIdx + 1);

    // Undo node effects in reverse
    for (let i = undoNodeIds.length - 1; i >= 0; i--) {
      const nid = undoNodeIds[i];
      const node = graph.nodes.find((n) => n.id === nid);
      if (!node) continue;

      if (node.type === 'sfDecision') {
        const name = node.data?.decisionName;
        if (name) setDecisions((prev) => { const next = { ...prev }; delete next[name]; return next; });
      }
      if (node.type === 'sfTimeskip' && (node.data?.timeskipMode || 'skip') === 'skip') {
        const h = node.data?.hours || 0, m = node.data?.minutes || 0;
        setGameTime((prev) => {
          let totalMin = prev.hours * 60 + prev.minutes - (h * 60 + m);
          if (totalMin < 0) totalMin += 24 * 60;
          return { ...prev, hours: Math.floor(totalMin / 60) % 24, minutes: totalMin % 60 };
        });
      }
      if (node.type === 'sfImage') {
        setGalleryUnlocks((prev) => prev.filter((g) => g.nodeId !== nid));
      }
      if (node.type === 'sfIntroduce') {
        const charId = node.data?.characterId;
        if (charId) setUnlockedContacts((prev) => { const next = { ...prev }; delete next[charId]; return next; });
      }
      if (node.type === 'sfStatus') {
        const charId = node.data?.characterId;
        if (charId) setContactStatuses((prev) => { const next = { ...prev }; delete next[charId]; return next; });
      }
    }

    // Undo relationship changes from dialogue choices
    for (let i = undoNodeIds.length - 1; i >= 0; i--) {
      const nid = undoNodeIds[i];
      const node = graph.nodes.find((n) => n.id === nid);
      if (!node || node.type !== 'sfDialogue') continue;
      const rels = node.data?.relationships || [];
      if (rels.every((r) => !r || r === 0)) continue;
      const nextInHistory = undoNodeIds[i + 1] || null;
      if (!nextInHistory) continue;
      const edge = graph.edges.find(
        (e) => e.source === nid && e.target === nextInHistory && e.sourceHandle?.startsWith('choice-')
      );
      if (edge) {
        const ci = parseInt(edge.sourceHandle.split('-')[1]) || 0;
        const delta = rels[ci] || 0;
        if (delta !== 0) {
          const contactId = node.data?.speakerId;
          if (contactId) setRelationships((prev) => ({ ...prev, [contactId]: (prev[contactId] || 0) - delta }));
        }
      }
    }

    // Trim chat histories
    const msgsToRemove = {};
    for (const nid of undoNodeIds) {
      const node = graph.nodes.find((n) => n.id === nid);
      if (!node || node.type !== 'sfDialogue') continue;
      const cid = node.data?.speakerId;
      if (!cid) continue;
      const text = node.data?.text || node.data?.text_en || '';
      const msgCount = text ? text.split('\n').filter(Boolean).length : 0;
      const choiceCount = (node.data?.variants || []).length > 0 ? 1 : 0;
      msgsToRemove[cid] = (msgsToRemove[cid] || 0) + msgCount + choiceCount;
    }
    setChatHistories((prev) => {
      const next = { ...prev };
      for (const [cid, count] of Object.entries(msgsToRemove)) {
        if (next[cid]) {
          let remaining = count;
          while (remaining > 0 && next[cid].length > 0) {
            const last = next[cid][next[cid].length - 1];
            next[cid] = next[cid].slice(0, -1);
            if (!last.timeskipSeparator) remaining--;
          }
        }
      }
      return next;
    });

    setNodeHistory(nodeHistory.slice(0, targetIdx + 1));
    processedNodesRef.current = new Set(nodeHistory.slice(0, targetIdx + 1));
    processGenRef.current++; // cancel all pending timeouts from old generation
    setCurrentNodeId(targetNodeId);
    setCurrentChoices(null);
    setShowStoryGraph(false);

    // Switch to the chat of the target node's speaker
    const targetNode = graph.nodes.find((n) => n.id === targetNodeId);
    const targetSpeaker = targetNode?.data?.speakerId;
    if (targetSpeaker) {
      setActiveContactId(targetSpeaker);
      navigateTo('chat', 1);
    }

    // Target node messages are already in chat — restore choices or continue flow
    // without re-adding messages (rewind only targets sfDialogue nodes).
    setTimeout(() => {
      if (!targetNode || targetNode.type !== 'sfDialogue') return;
      const curLang = resolvedLangRef.current;
      const variants = getLocalizedVariants(targetNode, curLang).filter(Boolean);
      const contactId = targetSpeaker || lastSpeakerIdRef.current || activeContactIdRef.current;
      if (variants.length > 0) {
        setCurrentChoices({ nodeId: targetNodeId, variants, contactId, relationships: targetNode.data?.relationships || [], tiers: targetNode.data?.tiers || [] });
      } else {
        // No choices — advance to the next node via flow-out edge
        const edge = effectiveStoryflowData.graph.edges.find(
          (e) => e.source === targetNodeId && (e.sourceHandle === 'flow-out' || !e.sourceHandle)
        );
        if (edge) {
          setCurrentNodeId(edge.target);
          setTimeout(() => processNode(edge.target, edge.targetHandle || null), 300);
        }
      }
    }, 300);
    autoSaveProgress();
  }, [effectiveStoryflowData, nodeHistory, processNode, autoSaveProgress, navigateTo]);

  // Start storyflow processing when ready
  useEffect(() => {
    if (storyflowStarted.current) return;
    // Play-from-here mode
    if (entryNode && effectiveStoryflowData) {
      storyflowStarted.current = true;
      // Unlock all chapters up to and including the current one
      const currentNum = effectiveStoryflowData.number ?? 0;
      const toUnlock = allStoryflows.filter((s) => (s.number ?? 0) <= currentNum).map((s) => s.id);
      if (toUnlock.length > 0) setUnlockedChapters(toUnlock);
      if (effectiveStoryflowData.name) setCurrentChapter(effectiveStoryflowData.name);
      processNode(entryNode);
      return;
    }
    // Normal start: after backstory is applied, process from current node
    if (!isTestMode && backstoryApplied && currentNodeId && effectiveStoryflowData && screen !== 'boot') {
      storyflowStarted.current = true;
      processNode(currentNodeId);
    }
  }, [entryNode, effectiveStoryflowData, processNode, isTestMode, backstoryApplied, currentNodeId, screen, allStoryflows]);

  // Test mode: trigger video sequence after Sarah's choice (once)
  useEffect(() => {
    if (!isTestMode || !testChoicesShown._test_sarah || videoTestTriggered.current) return;
    videoTestTriggered.current = true;
    const timer = setTimeout(() => {
      processNode('tv1');
    }, 1500);
    return () => clearTimeout(timer);
  }, [isTestMode, testChoicesShown._test_sarah, processNode]);

  const handleChoose = useCallback((choiceIndex) => {
    vibrateTap();
    playGameSound('chatOutgoing');
    // Test mode choices
    if (isTestMode && activeContactId && !currentChoices && TEST_CHOICES[activeContactId]) {
      const choiceText = TEST_CHOICES[activeContactId][choiceIndex] || '';
      setChatHistories((prev) => ({
        ...prev,
        [activeContactId]: [
          ...(prev[activeContactId] || []),
          { text: choiceText, isUser: true, time: formatGameTimeStr(gameTimeRef.current) }
        ]
      }));
      setTestChoicesShown((prev) => ({ ...prev, [activeContactId]: true }));
      return;
    }

    if (!currentChoices) return;
    const { nodeId, variants, contactId, tiers, isDecision, decisionName } = currentChoices;

    // Premium tier check
    const requiredTier = tiers?.[choiceIndex];
    if (requiredTier && !meetsTier(playerTier, requiredTier, patreonTiers || [])) {
      // Show patreon screen instead
      navigateTo('patreon', 1);
      return;
    }

    const choiceText = variants[choiceIndex] || '';

    // Add choice as user message
    if (contactId) {
      setChatHistories((prev) => ({
        ...prev,
        [contactId]: [
          ...(prev[contactId] || []),
          { text: choiceText, isUser: true, time: formatGameTimeStr(gameTimeRef.current) }
        ]
      }));
    }

    // Store decision if this is a decision node
    if (isDecision && decisionName) {
      setDecisions((prev) => ({ ...prev, [decisionName]: choiceText }));
    }

    // Apply relationship changes
    const relChanges = currentChoices.relationships;
    if (relChanges?.[choiceIndex] && contactId) {
      const delta = relChanges[choiceIndex];
      if (typeof delta === 'number' && delta !== 0) {
        setRelationships((prev) => ({ ...prev, [contactId]: (prev[contactId] || 0) + delta }));
      }
    }

    setCurrentChoices(null);

    if (effectiveStoryflowData?.graph?.edges) {
      const handleId = isDecision ? `decision-out-${choiceIndex}` : `choice-${choiceIndex}`;
      const edge = effectiveStoryflowData.graph.edges.find(
        (e) => e.source === nodeId && e.sourceHandle === handleId
      );
      if (edge) {
        setCurrentNodeId(edge.target);
        setTimeout(() => processNode(edge.target, edge.targetHandle || null), 300);
      } else {
        // Fallback: try flow-out (decisions can route through it; dialogue
        // nodes with variants never have flow-out so this simply misses).
        const fallback = effectiveStoryflowData.graph.edges.find(
          (e) => e.source === nodeId && (e.sourceHandle === 'flow-out' || !e.sourceHandle)
        );
        if (fallback) {
          setCurrentNodeId(fallback.target);
          setTimeout(() => processNode(fallback.target, fallback.targetHandle || null), 300);
        } else {
          // Dead-end choice — trigger end-of-content flow (results → coming soon).
          if (!endOfContentPending) setEndOfContentPending(true);
        }
      }
    } else {
      // No edges in graph at all — same terminal state.
      if (!endOfContentPending) setEndOfContentPending(true);
    }
    // Auto-save after each choice
    setTimeout(autoSaveProgress, 500);
  }, [currentChoices, effectiveStoryflowData, processNode, isTestMode, activeContactId, autoSaveProgress, playerTier, patreonTiers, navigateTo, endOfContentPending]);

  const handleOpenCamera = useCallback(() => {
    vibrateTap();
    if (videoNodePending) {
      setVideoOverlayNodeId(videoNodePending.nodeId);
      setVideoNodePending(null);
    }
  }, [videoNodePending]);

  const handleVideoExit = useCallback((nextNodeId) => {
    setVideoOverlayNodeId(null);
    if (nextNodeId) {
      setCurrentNodeId(nextNodeId);
      setTimeout(() => processNode(nextNodeId), 400);
    }
  }, [processNode]);

  const handleContactSelect = useCallback((contactId) => {
    vibrateTap();
    setActiveContactId(contactId);
    navigateTo('chat', 1);
  }, [navigateTo]);

  const handleIntroduceClick = useCallback((contactId) => {
    vibrateTap();
    setUnlockedContacts((prev) => ({ ...prev, [contactId]: true }));
    setActiveContactId(contactId);
    navigateTo('chat', 1);
  }, [navigateTo]);

  // ── Reset photo viewer state on screen change ──
  useEffect(() => {
    setIsPhotoViewerOpen(false);
  }, [screen]);

  // ── Test message delivery (every 3s) ──
  useEffect(() => {
    if (!isTestMode || screen === 'boot' || isPhotoViewerOpen || testMessagesDone || showChapter || showResults || showComingSoon || showStoryGraphGate || showStoryGraph || videoOverlayNodeId) return;
    const timer = setTimeout(() => {
      const msg = TEST_NOTIFICATION_MESSAGES[testMsgIndex];

      // Timeskip — advance game time and accumulate per-contact
      if (msg.type === 'timeskip') {
        setGameTime((prev) => {
          const result = applyTimeskip(prev, msg.timeskipMode || 'skip', msg.hours || 0, msg.minutes || 0);
          gameTimeRef.current = result;
          if (result.deltaMinutes > 0) {
            const acc = timeskipAccumRef.current;
            const allIds = new Set([
              ...Object.keys(chatHistoriesRef.current || {}),
              ...(TEST_CONTACTS || []).map((c) => c.id),
            ]);
            allIds.forEach((cId) => { acc[cId] = (acc[cId] || 0) + result.deltaMinutes; });
          }
          return result;
        });
        setTestMsgIndex((i) => i + 1);
        return;
      }

      // Status change — update contact status, no chat message
      if (msg.type === 'status') {
        setContactStatuses((prev) => ({ ...prev, [msg.contactId]: msg.emoji }));
        setTestMsgIndex((i) => i + 1);
        return;
      }

      // Introduce — unlock a contact and add contact card to chat
      if (msg.type === 'introduce') {
        setUnlockedContacts((prev) => ({ ...prev, [msg.introduceId]: true }));
        setChatHistories((prev) => ({
          ...prev,
          [msg.contactId]: [
            ...(prev[msg.contactId] || []),
            {
              introduce: true,
              introduceId: msg.introduceId,
              introduceName: msg.introduceName,
              introduceAvatar: null,
              isUser: false,
              time: formatGameTimeStr(gameTimeRef.current)
            }
          ]
        }));
        if (!(screen === 'chat' && activeContactId === msg.contactId)) {
          setNotifications((prev) => [...prev, { ...msg, text: `Shared contact: ${msg.introduceName}`, id: Date.now() }]);
        }
        setTestMsgIndex((i) => i + 1);
        return;
      }

      // Regular message — show typing indicator first if user is viewing this chat
      const deliverMessage = () => {
        vibrateMessage();
        if (screen === 'chat' && activeContactId === msg.contactId) playGameSound('chatIncoming');
        setChatHistories((prev) => {
          const extra = [];
          const mins = timeskipAccumRef.current[msg.contactId];
          if (mins && mins > 0 && (prev[msg.contactId] || []).length > 0) {
            const label = formatTimeskipLabel(mins, gameTimeRef.current.hours, tRef.current);
            if (label) extra.push({ timeskipSeparator: true, label, totalMinutes: mins });
            timeskipAccumRef.current[msg.contactId] = 0;
          }
          return {
            ...prev,
            [msg.contactId]: [
              ...(prev[msg.contactId] || []),
              ...extra,
              { text: msg.text, isUser: false, time: formatGameTimeStr(gameTimeRef.current) }
            ]
          };
        });
        setTyping(false);
        setTypingContactId(null);
        if (!(screen === 'chat' && activeContactId === msg.contactId)) {
          setNotifications((prev) => [...prev, { ...msg, id: Date.now() }]);
        }
        setTestMsgIndex((i) => i + 1);
      };

      // Show typing indicator, then deliver after a short delay
      setTyping(true);
      setTypingContactId(msg.contactId);
      const sm = speedRef.current === '3x' ? 0.33 : speedRef.current === '2x' ? 0.5 : 1;
      testTypingTimerRef.current = setTimeout(deliverMessage, (800 + Math.random() * 600) * sm);
    }, 3000);
    return () => {
      clearTimeout(timer);
      if (testTypingTimerRef.current) {
        clearTimeout(testTypingTimerRef.current);
        testTypingTimerRef.current = null;
        setTyping(false);
        setTypingContactId(null);
      }
    };
  }, [testMsgIndex, isTestMode, screen, isPhotoViewerOpen, testMessagesDone, showChapter, showResults, showComingSoon, showStoryGraphGate, showStoryGraph, activeContactId, videoOverlayNodeId]);

  // ── Notification queue processor ──
  const notifSoundPlayed = useRef(false);
  useEffect(() => {
    if (activeNotification || notifications.length === 0 || isPhotoViewerOpen || showStoryGraphGate || showStoryGraph || videoOverlayNodeId) return;
    const [next, ...rest] = notifications;
    vibrateMessage();
    // Play sound only for the first notification in a burst, at 2/3 master volume
    if (!notifSoundPlayed.current) {
      playGameSound('notification', { volumeScale: 0.67 });
      notifSoundPlayed.current = true;
    }
    setActiveNotification(next);
    setNotifications(rest);
    if (rest.length === 0) notifSoundPlayed.current = false;
  }, [notifications, activeNotification, isPhotoViewerOpen, showStoryGraphGate, showStoryGraph, videoOverlayNodeId]);

  // ── Auto-dismiss active notification ──
  useEffect(() => {
    if (!activeNotification || isPhotoViewerOpen || showStoryGraphGate || showStoryGraph || videoOverlayNodeId) return;
    clearTimeout(notifTimerRef.current);
    notifTimerRef.current = setTimeout(() => setActiveNotification(null), 2500);
    return () => clearTimeout(notifTimerRef.current);
  }, [activeNotification, isPhotoViewerOpen, showStoryGraphGate, showStoryGraph, videoOverlayNodeId]);

  // ── When a fullscreen overlay pauses notifications, drop the active toast
  // immediately instead of leaving it frozen on screen until the overlay closes.
  useEffect(() => {
    const paused = isPhotoViewerOpen || showStoryGraphGate || showStoryGraph || videoOverlayNodeId;
    if (!paused || !activeNotification) return;
    setActiveNotification(null);
    if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    notifSoundPlayed.current = false;
  }, [isPhotoViewerOpen, showStoryGraphGate, showStoryGraph, videoOverlayNodeId, activeNotification]);

  // ── When the user opens a chat, cancel any queued/active toasts for that
  // same contact — the messages they reference are already visible in the chat
  // (or about to land via processNode's own protection).
  useEffect(() => {
    if (screen !== 'chat' || !activeContactId) return;
    setNotifications((prev) => prev.filter((n) => n.contactId !== activeContactId));
    setActiveNotification((cur) => (cur && cur.contactId === activeContactId ? null : cur));
  }, [screen, activeContactId]);

  // ── Trigger chapter overlay after all test messages ──
  useEffect(() => {
    if (!isTestMode || !testMessagesDone || showChapter || chapterDismissed || isPhotoViewerOpen) return;
    const timer = setTimeout(() => { playGameSound('newChapter'); setShowChapter(true); }, 2000);
    return () => clearTimeout(timer);
  }, [isTestMode, testMessagesDone, showChapter, chapterDismissed, isPhotoViewerOpen]);

  // ── Trigger results overlay 3s after end-of-content is reached ──
  // Guarded by comingSoonDismissed so that if the player bounces into
  // Saves/Patreon from coming soon and comes back, the whole flow doesn't
  // re-fire — the end-of-content screens show at most once per session.
  // IMPORTANT: watch endOfContentPending, NOT chapterDismissed — the latter
  // is set whenever the chapter-start overlay is dismissed on a new chapter
  // and would otherwise fire results at the start of every chapter.
  useEffect(() => {
    if (!endOfContentPending || showResults || showComingSoon || comingSoonDismissed) return;
    const timer = setTimeout(() => { playGameSound('results'); setShowResults(true); }, 3000);
    return () => clearTimeout(timer);
  }, [endOfContentPending, showResults, showComingSoon, comingSoonDismissed]);

  const handleNotificationClick = useCallback((notif) => {
    vibrateTap();
    setActiveNotification(null);
    setActiveContactId(notif.contactId);
    navigateTo('chat', 1);
  }, [navigateTo]);

  const handleChapterContinue = useCallback(() => {
    vibrateTap();
    setShowChapter(false);
    setChapterDismissed(true);
    // Continue processing from current node (sfStart of new chapter)
    if (currentNodeId) {
      setTimeout(() => processNode(currentNodeId), 400);
    }
  }, [currentNodeId, processNode]);

  const handleResultsNext = useCallback(() => {
    vibrateTap();
    setShowResults(false);
    setShowComingSoon(true);
  }, []);

  const handleComingSoonAction = useCallback((action) => {
    vibrateTap();
    setShowComingSoon(false);
    setComingSoonDismissed(true);
    if (action === 'patreon') navigateTo('patreon', 1);
    else if (action === 'saves') navigateTo('saves', 1);
    else if (action === 'newgame') {
      // Match SavesScreen's "Reset Progress" flow: delete the stored
      // current_progress save first so the reset sticks. Without this, the
      // next autosave/boot would re-hydrate the old progress and make
      // "New Game" look like a noop.
      if (teamId) {
        if (isStandalone()) {
          try { deleteSavesByType(teamId, 'current_progress'); } catch (_) { /* ignore */ }
        } else {
          const token = sessionStorage.getItem('flowcore_session_token') || '';
          fetch(
            `${API_BASE_URL}/api/game-saves?teamId=${encodeURIComponent(teamId)}&saveType=current_progress`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => {});
        }
      }
      handleResetProgress();
    }
  }, [navigateTo, handleResetProgress, teamId]);

  const notifContact = useMemo(
    () => activeNotification ? (contacts.find((c) => c.id === activeNotification.contactId) || allContacts.find((c) => c.id === activeNotification.contactId)) : null,
    [activeNotification, contacts, allContacts]
  );

  // Chat message limit — show last 50 messages per chat
  const activeMessages = useMemo(() => {
    const all = activeContactId ? (chatHistories[activeContactId] || []) : [];
    // Use offset so keys are stable absolute indices even after slice
    const offset = all.length > 50 ? all.length - 50 : 0;
    const sliced = offset > 0 ? all.slice(-50) : all;
    return sliced.map((msg, i) => ({ ...msg, _key: offset + i }));
  }, [activeContactId, chatHistories]);
  const activeChoicesArr = currentChoices && currentChoices.contactId === activeContactId
    ? currentChoices.variants
    : (isTestMode && activeContactId && !testChoicesShown[activeContactId] && TEST_CHOICES[activeContactId])
      ? TEST_CHOICES[activeContactId]
      : null;

  const activeRelationships = currentChoices && currentChoices.contactId === activeContactId
    ? currentChoices.relationships
    : null;

  const renderScreen = () => {
    switch (screen) {
      case 'boot':
        return null; // boot is rendered as overlay outside AnimatePresence
      case 'home':
        return (
          <motion.div
            key="home"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <AndroidHomeScreen onNavigate={handleHomeAppClick} onExit={handleExit} hasUnread={
              !!(currentChoices) || !!(videoNodePending) || (isTestMode && contacts.some((c) => !testChoicesShown[c.id] && TEST_CHOICES[c.id]))
            } musicPlaying={sexifyState.playing} gameTime={gameTime} showClock={showClock} t={t} wallpaper={wallpaper} instaAppName={instaAppName} />
          </motion.div>
        );
      case 'messages':
        return (
          <motion.div
            key="messages"
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <ContactsPane
              contacts={contacts}
              chatHistories={chatHistories}
              activeContactId={activeContactId}
              onSelect={handleContactSelect}
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              choicesForContact={(cId) => {
                if (currentChoices && currentChoices.contactId === cId) return currentChoices.variants;
                if (isTestMode && !testChoicesShown[cId] && TEST_CHOICES[cId]) return TEST_CHOICES[cId];
                return null;
              }}
              contactStatuses={contactStatuses}
              emojiMap={emojiMap}
              onStoryGraph={handleStoryGraphClick}
              typingContactId={typing ? typingContactId : null}
              t={t}
            />
          </motion.div>
        );
      case 'chat':
        return (
          <motion.div
            key="chat"
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <ChatPane
              contact={activeContact}
              messages={activeMessages}
              choices={activeChoicesArr}
              relationships={activeRelationships}
              onChoose={handleChoose}
              typing={typing && activeContactId === typingContactId}
              typingLabel={typing && activeContactId === typingContactId ? typingLabel : null}
              onBack={() => { vibrateTap(); navigateTo('messages', -1); }}
              onPhotoViewerToggle={setIsPhotoViewerOpen}
              emojiMap={emojiMap}
              onIntroduceClick={handleIntroduceClick}
              statusEmoji={activeContactId ? contactStatuses[activeContactId] : null}
              onStoryGraph={handleStoryGraphClick}
              showCameraButton={!!(videoNodePending && videoNodePending.contactId === activeContactId)}
              onOpenCamera={handleOpenCamera}
              wallpaper={wallpaper}
              speed={speed}
              onCycleSpeed={cycleSpeed}
              t={t}
            />
          </motion.div>
        );
      case 'gallery':
        return (
          <motion.div
            key="gallery"
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <GalleryScreen
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              onNavigatePatreon={() => { vibrateTap(); navigateTo('patreon', 1); }}
              onPhotoViewerToggle={setIsPhotoViewerOpen}
              galleryUnlocks={galleryUnlocks}
              allStoryflows={allStoryflows}
              teamId={teamId}
              playerTier={playerTier}
              fullGalleryTier={fullGalleryTier}
              patreonTiers={patreonTiers}
              wallpaper={wallpaper}
              onSetWallpaper={setWallpaper}
              t={t}
            />
          </motion.div>
        );
      case 'insta':
        return (
          <motion.div
            key="insta"
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <InstaApp
              instaAccounts={instaAccountsProp || []}
              instaAppName={instaAppName || 'Instagram'}
              instaPrivateTier={instaPrivateTier}
              storiesMin={instaStoriesMin ?? 2}
              storiesMax={instaStoriesMax ?? 3}
              unlockedContacts={unlockedContacts}
              playerTier={playerTier}
              patreonTiers={patreonTiers}
              instaPosts={instaPosts}
              viewedStories={viewedStories}
              onViewStory={(accountId, postId) => {
                setViewedStories((prev) => ({
                  ...prev,
                  [accountId]: [...(prev[accountId] || []), postId]
                }));
              }}
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              onNavigatePatreon={() => { vibrateTap(); navigateTo('patreon', 1); }}
              t={t}
            />
          </motion.div>
        );
      case 'settings':
        return (
          <motion.div
            key="settings"
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <SettingsScreen onBack={() => { vibrateTap(); navigateTo('home', -1); }} sound={sound} onSoundChange={setSound} vibration={vibration} onVibrationChange={setVibration} showClock={showClock} onShowClockChange={setShowClock} language={language} onLanguageChange={setLanguage} speed={speed} onSpeedChange={setSpeed} t={t} />
          </motion.div>
        );
      case 'saves':
        return (
          <motion.div key="saves" custom={navDirection} variants={screenVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute inset-0" style={SCREEN_LAYER_STYLE}>
            <SavesScreen
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              teamId={teamId}
              gameState={getGameState()}
              onLoad={handleLoadSave}
              onReset={handleResetProgress}
              onRestoreProgress={() => { vibrateTap(); setShowRestoreQuiz(true); }}
              canRestoreProgress={!isTestMode && (allStoryflows || []).filter((s) => (s.number ?? 0) >= 2).length >= 3}
              playTimeSeconds={getPlayTimeSeconds()}
              gameTimeStr={formatGameTimeStr(gameTime)}
              currentChapter={currentChapter}
              currentChapterNumber={effectiveStoryflowData?.number ?? 0}
              t={t}
            />
          </motion.div>
        );
      case 'statistics':
        return (
          <motion.div key="statistics" custom={navDirection} variants={screenVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute inset-0" style={SCREEN_LAYER_STYLE}>
            <StatisticsScreen
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              t={t}
              playTimeSeconds={getPlayTimeSeconds()}
              galleryUnlocks={galleryUnlocks}
              totalPhotos={(allStoryflows || []).reduce((s, sf) => s + (sf.graph?.nodes || []).filter((n) => n.type === 'sfImage').length, 0)}
              totalVideos={(allStoryflows || []).reduce((s, sf) => s + (sf.graph?.nodes || []).filter((n) => n.type === 'sfVideo').length, 0)}
              relationships={relationships}
              characters={characters}
              unlockedContacts={unlockedContacts}
            />
          </motion.div>
        );
      case 'episodes':
        return (
          <motion.div key="episodes" custom={navDirection} variants={screenVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute inset-0" style={SCREEN_LAYER_STYLE}>
            <EpisodesScreen onBack={() => { vibrateTap(); navigateTo('home', -1); }} allStoryflows={allStoryflows} unlockedChapters={unlockedChapters} t={t} />
          </motion.div>
        );
      case 'patreon':
        return (
          <motion.div key="patreon" custom={navDirection} variants={screenVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute inset-0" style={SCREEN_LAYER_STYLE}>
            <PatreonScreen
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              patreonUrl={patreonUrl}
              authEndpoint={authEndpoint}
              patreonTiers={patreonTiers}
              playerTier={playerTier}
              onLinkedTierChange={(tier) => setPlayerTier(tier || '')}
              t={t}
            />
          </motion.div>
        );
      case 'sexify':
        return (
          <motion.div key="sexify" custom={navDirection} variants={screenVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute inset-0" style={SCREEN_LAYER_STYLE}>
            <SexifyScreen onBack={() => { vibrateTap(); navigateTo('home', -1); }} t={t} coversUnlocked={coversUnlocked} />
          </motion.div>
        );
      case 'placeholder':
        return (
          <motion.div
            key={`placeholder-${placeholderAppId}`}
            custom={navDirection}
            variants={screenVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute inset-0"
            style={SCREEN_LAYER_STYLE}
          >
            <PlaceholderScreen
              appId={placeholderAppId}
              onBack={() => { vibrateTap(); navigateTo('home', -1); }}
              t={t}
            />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full w-full overflow-hidden relative">
      <PhoneStatusBar gameTime={gameTime} />
      <AnimatePresence initial={false} custom={navDirection}>
        {renderScreen()}
      </AnimatePresence>
      {/* Boot overlay — outside AnimatePresence so its animation always runs */}
      {screen === 'boot' && (
        <PhoneBootScreen onComplete={() => navigateTo('home', 1)} />
      )}
      {/* Video overlay (fullscreen camera mode) */}
      <AnimatePresence>
        {videoOverlayNodeId && (
          <VideoOverlay
            key={videoOverlayNodeId}
            startNodeId={videoOverlayNodeId}
            storyflowData={effectiveStoryflowData}
            onExit={handleVideoExit}
            lang={resolvedLang}
          />
        )}
      </AnimatePresence>
      {/* Notification toast */}
      <AnimatePresence>
        {activeNotification && notifContact && (
          <NotificationToast
            key={activeNotification.id}
            notification={activeNotification}
            contact={notifContact}
            onClick={() => handleNotificationClick(activeNotification)}
            statusEmoji={contactStatuses[activeNotification.contactId]}
            emojiMap={emojiMap}
          />
        )}
      </AnimatePresence>
      {/* Chapter overlay */}
      <AnimatePresence>
        {showChapter && <NewChapterOverlay
          onContinue={handleChapterContinue}
          t={t}
          chapterNumber={effectiveStoryflowData?.number}
          chapterName={effectiveStoryflowData?.name}
          chapterCover={effectiveStoryflowData?.cover?.url128 || effectiveStoryflowData?.cover?.url64}
        />}
      </AnimatePresence>
      {/* Results overlay */}
      <AnimatePresence>
        {showResults && (() => {
          const unlockedPhotos = (galleryUnlocks || []).filter((g) => !g.isVideo).length;
          const totalPhotos = (allStoryflows || []).reduce(
            (sum, sf) => sum + (sf.graph?.nodes || []).filter((n) => n.type === 'sfImage').length,
            0
          );
          const resultStats = [
            { label: 'Photos Unlocked', value: `${unlockedPhotos} / ${totalPhotos || '?'}` },
            { label: 'Time Played', value: formatPlayTime(getPlayTimeSeconds()) }
          ];
          const resultRelationships = Object.keys(unlockedContacts || {}).map((charId) => {
            const contact = allContacts.find((c) => c.id === charId);
            return {
              name: contact?.name || charId,
              value: (relationships || {})[charId] || 0
            };
          });
          return (
            <ResultsOverlay
              onNext={handleResultsNext}
              t={t}
              stats={resultStats}
              relationships={resultRelationships}
            />
          );
        })()}
      </AnimatePresence>
      {/* Coming Soon overlay */}
      <AnimatePresence>
        {showComingSoon && (
          <ComingSoonOverlay
            onPatreon={() => handleComingSoonAction('patreon')}
            onSaves={() => handleComingSoonAction('saves')}
            onNewGame={() => handleComingSoonAction('newgame')}
            t={t}
          />
        )}
      </AnimatePresence>
      {/* Exit confirmation modal */}
      <AnimatePresence>
        {showExitModal && (
          <ExitConfirmModal
            onConfirm={confirmExit}
            onCancel={() => setShowExitModal(false)}
            emojiMap={emojiMap}
            t={t}
          />
        )}
      </AnimatePresence>
      {/* Story graph gate modal */}
      <AnimatePresence>
        {showStoryGraphGate && (
          <StoryGraphGateModal
            onContinue={handleStoryGraphGateContinue}
            onCancel={() => setShowStoryGraphGate(false)}
            t={t}
            tierName={fullStoryLogTier}
          />
        )}
      </AnimatePresence>
      {/* Story graph viewer */}
      <AnimatePresence>
        {showStoryGraph && (
          <StoryGraphViewer
            open={showStoryGraph}
            onClose={handleStoryGraphClose}
            title={effectiveStoryflowData ? `Ch.${effectiveStoryflowData.number || '?'} — ${effectiveStoryflowData.name || ''}` : ''}
            graph={effectiveStoryflowData?.graph}
            graphMeta={effectiveStoryflowData?.graphMeta}
            nodeHistory={nodeHistory}
            characters={characters}
            lang={resolvedLang}
            onRewind={handleRewind}
            hideUnvisited={!!fullStoryLogTier && !meetsTier(playerTier, fullStoryLogTier, patreonTiers || [])}
          />
        )}
      </AnimatePresence>
      {/* Restore-progress quiz overlay (launched from Saves screen) */}
      {showRestoreQuiz && (
        <div className="absolute inset-0 z-50">
          {restoreBuilding ? (
            <div className="h-full w-full flex items-center justify-center" style={{ background: 'linear-gradient(160deg, #ede9fe 0%, #ddd6fe 60%, #c4b5fd 100%)' }}>
              <div className="flex flex-col items-center gap-3">
                <div className="h-6 w-6 rounded-full border-2 border-violet-200 border-t-violet-500 animate-spin" />
                <div className="text-sm text-slate-600">{t('quizRestoring')}</div>
              </div>
            </div>
          ) : (
            <OnboardingQuiz
              allStoryflows={allStoryflows}
              language={language}
              onComplete={handleRestoreComplete}
              onCancel={() => setShowRestoreQuiz(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}
